<template>
  <div class="pdv-desktop-page">

    <!-- ── Aviso de terminal desvinculado ─────────────────────────────── -->
    <div v-if="terminalBloqueado" class="pdv-terminal-bloqueado-banner">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ motivoBloqueio || "Este terminal foi desvinculado no painel web. Configure novamente o terminal para continuar." }}</span>
      <span v-if="!configuracaoNfceAusente" class="pdv-terminal-bloqueado-redirect">Redirecionando para configuração...</span>
    </div>

    <!-- ── Header operacional do PDV Desktop ───────────────────────────── -->
    <PdvHeader
      :caixa-aberto="caixaAberto"
      :resumo="resumoCaixa"
      :online="online"
      :operador-nome="operadorNome"
      :terminal-nome="terminalNome"
      :serie="terminalSerie"
      :terminal-uuid="terminalUuid"
      :pendentes-sync="pendentesSync"
      :sincronizando="sincronizandoCaixa"
      @ir-dashboard="sairDoPdv"
      @abrir-caixa="modalAbrirCaixa = { visivel: true, bloqueante: !caixaAberto }"
      @fechar-caixa="abrirFecharCaixa"
      @sangria="abrirSangria"
      @suprimento="abrirSuprimento"
      @resumo-completo="abrirResumoCaixa"
      @sincronizar="sincronizarCaixaManual"
      @recarregar-combinacoes="recarregarCombinacoes"
      @abrir-configuracoes="abrirConfiguracoesTopo"
      @sair="sairDaSessaoPdv"
    />

  <div class="pdv-desktop-screen">

    <!-- ── Coluna Esquerda: Produtos / Carrinho ──────────────────────── -->
    <section class="pdv-left-panel">

      <!-- Entrada de produto -->
      <div class="product-entry-area">
        <div class="product-entry-row">
          <div class="nova-venda__search-wrap product-entry-search">
            <i
              :class="produtoSelecionado ? 'fas fa-check-circle' : 'fas fa-search'"
              class="nova-venda__search-icon"
              :style="produtoSelecionado ? 'color:#38a169' : ''"
            ></i>
            <input
              ref="searchInput"
              v-model="termoBusca"
              type="text"
              class="nova-venda__search-input"
              :class="{ 'nova-venda__search-input--selecionado': produtoSelecionado }"
              placeholder="Produto / Código / Código de barras..."
              autocomplete="off"
              @input="onBuscarProduto"
              @keydown.enter.prevent="handleEnterProduto"
              @keydown.up.prevent="navegarResultados(-1)"
              @keydown.down.prevent="navegarResultados(1)"
              @keydown.esc="limparBuscaProduto"
            />
          </div>
          <input
            ref="qtyInput"
            v-model.number="quantidadeItem"
            type="number"
            min="0.001"
            step="0.001"
            class="pdv-entry-input pdv-entry-input--qty"
            placeholder="Qtd"
            :disabled="!produtoSelecionado || aguardandoPesoBalanca"
            @keydown.enter.prevent="$refs.precoInput && $refs.precoInput.focus()"
            @input="_onQtyInput"
          />
          <input
            ref="precoInput"
            v-model.number="valorUnitarioItem"
            type="number"
            min="0"
            step="0.01"
            class="pdv-entry-input pdv-entry-input--preco"
            placeholder="R$ Unit."
            :disabled="!produtoSelecionado"
            @keydown.enter.prevent="inserirNoCarrinho"
            @input="_onValorUnitarioManual"
          />
          <div class="pdv-entry-total" :class="{ 'pdv-entry-total--vazio': !produtoSelecionado }">
            {{ formatBRL(valorTotalItem) }}
          </div>
          <button class="pdv-insert-btn" :disabled="aguardandoPesoBalanca" @click="inserirNoCarrinho">
            <i class="fas fa-plus"></i> Inserir
          </button>
        </div>
        <div
          v-if="aguardandoPesoBalanca"
          class="pdv-balanca-status"
          :class="{ 'pdv-balanca-status--erro': balancaErro }"
          aria-live="polite"
        >
          <i :class="balancaConectada ? 'fas fa-weight' : 'fas fa-exclamation-triangle'"></i>
          <span>
            <strong>{{ balancaMensagem }}</strong>
            <small v-if="balancaPesoTempoReal > 0">
              {{ Number(balancaPesoTempoReal).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) }} kg
            </small>
          </span>
          <button type="button" :disabled="balancaCapturando" @click="capturarPesoBalancaManual">
            {{ balancaCapturando ? 'Lendo...' : 'Capturar agora' }}
          </button>
        </div>
        <p v-if="erroItem" class="pdv-entry-error">
          <i class="fas fa-exclamation-circle"></i> {{ erroItem }}
        </p>
        <p v-if="feedbackItem" class="pdv-entry-feedback" aria-live="polite">
          <i class="fas fa-check-circle"></i> {{ feedbackItem }}
        </p>
      </div>

      <!-- Dropdown de resultados -->
      <div v-if="produtosBusca.length" ref="resultsList" class="nova-venda__results pdv-results-dropdown">
        <div
          v-for="(p, idx) in produtosBusca"
          :key="p.id_produto || p.id"
          :class="['nova-venda__result-item', { 'nova-venda__result-item--active': idx === selectedProductIndex }]"
          @mouseenter="selectedProductIndex = idx"
          @click="selecionarProduto(p)"
        >
          <div class="nova-venda__result-row">
            <img
              v-if="_imagemValida(p.imagem)"
              :src="p.imagem"
              class="nova-venda__result-thumb"
              alt=""
              @error="$event.target.style.display='none'"
            />
            <div class="nova-venda__result-body">
              <div class="nova-venda__result-nome">
                <span v-if="p.codigo" class="nova-venda__result-cod-inline">{{ p.codigo }}</span>
                {{ p.descricao }}
              </div>
              <div class="nova-venda__result-info">
                <span class="nova-venda__result-preco">
                  <template v-if="listaPrecoMap[p.id_produto || p.id]">
                    <span class="nova-venda__result-preco--lista-wrap">
                      <span class="nova-venda__result-preco--lista-badge">LP</span>
                      <span class="nova-venda__result-preco--lista">{{ formatBRL(listaPrecoMap[p.id_produto || p.id]) }}</span>
                    </span>
                    <span class="nova-venda__result-preco--original">{{ formatBRL(p.valor_venda || p.preco || p.valor || 0) }}</span>
                  </template>
                  <template v-else>
                    {{ formatBRL(p.valor_venda || p.preco || p.valor || 0) }}
                  </template>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p
        v-else-if="termoBusca.length >= 2 && !buscando"
        class="nova-venda__no-results pdv-no-results"
      >
        Nenhum produto encontrado.
      </p>

      <!-- Tabela do carrinho -->
      <div class="cart-table-wrapper">
        <div class="cart-table-header">
          <span class="cart-col-produto">Produto</span>
          <span class="cart-col-qty">Qtd</span>
          <span class="cart-col-unit">Unitário</span>
          <span class="cart-col-total">Total</span>
          <span class="cart-col-action"></span>
        </div>

        <!-- Carrinho vazio -->
        <div v-if="!carrinho.length" class="nova-venda__cart-empty">
          <i class="fas fa-shopping-cart"></i>
          <p>Nenhum produto adicionado</p>
          <small>Use a busca acima para adicionar produtos</small>
        </div>

        <!-- Itens do carrinho -->
        <div v-else class="nova-venda__cart-items">
          <div
            v-for="(item, idx) in carrinho"
            :key="idx"
            :class="['nova-venda__cart-item', { 'nova-venda__cart-item--selecionado': itemSelecionadoIdx === idx }]"
            @click="selecionarItemCarrinho(idx)"
          >
            <img
              v-if="_imagemValida(item.imagem)"
              :src="item.imagem"
              class="nova-venda__cart-item-thumb"
              alt=""
              @error="$event.target.style.display='none'"
            />
            <div class="nova-venda__cart-item-info">
              <span class="nova-venda__cart-item-nome">{{ _nomeItemCarrinho(item) }}</span>
              <span v-if="item.cod_barras" class="nova-venda__cart-item-codbarras">{{ item.cod_barras }}</span>
              <div v-if="_itemTemDesconto(item) || _itemTemAcrescimo(item) || item.tipo_preco_aplicado === 'atacado'" class="nova-venda__cart-item-badges">
                <span
                  v-if="item.tipo_preco_aplicado === 'atacado'"
                  class="nova-venda__cart-badge nova-venda__cart-badge--atacado"
                  title="Preço de atacado aplicado"
                >
                  Atacado
                </span>
                <span
                  v-if="_itemTemDesconto(item)"
                  class="nova-venda__cart-badge nova-venda__cart-badge--desc"
                  :title="`Desconto: ${formatBRL(item.desconto_valor || item.desconto || 0)}`"
                >
                  <i class="fas fa-minus-circle"></i>
                  Desc {{ _badgeAjuste(item, 'desconto') }}
                </span>
                <span
                  v-if="_itemTemAcrescimo(item)"
                  class="nova-venda__cart-badge nova-venda__cart-badge--acres"
                  :title="`Acréscimo: ${formatBRL(item.acrescimo_valor || item.acrescimo || 0)}`"
                >
                  <i class="fas fa-plus-circle"></i>
                  Acr {{ _badgeAjuste(item, 'acrescimo') }}
                </span>
              </div>
            </div>

            <div class="nova-venda__cart-item-controls" @click.stop>
              <button class="nova-venda__qty-btn" @click="decrementarQtd(idx)">−</button>
              <input
                v-model.number="item.quantidade"
                type="number"
                min="0.001"
                step="0.001"
                class="nova-venda__qty-input"
                @focus="item._qtd_antes_recalculo = Number(item.quantidade)"
                @change="recalcularItem(idx)"
              />
              <button class="nova-venda__qty-btn" @click="incrementarQtd(idx)">+</button>
            </div>

            <span class="nova-venda__cart-item-unit-col">{{ formatBRL(item.valor_unitario) }}</span>

            <span class="nova-venda__cart-item-total">
              <span v-if="_itemTemDesconto(item) || _itemTemAcrescimo(item)" class="nova-venda__cart-item-total-bruto">
                {{ formatBRL(_totalBrutoItem(item)) }}
              </span>
              {{ formatBRL(item.total) }}
            </span>
            <button
              class="nova-venda__desc-btn"
              title="Desconto/Acréscimo do item (Ctrl+D)"
              @click.stop="abrirDescontoAcrescimoItem(idx)"
            >
              <i class="fas fa-percent"></i>
            </button>
            <button
              v-if="_itemTemErroFiscal(item)"
              class="nova-venda__corrigir-btn"
              title="Corrigir dados fiscais"
              @click.stop="abrirCorrecaoFiscalItem(idx)"
            >
              <i class="fas fa-tools"></i>
            </button>
            <button class="nova-venda__remove-btn" @click.stop="removerItem(idx)">×</button>
          </div>
        </div>
      </div>

      <!-- Rodapé do carrinho: cards resumo -->
      <div class="cart-summary-footer cart-summary-footer--stacked">
        <div class="pdv-sale-footer-summary">
          <div class="pdv-sale-footer-summary-item">
            <span class="pdv-sale-footer-label">Total dos itens</span>
            <span class="pdv-sale-footer-value">{{ formatBRL(totalItensBruto) }}</span>
          </div>
          <div class="pdv-sale-footer-summary-item">
            <span class="pdv-sale-footer-label">Total desconto</span>
            <span class="pdv-sale-footer-value discount">{{ formatBRL(totalDescontoTudo) }}</span>
          </div>
          <div class="pdv-sale-footer-summary-item">
            <span class="pdv-sale-footer-label">Total acréscimo</span>
            <span class="pdv-sale-footer-value addition">{{ formatBRL(totalAcrescimoTudo) }}</span>
          </div>
        </div>
        <div class="pdv-sale-total-final">
          <span class="pdv-sale-total-final-label">TOTAL DA VENDA</span>
          <span class="pdv-sale-total-final-value">{{ formatBRL(totalComDescontoAcrescimo) }}</span>
        </div>
      </div>
    </section>

    <!-- ── Coluna Direita: Checkout / Pagamento ──────────────────────── -->
    <section class="pdv-right-panel">

      <!-- Header -->
      <div class="checkout-header">
        <h2 class="checkout-header__title">
          <i class="fas fa-check-circle"></i> Finalizar a venda
        </h2>
      </div>

      <!-- Alertas -->
      <div v-if="mensagemSucesso" class="nova-venda__alert nova-venda__alert--success pdv-alert-inline">
        <i class="fas fa-check-circle mr-2"></i>{{ mensagemSucesso }}
      </div>
      <div v-if="mensagemContingencia" class="nova-venda__alert nova-venda__alert--warning pdv-alert-inline">
        <i class="fas fa-exclamation-triangle mr-2"></i>{{ mensagemContingencia }}
      </div>
      <div v-if="mensagemErro" class="nova-venda__alert nova-venda__alert--error pdv-alert-inline">
        <i class="fas fa-exclamation-circle mr-2"></i>{{ mensagemErro }}
        <div v-if="erroFiscalCorrigido" style="margin-top:.6rem;">
          <button class="nova-venda__link-btn nova-venda__link-btn--success" @click="finalizarVenda">
            <i class="fas fa-redo mr-1"></i>Tentar finalizar novamente
          </button>
        </div>
        <div v-if="uuidRejeicao && !erroFiscalCorrigido" style="margin-top:.5rem;">
          <button class="nova-venda__link-btn" @click="verDetalheRejeicao">
            <i class="fas fa-search mr-1"></i>Ver detalhes / corrigir em NFC-e Locais
          </button>
        </div>
      </div>

      <!-- Painel interno com scroll -->
      <div class="pdv-right-scroll">

        <!-- Cliente -->
        <div class="customer-section">
          <h3 class="pdv-section-label">
            <i class="fas fa-user"></i> Cliente
            <span v-if="!_vendaExigeCliente" class="nova-venda__optional">opcional</span>
            <span v-else class="pdv-badge-obrigatorio"><i class="fas fa-exclamation-circle"></i> obrigatório</span>
          </h3>

          <div class="nova-venda__cliente-toolbar">
            <div class="nova-venda__search-wrap nova-venda__cliente-toolbar-search">
              <i class="fas fa-user nova-venda__search-icon"></i>
              <input
                v-model="termoCliente"
                type="text"
                class="nova-venda__search-input"
                placeholder="Buscar cliente cadastrado..."
                autocomplete="off"
                :disabled="modoCliente === 'manual'"
                @input="onBuscarCliente"
                @keydown.esc="limparBuscaCliente"
              />
            </div>
            <button
              class="nova-venda__cliente-toolbar-btn"
              title="Pesquisa avançada de clientes"
              @click="abrirPesquisaClientes"
            >
              +
            </button>
            <button
              class="nova-venda__cliente-toolbar-btn nova-venda__cliente-toolbar-btn--manual"
              @click="abrirClienteManual"
            >
              Informar manual
            </button>
          </div>

          <div v-if="clientesBusca.length && modoCliente !== 'manual'" class="nova-venda__results">
            <div
              v-for="c in clientesBusca"
              :key="c.id"
              class="nova-venda__result-item"
              @click="selecionarCliente(c)"
            >
              <div class="nova-venda__result-nome">{{ nomeCliente(c) }}</div>
              <div class="nova-venda__result-info">
                <span class="nova-venda__result-cod">
                  {{ c.cnpj_cpf || c.cpf || c.cnpj || "" }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="clienteSelecionado && modoCliente !== 'manual'" class="nova-venda__selected-badge">
            <i class="fas fa-user-check mr-2"></i>
            <span>{{ nomeCliente(clienteSelecionado) }}</span>
            <button class="nova-venda__remove-badge" @click="removerCliente">×</button>
          </div>

          <div v-if="modoCliente === 'manual' && _clienteInformado" class="nova-venda__selected-badge nova-venda__selected-badge--manual">
            <i class="fas fa-user-edit mr-2"></i>
            <span>
              {{ clienteManualNome || 'Cliente manual' }}
              <template v-if="clienteManualDocumento">· {{ clienteManualDocumento }}</template>
            </span>
            <button class="nova-venda__remove-badge" @click="limparClienteManual">×</button>
          </div>

          <div v-if="clienteManualExpandido" class="nova-venda__cliente-manual-panel">
            <div class="nova-venda__cliente-manual-grid">
              <div class="nova-venda__search-wrap">
                <i class="fas fa-user-edit nova-venda__search-icon"></i>
                <input
                  v-model="clienteManualNomeDraft"
                  type="text"
                  class="nova-venda__search-input"
                  placeholder="Nome do cliente"
                  autocomplete="off"
                />
              </div>
              <div class="nova-venda__search-wrap">
                <i class="fas fa-id-card nova-venda__search-icon"></i>
                <input
                  v-model="clienteManualDocumentoDraft"
                  type="text"
                  class="nova-venda__search-input"
                  placeholder="CPF/CNPJ"
                  autocomplete="off"
                  @input="onInputDocumentoManualDraft"
                />
              </div>
            </div>
            <div class="nova-venda__cliente-manual-actions">
              <button class="nova-venda__cliente-manual-btn nova-venda__cliente-manual-btn--apply" @click="aplicarClienteManual">Aplicar</button>
              <button class="nova-venda__cliente-manual-btn" @click="limparClienteManualDraft">Limpar</button>
              <button class="nova-venda__cliente-manual-btn" @click="cancelarClienteManual">Cancelar</button>
            </div>
          </div>

          <!-- Lista de preço -->
          <div class="nova-venda__lista-preco-bar">
            <template v-if="listaPrecoSelecionada">
              <span class="nova-venda__lista-preco-chip">
                <i class="fas fa-tag"></i>
                {{ listaPrecoSelecionada.descricao }}
                <button class="nova-venda__lista-preco-chip__remove" @click="removerListaPreco" title="Remover lista">×</button>
              </span>
            </template>
            <template v-else-if="listasDisponiveis.length">
              <button
                v-if="!listaPrecoAberta"
                class="nova-venda__lista-preco-btn"
                @click="listaPrecoAberta = true"
              >
                <i class="fas fa-tag"></i> Lista de preço
              </button>
              <select
                v-else
                class="nova-venda__lista-preco-select"
                autofocus
                @change="onSelecionarListaPreco($event); listaPrecoAberta = false"
                @blur="listaPrecoAberta = false"
              >
                <option value="">Selecionar...</option>
                <option
                  v-for="lista in listasDisponiveis"
                  :key="lista.id_lista_preco"
                  :value="lista.id_lista_preco"
                >
                  {{ lista.descricao }}
                </option>
              </select>
            </template>
          </div>
        </div>

        <!-- Aviso: cliente obrigatório por forma de pagamento -->
        <div v-if="avisoClienteObrigatorio" class="pdv-aviso-cliente">
          <i class="fas fa-user-times"></i> {{ avisoClienteObrigatorio }}
        </div>

        <!-- Vendedor -->
        <div v-if="vendedores.length" class="customer-section">
          <h3 class="pdv-section-label">
            <i class="fas fa-user-tie"></i> Vendedor
            <span class="nova-venda__optional">opcional</span>
          </h3>
          <div v-if="vendedorSelecionado" class="nova-venda__selected-badge">
            <i class="fas fa-user-check mr-2"></i>
            <span>{{ vendedorSelecionado.nome }}</span>
            <button class="nova-venda__remove-badge" @click="vendedorSelecionado = null">×</button>
          </div>
          <select
            v-else
            class="nova-venda__lista-preco-select"
            @change="vendedorSelecionado = vendedores.find(v => v.id_vendedor === parseInt($event.target.value)) || null; $event.target.value = ''"
          >
            <option value="">Selecionar vendedor...</option>
            <option
              v-for="v in vendedores"
              :key="v.id_vendedor"
              :value="v.id_vendedor"
            >
              {{ v.nome }}
            </option>
          </select>
        </div>

        <!-- Observação -->
        <div class="extra-info-section">
          <div class="nova-venda__complementares">
            <button
              class="nova-venda__complementares-toggle"
              @click="complementaresAberto = !complementaresAberto"
            >
              <i :class="complementaresAberto ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              Observação
            </button>
            <div v-if="complementaresAberto" class="nova-venda__complementares-body">
              <label class="nova-venda__label">Observação</label>
              <textarea
                v-model="observacaoVenda"
                class="nova-venda__textarea"
                rows="3"
                placeholder="Observações da venda..."
              />
            </div>
          </div>
        </div>

        <!-- Valores: Desconto / Acréscimo / Total a pagar -->
        <div class="checkout-values-section">
          <h3 class="pdv-section-label"><i class="fas fa-tag"></i> Valores</h3>
          <div class="nova-venda__ajustes-grid">
            <div class="nova-venda__total-desconto-row nova-venda__total-desconto-row--compact">
              <span class="nova-venda__total-desconto-label">Desconto</span>
              <div class="nova-venda__total-inputs">
                <div class="nova-venda__total-input-wrap">
                  <input
                    v-model.number="descontoVendaReais"
                    type="number" min="0" step="0.01"
                    class="nova-venda__total-input"
                    placeholder="0,00"
                  />
                  <span class="nova-venda__total-input-suffix">R$</span>
                </div>
                <div class="nova-venda__total-input-wrap">
                  <input
                    v-model.number="descontoVendaPct"
                    type="number" min="0" max="100" step="0.1"
                    class="nova-venda__total-input"
                    placeholder="0"
                  />
                  <span class="nova-venda__total-input-suffix">%</span>
                </div>
              </div>
            </div>
            <div class="nova-venda__total-desconto-row nova-venda__total-desconto-row--compact">
              <span class="nova-venda__total-desconto-label">Acréscimo</span>
              <div class="nova-venda__total-inputs">
                <div class="nova-venda__total-input-wrap">
                  <input
                    v-model.number="acrescimoVendaReais"
                    type="number" min="0" step="0.01"
                    class="nova-venda__total-input"
                    placeholder="0,00"
                  />
                  <span class="nova-venda__total-input-suffix">R$</span>
                </div>
                <div class="nova-venda__total-input-wrap">
                  <input
                    v-model.number="acrescimoVendaPct"
                    type="number" min="0" step="0.1"
                    class="nova-venda__total-input"
                    placeholder="0"
                  />
                  <span class="nova-venda__total-input-suffix">%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="checkout-total-a-pagar pdv-total-payable-card">
            <div class="checkout-total-a-pagar__label">
              <span>Valor a pagar</span>
              <span class="checkout-total-a-pagar__valor pdv-total-payable">
                {{ formatBRL(totalComDescontoAcrescimo) }}
              </span>
              <small
                v-if="formaPagamentoSelecionada"
                class="checkout-total-a-pagar__forma"
              >
                <i :class="_iconePagamento(formaPagamentoSelecionada.descricao)"></i>
                {{ formaPagamentoSelecionada.descricao }}
                <span v-if="valorRestante > 0" class="checkout-total-a-pagar__restante">
                  · Restante {{ formatBRL(valorRestante) }}
                </span>
              </small>
            </div>

            <template v-if="formaPagamentoSelecionada">
              <div class="checkout-total-a-pagar__edit">
                <div class="pdv-valor-recebido-input-wrap">
                  <span class="pdv-valor-recebido-prefix">R$</span>
                  <input
                    ref="valorPagamentoInput"
                    v-model.number="valorPagamentoAtual"
                    type="number"
                    step="0.01"
                    min="0"
                    class="pdv-valor-recebido-input pdv-payment-value-input"
                    placeholder="0,00"
                    @keydown.enter.prevent="adicionarPagamento"
                    @focus="$event.target.select()"
                  />
                </div>
                <button
                  class="pdv-adicionar-pagamento-btn"
                  :disabled="!formaPagamentoSelecionada || valorPagamentoAtual <= 0"
                  @click="adicionarPagamento"
                >
                  <i class="fas fa-plus"></i> Adicionar
                </button>
              </div>
            </template>
          </div>
        </div>

        <!-- Pagamento -->
        <div class="payment-section">
          <h3 class="pdv-section-label"><i class="fas fa-credit-card"></i> Pagamento</h3>

          <!-- Formas rápidas de pagamento -->
          <div class="pdv-payment-cards">
            <button
              :class="['pdv-payment-card', { 'pdv-payment-card--active': pagamentoRapidoAtivo === 'avista' }]"
              :disabled="!carrinho.length || !_formaAvista"
              title="Dinheiro (F4)"
              @click="selecionarPagamentoDinheiro"
            >
              <i class="fas fa-money-bill-wave"></i>
              <span>Dinheiro</span>
              <span class="pdv-payment-card__atalho">F4</span>
            </button>
            <button
              :class="['pdv-payment-card', { 'pdv-payment-card--active': pagamentoRapidoAtivo === 'pix' }]"
              :disabled="!carrinho.length || !_formaPix"
              title="PIX (F3)"
              @click="adicionarPagamentoRapido('pix')"
            >
              <i class="fas fa-qrcode"></i>
              <span>Pix</span>
              <span class="pdv-payment-card__atalho">F3</span>
            </button>
            <button
              :class="['pdv-payment-card', { 'pdv-payment-card--active': pagamentoRapidoAtivo === 'credito1x' }]"
              :disabled="!carrinho.length || !_formaCredito"
              title="Cartão de Crédito (F1)"
              @click="adicionarPagamentoRapido('cartao_credito')"
            >
              <i class="fas fa-credit-card"></i>
              <span>C. Crédito</span>
              <span class="pdv-payment-card__atalho">F1</span>
            </button>
            <button
              :class="['pdv-payment-card', { 'pdv-payment-card--active': pagamentoRapidoAtivo === 'debito' }]"
              :disabled="!carrinho.length || !_formaDebito"
              title="Cartão de Débito (F2)"
              @click="adicionarPagamentoRapido('cartao_debito')"
            >
              <i class="fas fa-credit-card"></i>
              <span>C. Débito</span>
              <span class="pdv-payment-card__atalho">F2</span>
            </button>
            <div class="pdv-payment-outros-wrapper">
              <button
                :class="['pdv-payment-card pdv-payment-card--outros', { 'pdv-payment-card--active': pagamentoRapidoAtivo === 'outros' }]"
                :disabled="!carrinho.length"
                @click.stop="abrirMenuOutros"
              >
                <i class="fas fa-ellipsis-h"></i>
                <span>Outros</span>
              </button>
              <div v-if="outrosMenuAberto" class="pdv-outros-menu">
                <button
                  v-for="forma in formasOutros"
                  :key="forma.id || forma.id_meio_pagamento"
                  class="pdv-outros-menu__item"
                  @click.stop="selecionarFormaOutrosPagamento(forma)"
                >
                  <i :class="_iconePagamento(forma.descricao)"></i>
                  <span>{{ forma.descricao }}</span>
                  <span v-if="isTrue(forma.informar_cliente)" class="pdv-outros-exige-cliente">
                    <i class="fas fa-user-check"></i> Exige cliente
                  </span>
                </button>
                <div v-if="!formasOutros.length" class="pdv-outros-menu__empty">
                  Nenhuma outra forma disponível
                </div>
              </div>
            </div>
          </div>

          <!-- Preview de parcelas a prazo -->
          <div v-if="parcelasRange" class="nova-venda__parcelas-preview">
            <div class="nova-venda__parcelas-preview__title">
              <i class="fas fa-calendar-alt mr-1"></i>
              {{ parcelasRange.length }}x parcela{{ parcelasRange.length > 1 ? 's' : '' }} — ajuste os vencimentos se necessário
            </div>
            <div
              v-for="(p, i) in parcelasRange"
              :key="i"
              class="nova-venda__parcela-item"
            >
              <span class="nova-venda__parcela-num">{{ i + 1 }}ª</span>
              <span class="nova-venda__parcela-val">{{ formatBRL(p.value) }}</span>
              <input
                :value="parcelasVencimento[i]"
                type="date"
                class="nova-venda__parcela-date-input"
                @change="$set(parcelasVencimento, i, $event.target.value)"
              />
            </div>
          </div>

          <!-- Lista de pagamentos adicionados -->
          <div v-if="listaPagamentos.length" class="pdv-pagamentos-adicionados">
            <div class="pdv-pagamentos-adicionados__titulo">Pagamentos adicionados:</div>
            <div
              v-for="(pag, idx) in listaPagamentos"
              :key="idx"
              class="pdv-pagamento-item"
            >
              <i :class="_iconePagamento(pag.descricao)" class="pdv-pagamento-item__icone"></i>
              <div class="pdv-pagamento-item__info">
                <span class="pdv-pagamento-item__descricao">{{ pag.descricao }}</span>
                <span v-if="pag.vezes > 1" class="pdv-pagamento-item__parcelas">{{ pag.vezes }}x</span>
                <span v-if="pag.informar_cliente" class="pdv-badge-cliente">
                  <i class="fas fa-user-check"></i> Cliente obrigatório
                </span>
              </div>
              <span class="pdv-pagamento-item__valor pdv-payment-item-value">{{ formatBRL(pag.value) }}</span>
              <button
                class="pdv-pagamento-item__remover"
                title="Remover pagamento"
                @click="removerPagamento(idx)"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Resumo de pagamento -->
        <div class="payment-summary-section">
          <div class="pdv-resumo-pag">
            <div v-if="totalPago > 0" class="pdv-resumo-pag__item">
              <span class="pdv-resumo-pag__label"><i class="fas fa-check-circle"></i> Pago</span>
              <span class="pdv-resumo-pag__value pdv-payment-summary-value">{{ formatBRL(totalPago) }}</span>
            </div>
            <div
              v-if="totalComDescontoAcrescimo > 0"
              class="pdv-resumo-pag__item"
              :class="{ 'pdv-resumo-pag__item--restante': valorRestante > 0 }"
            >
              <span class="pdv-resumo-pag__label">
                <i :class="valorRestante > 0 ? 'fas fa-exclamation-circle' : 'fas fa-check-circle'"></i>
                Restante
              </span>
              <span class="pdv-resumo-pag__value pdv-payment-summary-value" :class="{ 'pdv-resumo-pag__value--restante': valorRestante > 0 }">
                {{ formatBRL(valorRestante) }}
              </span>
            </div>
            <div v-if="valorTroco > 0" class="pdv-resumo-pag__item pdv-resumo-pag__item--troco">
              <span class="pdv-resumo-pag__label"><i class="fas fa-coins"></i> Troco</span>
              <span class="pdv-resumo-pag__value pdv-resumo-pag__value--troco pdv-change-value">{{ formatBRL(valorTroco) }}</span>
            </div>
          </div>
        </div>

      </div><!-- /pdv-right-scroll -->

      <!-- Rodapé de ações -->
      <div class="checkout-actions-footer">
        <button
          v-if="mensagemSucesso || mensagemContingencia"
          class="checkout-action-btn checkout-action-btn--primary"
          @click="novaVenda"
        >
          <i class="fas fa-plus-circle"></i> Nova Venda
        </button>
        <button
          v-if="(mensagemSucesso || mensagemContingencia) && uuidUltimaVenda"
          class="checkout-action-btn checkout-action-btn--success"
          @click="modalDanfe = { visivel: true, uuid: uuidUltimaVenda }"
        >
          <i class="fas fa-receipt"></i> Imprimir DANFE
        </button>
        <template v-if="!mensagemSucesso && !mensagemContingencia">
          <button
            class="checkout-action-btn checkout-action-btn--secondary"
            title="Pesquisa avançada de produtos (F6)"
            @click="abrirPesquisaProdutos"
          >
            <i class="fas fa-box"></i>
            <span>Produtos (F6)</span>
          </button>
          <button
            class="checkout-action-btn checkout-action-btn--secondary"
            title="Pesquisa avançada de clientes (F7)"
            @click="abrirPesquisaClientes"
          >
            <i class="fas fa-user-friends"></i>
            <span>Clientes (F7)</span>
          </button>
          <button
            class="checkout-action-btn checkout-action-btn--secondary"
            title="Listar vendas (F9)"
            @click="abrirVendasOffline"
          >
            <i class="fas fa-list"></i>
            <span>Vendas (F9)</span>
          </button>
          <button
            class="checkout-action-btn checkout-action-btn--danger checkout-action-btn--linha-final"
            title="Cancelar a venda atual (Ctrl+S)"
            @click="cancelarVendaAtual"
          >
            <i class="fas fa-times-circle"></i>
            <span>Cancelar venda</span>
            <span class="checkout-action-btn__atalho">Ctrl+S</span>
          </button>
          <button
            class="checkout-action-btn checkout-action-btn--pre-venda checkout-action-btn--finalizar checkout-action-btn--linha-final"
            title="Concluir como pré-venda, gerar financeiro/estoque e preparar o XML (F8)"
            :disabled="!podeFinalizar || finalizando"
            @click="finalizarPreVenda"
          >
            <span
              v-if="finalizando && operacaoFinalizacao === 'pre_venda'"
              class="checkout-action-btn__conteudo"
            >
              <span class="spinner-border spinner-border-sm mr-2"></span>
              Salvando pré-venda...
            </span>
            <span v-else class="checkout-action-btn__conteudo">
              <i class="fas fa-file-invoice"></i>
              <span class="checkout-action-btn__label">Finalizar pré-venda (F8)</span>
            </span>
          </button>
          <button
            class="checkout-action-btn checkout-action-btn--primary checkout-action-btn--finalizar checkout-action-btn--linha-final"
            title="Finalizar a venda como NFC-e (F5)"
            :disabled="!podeFinalizar || finalizando"
            @click="finalizarVendaPorAtalho"
          >
            <span
              v-if="finalizando && operacaoFinalizacao === 'nfce'"
              class="checkout-action-btn__conteudo"
            >
              <span class="spinner-border spinner-border-sm mr-2"></span>
              Finalizando...
            </span>
            <span v-else class="checkout-action-btn__conteudo">
              <i class="fas fa-check-circle"></i>
              <span class="checkout-action-btn__label">Finalizar NFC-e (F5)</span>
            </span>
          </button>
        </template>
      </div>
    </section>

    <!-- Overlay para fechar menu Outros -->
    <div v-if="outrosMenuAberto" class="pdv-outros-overlay" @click="outrosMenuAberto = false"></div>

    <!-- Modal DANFE NFC-e -->
    <DanfeNfce
      :visivel="modalDanfe.visivel"
      :uuid="modalDanfe.uuid"
      @fechar="modalDanfe.visivel = false"
    />

    <!-- Modal seleção de combinação -->
    <CombinacaoProdutoModalPdv
      :visivel="modalCombinacao.visivel"
      :produto="modalCombinacao.produto"
      :combinacoes="modalCombinacao.combinacoes"
      @confirmar="onConfirmarCombinacao"
      @fechar="onFecharModalCombinacao"
    />

    <!-- Modal seleção de lote -->
    <LoteProdutoModalPdv
      :visivel="modalLote.visivel"
      :produto="modalLote.produto"
      :lotes="modalLote.lotes"
      :quantidade="Number(quantidadeItem) || 1"
      @confirmar="onConfirmarLote"
      @fechar="onFecharModalLote"
    />

    <!-- Modal correção fiscal de item do carrinho -->
    <CorrecaoFiscalItemCarrinho
      :visivel="modalCorrecaoFiscal.visivel"
      :item="modalCorrecaoFiscal.item"
      :erroInfo="modalCorrecaoFiscal.erroInfo"
      @salvar="onSalvarCorrecaoFiscal"
      @fechar="modalCorrecaoFiscal.visivel = false; mensagemErro = ''; erroFiscalCorrigido = false"
    />

    <!-- Modal desconto/acréscimo de item do carrinho -->
    <ItemDescontoAcrescimoModal
      :visivel="modalItemDescAcres.visivel"
      :item="modalItemDescAcres.item"
      @aplicar="onAplicarDescontoAcrescimoItem"
      @fechar="modalItemDescAcres.visivel = false"
    />

    <!-- Modais Caixa Local -->
    <AbrirCaixaModal
      :visivel="modalAbrirCaixa.visivel"
      :bloqueante="modalAbrirCaixa.bloqueante"
      @aberto="onCaixaAberto"
      @fechar="modalAbrirCaixa.visivel = false"
    />
    <FecharCaixaModal
      :visivel="modalFecharCaixa.visivel"
      :caixa-aberto="caixaAberto"
      @fechado="onCaixaFechado"
      @fechar="modalFecharCaixa.visivel = false"
    />
    <MovimentoCaixaModal
      :visivel="modalMovimento.visivel"
      :tipo="modalMovimento.tipo"
      @registrado="onMovimentoRegistrado"
      @fechar="modalMovimento.visivel = false"
    />

    <!-- Modal pesquisa avançada de produtos -->
    <ProdutoPesquisaAvancadaModal
      :visivel="modalProdutoPesquisa.visivel"
      @selecionar="onSelecionarProdutoPesquisaAvancada"
      @fechar="modalProdutoPesquisa.visivel = false"
    />

    <!-- Modal pesquisa avançada de clientes -->
    <ClientePesquisaAvancadaModal
      :visivel="modalClientePesquisa.visivel"
      @selecionar="onSelecionarClientePesquisaAvancada"
      @fechar="modalClientePesquisa.visivel = false"
    />
  </div>
  </div>
</template>

<script>
import _ from "lodash";
import ProdutoLocalRepository from "@/modules/PdvDesktop/repositories/ProdutoLocalRepository";
import ClienteLocalRepository from "@/modules/PdvDesktop/repositories/ClienteLocalRepository";
import VendedorLocalRepository from "@/modules/PdvDesktop/repositories/VendedorLocalRepository";
import GrupoLocalRepository from "@/modules/PdvDesktop/repositories/GrupoLocalRepository";
import FormaPagamentoLocalRepository from "@/modules/PdvDesktop/repositories/FormaPagamentoLocalRepository";
import ListaPrecoLocalRepository from "@/modules/PdvDesktop/repositories/ListaPrecoLocalRepository";
import ProdutoLoteLocalRepository from "@/modules/PdvDesktop/repositories/ProdutoLoteLocalRepository";
import ProdutoCombinacaoLocalRepository from "@/modules/PdvDesktop/repositories/ProdutoCombinacaoLocalRepository";
import NfcePdvDesktopService from "@/modules/PdvDesktop/services/NfcePdvDesktopService";
import FiscalContextPdvDesktopService from "@/modules/PdvDesktop/services/FiscalContextPdvDesktopService";
import DanfeNfce from "@/modules/PdvDesktop/components/DanfeNfce.vue";
import LoteProdutoModalPdv from "@/modules/PdvDesktop/components/LoteProdutoModalPdv.vue";
import CombinacaoProdutoModalPdv from "@/modules/PdvDesktop/components/CombinacaoProdutoModalPdv.vue";
import CorrecaoFiscalItemCarrinho from "@/modules/PdvDesktop/components/CorrecaoFiscalItemCarrinho.vue";
import ItemDescontoAcrescimoModal from "@/modules/PdvDesktop/components/ItemDescontoAcrescimoModal.vue";
import AbrirCaixaModal from "@/modules/PdvDesktop/components/AbrirCaixaModal.vue";
import FecharCaixaModal from "@/modules/PdvDesktop/components/FecharCaixaModal.vue";
import MovimentoCaixaModal from "@/modules/PdvDesktop/components/MovimentoCaixaModal.vue";
import ProdutoPesquisaAvancadaModal from "@/modules/PdvDesktop/components/ProdutoPesquisaAvancadaModal.vue";
import ClientePesquisaAvancadaModal from "@/modules/PdvDesktop/components/ClientePesquisaAvancadaModal.vue";
import CaixaPdvDesktopService from "@/modules/PdvDesktop/services/CaixaPdvDesktopService";
import SyncPdvDesktopService from "@/modules/PdvDesktop/services/SyncPdvDesktopService";
import TerminalConfigService from "@/modules/PdvDesktop/services/TerminalConfigService";
import CaixaSyncService from "@/modules/PdvDesktop/services/CaixaSyncService";
import PdvDesktopAuthService from "@/modules/PdvDesktop/services/PdvDesktopAuthService";
import BalancaCheckoutService from "@/modules/PdvDesktop/services/BalancaCheckoutService";
import CaixaLocalRepository from "@/modules/PdvDesktop/repositories/CaixaLocalRepository";
import PdvHeader from "@/modules/PdvDesktop/components/PdvHeader.vue";
import { getElectronAPI } from "@/plugins/desktop";

export default {
  name: "NovaVenda",

  components: { DanfeNfce, LoteProdutoModalPdv, CombinacaoProdutoModalPdv, CorrecaoFiscalItemCarrinho, ItemDescontoAcrescimoModal, AbrirCaixaModal, FecharCaixaModal, MovimentoCaixaModal, ProdutoPesquisaAvancadaModal, ClientePesquisaAvancadaModal, PdvHeader },

  data() {
    return {
      // Estado de vínculo do terminal
      terminalBloqueado: false,
      motivoBloqueio: "",
      configuracaoNfceAusente: false,

      // Busca produto
      termoBusca: "",
      produtosBusca: [],
      buscando: false,
      selectedProductIndex: 0,

      // Campos de entrada de produto
      produtoSelecionado: null,
      quantidadeItem: 1,
      valorUnitarioItem: 0,
      valorTotalItem: 0,
      erroItem: "",
      feedbackItem: "",
      codigoBalancaAtual: null,
      valorUnitarioAlteradoManual: false,

      // Balança de checkout
      balancaConfig: null,
      balancaConectada: false,
      balancaEstado: "idle",
      balancaPesoTempoReal: 0,
      balancaErro: "",
      balancaCapturando: false,
      aguardandoPesoBalanca: false,
      balancaPesoProcessado: false,
      balancaAplicandoPeso: false,
      balancaEsperaId: 0,

      // Busca cliente
      termoCliente: "",
      clientesBusca: [],
      clienteSelecionado: null,
      modoCliente: "cadastrado",
      clienteManualNome: "",
      clienteManualDocumento: "",
      clienteManualExpandido: false,
      clienteManualNomeDraft: "",
      clienteManualDocumentoDraft: "",

      // Vendedor
      vendedores: [],
      vendedorSelecionado: null,

      // Filtro de grupo
      grupos: [],
      grupoFiltro: null,

      // Lista de preço
      listasDisponiveis: [],          // todas as listas ativas
      listaPrecoSelecionada: null,    // lista ativa no momento
      listaPrecoDeCliente: false,     // true quando veio do cliente automaticamente
      listaPrecoAberta: false,        // controla exibição do select inline
      idListaPrecoCliente: null,
      listaPrecoMap: {},              // { id_produto: preco_lista } para lookup rápido

      // Formas de pagamento
      formasPagamento: [],
      formaPagamentoSelecionada: null,
      valorPagamentoAtual: 0,
      listaPagamentos: [],

      outrosMenuAberto: false,

      // Carrinho
      carrinho: [],

      // Desconto / Acréscimo na venda
      descontoVendaReais: 0,
      descontoVendaPct: 0,
      acrescimoVendaReais: 0,
      acrescimoVendaPct: 0,

      // Informações complementares
      observacaoVenda: "",
      complementaresAberto: false,

      // Estado
      finalizando: false,
      operacaoFinalizacao: null,
      mensagemSucesso: "",
      mensagemErro: "",
      mensagemContingencia: "",
      uuidRejeicao: null,
      uuidUltimaVenda: null,
      pagamentoRapidoAtivo: null,

      // Dados fiscais
      sincronizandoFiscal: false,
      mensagemFiscal: "",
      mensagemFiscalErro: false,

      // Datas de vencimento das parcelas (geradas de intervalo_dias)
      parcelasVencimento: [],
      diasParcelas: [], // mantido por compatibilidade

      // Aviso de cliente obrigatório por forma de pagamento
      avisoClienteObrigatorio: "",

      // Modal de Combinação
      modalCombinacao: {
        visivel:              false,
        produto:              null,
        combinacoes:          [],
        combinacaoConfirmada: null,
      },

      // Modal de Lote
      modalLote: {
        visivel: false,
        lotes:   [],
        produto: null,
      },

      // Modal DANFE NFC-e (após finalização)
      modalDanfe: {
        visivel: false,
        uuid:    null,
      },

      // Modal de correção fiscal de item do carrinho (antes da finalização)
      modalCorrecaoFiscal: {
        visivel:  false,
        item:     null,
        itemIdx:  -1,
        erroInfo: null,
      },
      // Modal de desconto/acréscimo por item
      modalItemDescAcres: {
        visivel: false,
        item:    null,
        itemIdx: -1,
      },
      // Índice do item selecionado no carrinho (para atalho Ctrl+D)
      itemSelecionadoIdx: -1,
      // true após correção fiscal ser salva — habilita botão de retry
      erroFiscalCorrigido: false,

      // ─── Caixa Local PDV Desktop ───────────────────────────────────
      caixaAberto: null,
      resumoCaixa: null,
      modalAbrirCaixa:  { visivel: false, bloqueante: false },
      modalFecharCaixa: { visivel: false },
      modalMovimento:   { visivel: false, tipo: "sangria" },

      // ─── Header operacional do PDV Desktop ─────────────────────────
      online: typeof navigator !== "undefined" ? navigator.onLine : true,
      operadorNome: "",
      terminalNome: "",
      terminalSerie: "",
      terminalUuid: "",
      pendentesSync: 0,
      sincronizandoCaixa: false,

      // ─── Modais de pesquisa avançada ───────────────────────────────
      modalProdutoPesquisa: { visivel: false },
      modalClientePesquisa: { visivel: false },

      // ─── Saída do PDV em andamento ─────────────────────────────────
      saindoDoPdv: false,
      confirmandoCancelamento: false,
    };
  },

  watch: {
    // Gera vencimentos a partir de intervalo_dias quando a forma de pagamento muda
    formaPagamentoSelecionada(forma) {
      if (!forma) { this.parcelasVencimento = []; this.diasParcelas = []; return; }
      // Preenche campo de valor com o restante quando a forma muda
      this.valorPagamentoAtual = this.valorRestante > 0 ? this.valorRestante : 0;

      const tipo  = Number(forma.tipo_repeticao ?? 0);
      const vezes = Number(forma.vezes) || 1;
      const hoje  = new Date();
      const toISO = (d) => new Date(d).toISOString().split("T")[0];

      if (tipo === 0) {
        this.parcelasVencimento = [];
        this.diasParcelas = [];
        return;
      }

      const intervaloData = forma.intervalo_dias;
      let datas = [];

      // tipo_repeticao === 9 → intervalo_dias.intervalo é array de objetos
      if (tipo === 9 && intervaloData && Array.isArray(intervaloData.intervalo)) {
        datas = intervaloData.intervalo.slice(0, vezes).map((item) => {
          const offset = Number(item.periodo_qtd) || 0;
          const period = (item.periodo_descricao || "days").toLowerCase();
          const dt = new Date(hoje);
          if (period === "months")      dt.setMonth(dt.getMonth() + offset);
          else if (period === "years") dt.setFullYear(dt.getFullYear() + offset);
          else if (period === "weeks") dt.setDate(dt.getDate() + offset * 7);
          else                         dt.setDate(dt.getDate() + offset);
          return toISO(dt);
        });

      // intervalo regular (sequencial) → intervalo_dias.intervalo é objeto
      } else if (intervaloData && intervaloData.intervalo && !Array.isArray(intervaloData.intervalo)) {
        const iv         = intervaloData.intervalo;
        const periodoQtd = Number(iv.periodo_qtd) || 1;
        const periodoDesc = (iv.periodo_descricao || "days").toLowerCase();
        let prev = null;
        datas = Array.from({ length: vezes }, () => {
          const base = prev ? new Date(prev) : new Date(hoje);
          if (periodoDesc === "months")      base.setMonth(base.getMonth() + periodoQtd);
          else if (periodoDesc === "years") base.setFullYear(base.getFullYear() + periodoQtd);
          else if (periodoDesc === "weeks") base.setDate(base.getDate() + periodoQtd * 7);
          else                              base.setDate(base.getDate() + periodoQtd);
          prev = new Date(base);
          return toISO(base);
        });

      } else {
        // fallback sem intervalo: 30 dias por parcela
        datas = Array.from({ length: vezes }, (_, i) => {
          const dt = new Date(hoje);
          dt.setDate(dt.getDate() + (i + 1) * 30);
          return toISO(dt);
        });
      }

      this.parcelasVencimento = datas;
      const MS_DAY = 24 * 60 * 60 * 1000;
      this.diasParcelas = datas.map((d) => Math.round((new Date(d) - hoje) / MS_DAY));
    },

    // Sincroniza desconto R$ ↔ %
    descontoVendaReais(val) {
      if (this._descontoOrigemPct) return;
      this._descontoOrigemPct = false;
      const s = this.subtotal;
      this.descontoVendaPct = s > 0 ? parseFloat(((Number(val) / s) * 100).toFixed(2)) : 0;
    },
    descontoVendaPct(val) {
      this._descontoOrigemPct = true;
      this.descontoVendaReais = parseFloat(((Number(val) / 100) * this.subtotal).toFixed(2));
      this.$nextTick(() => { this._descontoOrigemPct = false; });
    },
    // Sincroniza acréscimo R$ ↔ %
    acrescimoVendaReais(val) {
      if (this._acrescimoOrigemPct) return;
      const s = this.subtotal;
      this.acrescimoVendaPct = s > 0 ? parseFloat(((Number(val) / s) * 100).toFixed(2)) : 0;
    },
    acrescimoVendaPct(val) {
      this._acrescimoOrigemPct = true;
      this.acrescimoVendaReais = parseFloat(((Number(val) / 100) * this.subtotal).toFixed(2));
      this.$nextTick(() => { this._acrescimoOrigemPct = false; });
    },
  },

  computed: {
    balancaMensagem() {
      if (this.balancaErro) return this.balancaErro;
      if (!this.balancaConectada) return "Conectando à balança...";
      if (this.balancaEstado === "estavel") return "Peso estável. Adicionando produto...";
      if (this.balancaPesoTempoReal > 0) return "Aguardando o peso estabilizar...";
      return "Esperando item na balança";
    },

    subtotal() {
      return this.carrinho.reduce((acc, item) => acc + (Number(item.total) || 0), 0);
    },

    // Soma dos totais brutos por item (qty * unit) — antes de descontos/acréscimos
    totalItensBruto() {
      return this.carrinho.reduce((acc, item) => {
        const tb = Number(item.total_bruto);
        if (tb > 0) return acc + tb;
        const q = Number(item.quantidade)     || 0;
        const u = Number(item.valor_unitario) || 0;
        return acc + (q * u);
      }, 0);
    },

    // Soma de descontos por item
    totalDescontoItens() {
      return this.carrinho.reduce(
        (acc, item) => acc + (Number(item.desconto_valor ?? item.desconto) || 0),
        0,
      );
    },

    // Soma de acréscimos por item
    totalAcrescimoItens() {
      return this.carrinho.reduce(
        (acc, item) => acc + (Number(item.acrescimo_valor ?? item.acrescimo) || 0),
        0,
      );
    },

    // Total geral de desconto (itens + venda)
    totalDescontoTudo() {
      return (Number(this.totalDescontoItens) || 0) + (Number(this.descontoVendaReais) || 0);
    },

    // Total geral de acréscimo (itens + venda)
    totalAcrescimoTudo() {
      return (Number(this.totalAcrescimoItens) || 0) + (Number(this.acrescimoVendaReais) || 0);
    },

    totalComDescontoAcrescimo() {
      const s = this.subtotal;
      const d = Number(this.descontoVendaReais) || 0;
      const a = Number(this.acrescimoVendaReais) || 0;
      return Math.max(0, s - d + a);
    },

    totalPago() {
      return this.listaPagamentos.reduce((acc, pag) => acc + (Number(pag.value) || 0), 0);
    },

    troco() {
      const diff = this.totalPago - this.totalComDescontoAcrescimo;
      return diff > 0 ? diff : 0;
    },

    valorRestante() {
      return Math.max(0, this.totalComDescontoAcrescimo - this.totalPago);
    },

    valorTroco() {
      return Math.max(0, this.totalPago - this.totalComDescontoAcrescimo);
    },

    vendaQuitada() {
      return this.totalPago > 0 && this.totalPago >= this.totalComDescontoAcrescimo;
    },

    formasOutros() {
      return this.formasPagamento.filter(
        (f) => !/\bvista\b|\bdinheiro\b|\besp[eé]cie\b|pix|cr[eé]dito|d[eé]bito/i.test(f.descricao)
      );
    },

    _formaAvista() {
      return this.formasPagamento.find((f) =>
        /\bvista\b|\bdinheiro\b|\besp[eé]cie\b/i.test(f.descricao)
      ) || null;
    },

    _formaPix() {
      return this.formasPagamento.find((f) => /pix/i.test(f.descricao)) || null;
    },

    _formaCredito() {
      return this.formasPagamento.find((f) => /cr[eé]dito/i.test(f.descricao)) || null;
    },

    _formaDebito() {
      return this.formasPagamento.find((f) => /d[eé]bito/i.test(f.descricao)) || null;
    },

    _vendaExigeCliente() {
      return this.listaPagamentos.some((p) => p.informar_cliente);
    },

    _clienteInformado() {
      if (this.modoCliente === "manual") {
        const nome = (this.clienteManualNome || "").trim();
        const doc = this._normalizarDocumento(this.clienteManualDocumento);
        return !!(nome || doc);
      }
      const c = this.clienteSelecionado;
      if (!c) return false;
      return !!(c.id_pessoa || c.id || c.cnpj_cpf || c.cpf);
    },

    podeFinalizar() {
      return (
        this.carrinho.length > 0 &&
        this.totalComDescontoAcrescimo > 0 &&
        this.listaPagamentos.length > 0 &&
        this.totalPago >= this.totalComDescontoAcrescimo
      );
    },

    // Retorna [{date, value}] para cada parcela com vencimento calculado via intervalo_dias.
    parcelasRange() {
      const valor = Number(this.valorPagamentoAtual);
      if (!this.parcelasVencimento.length || valor <= 0) return null;

      const n    = this.parcelasVencimento.length;
      const base = parseFloat((valor / n).toFixed(2));

      return this.parcelasVencimento.map((date, i) => {
        const v = i === n - 1
          ? parseFloat((valor - base * (n - 1)).toFixed(2))
          : base;
        return { date, value: v };
      });
    },
  },

  created() {
    // Instância própria do debounce para evitar compartilhamento entre instâncias
    this.onBuscarProduto = _.debounce(this._buscarProduto, 350);
    this.onBuscarCliente = _.debounce(this._buscarCliente, 500);
    // Flags de controle de loop (não reativas — fora do data())
    this._descontoOrigemPct = false;
    this._acrescimoOrigemPct = false;
  },

  async mounted() {
    if (!this.$isDesktop) {
      this.$router.push({ name: "pdvDesktopLogin" });
      return;
    }

    await Promise.all([
      this.carregarFormasPagamento(),
      this.carregarListasPreco(),
      this.carregarVendedores(),
      this.carregarGrupos(),
      this._carregarContextoHeader(),
      this._inicializarBalancaCheckout(),
    ]);
    this._registrarAtalhos();
    window.addEventListener("online",  this._onConexaoChange);
    window.addEventListener("offline", this._onConexaoChange);

    // ── Valida vínculo do terminal com o back (se estiver online) ─────────
    await this._validarVinculoTerminalInicio();
    if (this.terminalBloqueado) return; // interrompe o mount — redirecionamento já agendado

    // ── Caixa local: precisa estar aberto para vender ──────────────────
    await this.carregarCaixaAberto();
    if (!this.caixaAberto) {
      this.modalAbrirCaixa = { visivel: true, bloqueante: true };
    }
    // Sincroniza caixas pendentes em background (não bloqueia abertura da tela)
    this._sincronizarCaixaBackground();

    this.$nextTick(() => {
      this.$refs.searchInput && this.$refs.searchInput.focus();
    });
  },

  beforeDestroy() {
    this._removerAtalhos();
    window.removeEventListener("online",  this._onConexaoChange);
    window.removeEventListener("offline", this._onConexaoChange);
    if (typeof this._removerListenerBalanca === "function") this._removerListenerBalanca();
    BalancaCheckoutService.desconectar().catch(() => {});
  },

  methods: {
    // ── Formatação ──────────────────────────────────────────────────────

    formatBRL(val) {
      return Number(val || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },

    _imagemValida(url) {
      return typeof url === "string" && url.trim().length > 0 && /^https?:\/\//i.test(url);
    },

    _nomeItemCarrinho(item) {
      let nome = item.descricao;
      if (item.descricao_combinacao) {
        nome += ` - ${item.descricao_combinacao}`;
      }
      if (item.numero_lote) {
        nome += ` - N\u00ba do Lote: ${item.numero_lote}`;
        if (item.data_validade) {
          const [ano, mes, dia] = String(item.data_validade).split('T')[0].split('-');
          nome += ` / Dt Validade: ${dia}/${mes}/${ano}`;
        }
      }
      return nome;
    },

    nomeCliente(c) {
      return c.nome_fantasia || c.nome || c.razao_social || c.cnpj_cpf || "—";
    },

    _iconePagamento(descricao) {
      const d = descricao || "";
      if (/pix/i.test(d)) return "fas fa-qrcode";
      if (/cr[eé]dito/i.test(d)) return "fas fa-credit-card";
      if (/d[eé]bito/i.test(d)) return "fas fa-credit-card";
      if (/vista|dinheiro|esp[eé]cie/i.test(d)) return "fas fa-money-bill-wave";
      return "fas fa-coins";
    },

    // ── Helpers de validação ───────────────────────────────────────────

    isTrue(value) {
      return value === true || value === 1 || value === "1" || value === "true";
    },

    _focarCampoCliente() {
      this.$nextTick(() => {
        const seletor = this.modoCliente === "manual"
          ? '.customer-section .nova-venda__cliente-manual-grid input[type="text"]'
          : '.customer-section input[type="text"]';
        const input = this.$el && this.$el.querySelector(seletor);
        if (input) input.focus();
      });
    },

    _normalizarDocumento(valor) {
      return String(valor || "").replace(/\D/g, "").slice(0, 14);
    },

    onInputDocumentoManual() {
      const doc = this._normalizarDocumento(this.clienteManualDocumento);
      this.clienteManualDocumento = doc;
      this._verificarClienteObrigatorio();
    },

    onInputDocumentoManualDraft() {
      this.clienteManualDocumentoDraft = this._normalizarDocumento(this.clienteManualDocumentoDraft);
    },

    abrirClienteManual() {
      this.modoCliente = "manual";
      this.clienteSelecionado = null;
      this.termoCliente = "";
      this.clientesBusca = [];
      if (this.listaPrecoDeCliente) {
        this.removerListaPreco();
      }
      this.clienteManualNomeDraft = this.clienteManualNome || "";
      this.clienteManualDocumentoDraft = this.clienteManualDocumento || "";
      this.clienteManualExpandido = true;
      this._focarCampoCliente();
    },

    aplicarClienteManual() {
      const nome = (this.clienteManualNomeDraft || "").trim();
      const doc = this._normalizarDocumento(this.clienteManualDocumentoDraft);

      if (!nome && !doc) {
        this.mensagemErro = "Informe ao menos Nome ou CPF/CNPJ para aplicar cliente manual.";
        return;
      }

      this.clienteManualNome = nome;
      this.clienteManualDocumento = doc;
      this.modoCliente = "manual";
      this.clienteManualExpandido = false;
      this.mensagemErro = "";
      this._verificarClienteObrigatorio();
    },

    limparClienteManualDraft() {
      this.clienteManualNomeDraft = "";
      this.clienteManualDocumentoDraft = "";
      this.$nextTick(() => this._focarCampoCliente());
    },

    limparClienteManual() {
      this.clienteManualNome = "";
      this.clienteManualDocumento = "";
      this.clienteManualNomeDraft = "";
      this.clienteManualDocumentoDraft = "";
      this.modoCliente = "cadastrado";
      this.clienteManualExpandido = false;
      this._verificarClienteObrigatorio();
    },

    cancelarClienteManual() {
      this.clienteManualExpandido = false;
      this.clienteManualNomeDraft = this.clienteManualNome || "";
      this.clienteManualDocumentoDraft = this.clienteManualDocumento || "";
      if (!this.clienteManualNome && !this.clienteManualDocumento) {
        this.modoCliente = "cadastrado";
      }
      this._verificarClienteObrigatorio();
    },

    definirModoCliente(modo) {
      if (modo !== "cadastrado" && modo !== "manual") return;
      this.modoCliente = modo;
      if (modo === "manual") {
        this.clienteSelecionado = null;
        this.termoCliente = "";
        this.clientesBusca = [];
        if (this.listaPrecoDeCliente) {
          this.removerListaPreco();
        }
      } else {
        this.clienteManualNome = "";
        this.clienteManualDocumento = "";
      }
      this._verificarClienteObrigatorio();
      this._focarCampoCliente();
    },

    _montarBuyer() {
      if (this.modoCliente === "manual") {
        const nome = (this.clienteManualNome || "").trim();
        const doc = this._normalizarDocumento(this.clienteManualDocumento);
        return {
          id_pessoa: null,
          id_cliente: null,
          uuid: null,
          nome_fantasia: nome || null,
          nome: nome || null,
          cnpj_cpf: doc || null,
          cpf: doc || null,
        };
      }

      return this.clienteSelecionado
        ? {
            id_pessoa:     this.clienteSelecionado.id_pessoa     ?? null,
            id_cliente:    this.clienteSelecionado.id_cliente    ?? null,
            uuid:          this.clienteSelecionado.uuid          ?? null,
            nome_fantasia: this.nomeCliente(this.clienteSelecionado),
            cnpj_cpf:
              this.clienteSelecionado.cnpj_cpf ||
              this.clienteSelecionado.cpf ||
              null,
          }
        : {};
    },

    _verificarClienteObrigatorio() {
      const exige = this.listaPagamentos.some((p) => p.informar_cliente);
      if (exige && !this._clienteInformado) {
        this.avisoClienteObrigatorio = "Esta forma de pagamento exige cliente informado.";
        this._focarCampoCliente();
      } else {
        this.avisoClienteObrigatorio = "";
      }
    },

    // ── Produto ─────────────────────────────────────────────────────────

    async _buscarProduto() {
      // Se há produto selecionado e o usuário digitou nova busca, limpar seleção
      if (this.produtoSelecionado) {
        this._limparCamposItem();
      }

      const termo = (this.termoBusca || "").trim();
      if (termo.length < 1) {
        this.produtosBusca = [];
        return;
      }

      this.buscando = true;
      try {
        // Leitura/digitação de código de barras: adiciona direto no carrinho
        // com quantidade 1, mantendo código de balança com prioridade.
        if (/^\d{4,}$/.test(termo)) {
          // Tenta como código de balança (13 dígitos): auto-insere se pesado
          const tratado = await this._processarCodigoBalanca(termo);
          if (tratado) return;

          if (/^\d{8,14}$/.test(termo)) {
            const adicionouDireto = await this.tentarAdicionarProdutoExato(termo);
            if (adicionouDireto) return;
          }

          const porBarras = await ProdutoLocalRepository.buscarPorCodigoBarras(termo);
          if (porBarras) {
            this.selecionarProduto(porBarras);
            return;
          }
        }

        const results = (await ProdutoLocalRepository.search(termo, this.grupoFiltro, { typeFilter: 3 })) || [];
        this.produtosBusca = results;
        this.selectedProductIndex = 0;

        // Auto-seleciona apenas para códigos numéricos curtos digitados manualmente.
        // Códigos longos (ex.: EAN-13 de scanner) não devem mover foco para quantidade.
        if (results.length === 1 && /^\d{1,7}$/.test(termo)) {
          this.selecionarProduto(results[0]);
        }
      } catch (e) {
        console.error("[NovaVenda] Erro na busca de produto:", e);
        this.produtosBusca = [];
        this.selectedProductIndex = 0;
      } finally {
        this.buscando = false;
      }
    },

    navegarResultados(direcao) {
      if (!this.produtosBusca.length) return;
      const total = this.produtosBusca.length;
      this.selectedProductIndex = (this.selectedProductIndex + direcao + total) % total;
      this._scrollItemVisivel();
    },

    _scrollItemVisivel() {
      this.$nextTick(() => {
        const list = this.$refs.resultsList;
        if (!list) return;
        const items = list.querySelectorAll(".nova-venda__result-item");
        const active = items[this.selectedProductIndex];
        if (active) active.scrollIntoView({ block: "nearest" });
      });
    },

    // Disparado pelo Enter no campo Produto/Código
    async handleEnterProduto() {
      const termo = String(this.termoBusca || "").trim();
      if (!termo) return;
      const termoSomenteDigitos = termo.replace(/\D/g, "");
      const leituraCodigoBarras = /^\d{8,14}$/.test(termoSomenteDigitos);

      console.log("[PDV ENTER] termo:", termo);

      // 1. Tenta código de balança (produto pesado, 13 dígitos)
      const processouBalanca = await this._processarCodigoBalanca(termo);
      if (processouBalanca) return;

      // 2. Tenta produto exato pelo código — ANTES de consultar a lista
      //    Isso garante que um código de barras bipado seja adicionado direto
      //    mesmo que haja resultados de busca anteriores em produtosBusca.
      const adicionouExato = await this.tentarAdicionarProdutoExato(termo);
      if (adicionouExato) return;

      // Para leitura de código de barras, não cair no fallback de seleção de item,
      // pois isso muda o foco para quantidade e quebra o fluxo de bipe contínuo.
      if (leituraCodigoBarras) return;

      // 3. Usa o item destacado da lista de resultados visível (busca por descrição)
      if (this.produtosBusca.length > 0) {
        const produto =
          this.produtosBusca[this.selectedProductIndex] || this.produtosBusca[0];
        if (produto) {
          this.selecionarProduto(produto);
          return;
        }
      }

      // 4. Produto já selecionado manualmente: foca campo quantidade
      if (this.produtoSelecionado) {
        this.$refs.qtyInput && this.$refs.qtyInput.focus();
        this.$refs.qtyInput && this.$refs.qtyInput.select();
      }
    },

    /**
     * Tenta encontrar produto por código exato e adiciona direto no carrinho.
     * Retorna true se o produto foi encontrado e processado, false caso contrário.
     */
    async tentarAdicionarProdutoExato(termo) {
      let produto = null;
      const termoOriginal = String(termo || "").trim();
      const termoSomenteDigitos = termoOriginal.replace(/\D/g, "");
      const codigoBarrasLido = /^\d{8,14}$/.test(termoOriginal)
        ? termoOriginal
        : (/^\d{8,14}$/.test(termoSomenteDigitos) ? termoSomenteDigitos : null);
      const normalizarCodigo = (valor) => String(valor || "").replace(/\D/g, "");

      try {
        // Prioriza leitura por código de barras para inclusão direta no carrinho.
        if (codigoBarrasLido) {
          produto = await ProdutoLocalRepository.buscarPorCodigoBarras(codigoBarrasLido);
        }

        if (!produto) {
          produto = await ProdutoLocalRepository.buscarProdutoExato(termoOriginal);
        }

        if (!produto && codigoBarrasLido && codigoBarrasLido !== termoOriginal) {
          produto = await ProdutoLocalRepository.buscarProdutoExato(codigoBarrasLido);
        }

        if (!produto && codigoBarrasLido) {
          const candidatos = (await ProdutoLocalRepository.search(codigoBarrasLido)) || [];
          produto = candidatos.find((p) => {
            const codBarras = normalizarCodigo(p.cod_barras);
            const codigo = normalizarCodigo(p.codigo);
            const referencia = normalizarCodigo(p.referencia);
            const idProduto = normalizarCodigo(p.id_produto || p.id);
            return (
              codBarras === codigoBarrasLido ||
              codigo === codigoBarrasLido ||
              referencia === codigoBarrasLido ||
              idProduto === codigoBarrasLido
            );
          }) || null;
        }
      } catch (e) {
        console.error("[NovaVenda] buscarProdutoExato erro:", e);
        return false;
      }

      console.log("[PDV ENTER] produto exato:", produto);

      if (!produto) return false;

      const produtoNorm = this._normalizarProduto(produto);
      const ehLeituraCodigoBarras = !!codigoBarrasLido;

      if (this._bloqueadoPorReservaCondicional(produtoNorm)) return true;

      if (this._produtoUsaBalancaCheckout(produtoNorm)) {
        this.selecionarProduto(produtoNorm);
        return true;
      }

      // Leitura por código de barras entra sempre com quantidade 1.
      const quantidade = ehLeituraCodigoBarras ? 1 : this.getQuantidadeEntrada();
      const valorUnitario = ehLeituraCodigoBarras
        ? this.obterPrecoProduto(produtoNorm, quantidade)
        : this.getValorUnitarioEntrada(produtoNorm);

      console.log("[PDV ENTER] adicionando direto no carrinho");

      // Produto controla combinação?
      if (await this._produtoControlaCombinacoesAsync(produtoNorm)) {
        let combinacoes = [];
        try {
          combinacoes = (await ProdutoCombinacaoLocalRepository.buscarCombinacoesPorProduto(this.getIdProduto(produtoNorm))) || [];
        } catch (e) {
          console.error("[NovaVenda] Erro ao buscar combinações:", e);
        }
        if (combinacoes.length === 0) {
          this.erroItem = "Nenhuma combinação cadastrada para este produto.";
          return true;
        }
        this.produtoSelecionado  = produtoNorm;
        this.quantidadeItem      = quantidade;
        this.valorUnitarioItem   = valorUnitario;
        this.modalCombinacao.produto     = produtoNorm;
        this.modalCombinacao.combinacoes = combinacoes;
        this.modalCombinacao.combinacaoConfirmada = null;
        this.modalCombinacao.visivel     = true;
        return true;
      }

      // Produto controla lote?
      if (this._produtoControlaLote(produtoNorm)) {
        let lotes = [];
        try {
          lotes =
            (await ProdutoLoteLocalRepository.buscarLotesDisponiveisPorProduto(
              produtoNorm.id_produto
            )) || [];
        } catch (e) {
          console.error("[NovaVenda] Erro ao buscar lotes:", e);
        }

        if (lotes.length === 0) {
          this.erroItem = "Nenhum lote disponível em estoque para este produto.";
          return true;
        }

        if (lotes.length === 1) {
          await this._adicionarAoCarrinhoComLote(
            produtoNorm,
            quantidade,
            valorUnitario,
            lotes[0],
            quantidade
          );
          this.feedbackItem = `Produto adicionado: ${produtoNorm.descricao}`;
          clearTimeout(this._feedbackTimer);
          this._feedbackTimer = setTimeout(() => {
            this.feedbackItem = "";
          }, 3000);
          if (ehLeituraCodigoBarras) this._finalizarLeituraCodigoBarras();
          else {
            this.limparEntradaProduto();
            this.$nextTick(() => {
              this.$refs.searchInput && this.$refs.searchInput.focus();
            });
          }
          return true;
        }

        // Múltiplos lotes: preenche campos e abre modal
        this.produtoSelecionado = produtoNorm;
        this.quantidadeItem = quantidade;
        this.valorUnitarioItem = valorUnitario;
        this.modalLote.produto = produtoNorm;
        this.modalLote.lotes = lotes;
        this.modalLote.visivel = true;
        return true;
      }

      // Produto sem lote: adiciona direto
      this.codigoBalancaAtual = null;
      this._adicionarAoCarrinho(produtoNorm, quantidade, valorUnitario);
      this.feedbackItem = `Produto adicionado: ${produtoNorm.descricao}`;
      clearTimeout(this._feedbackTimer);
      this._feedbackTimer = setTimeout(() => {
        this.feedbackItem = "";
      }, 3000);
      if (ehLeituraCodigoBarras) this._finalizarLeituraCodigoBarras();
      else {
        this.limparEntradaProduto();
        this.$nextTick(() => {
          this.$refs.searchInput && this.$refs.searchInput.focus();
        });
      }
      return true;
    },

    // ── Preço de atacado ────────────────────────────────────────────────
    // Retorna o preço correto considerando a regra de atacado:
    //   - preco_atacado > 0 e sem quantidade_atacado → sempre usa atacado
    //   - preco_atacado > 0 e quantidade_atacado > 0 → usa atacado se qty >= qAtacado
    //   - caso contrário → preco normal
    obterPrecoProduto(produto, quantidade = 1) {
      const precoNormal  = Number(produto?.preco          || 0);
      const precoAtacado = Number(produto?.preco_atacado  || 0);
      const qAtacado     = Number(produto?.quantidade_atacado || 0);

      if (precoAtacado > 0) {
        if (!qAtacado || qAtacado <= 0) return precoAtacado;
        if (Number(quantidade || 1) >= qAtacado) return precoAtacado;
      }

      return precoNormal;
    },

    // Retorna a quantidade da entrada (1 se não informada)
    getQuantidadeEntrada() {
      const quantidade = Number(this.quantidadeItem || 0);
      return quantidade > 0 ? quantidade : 1;
    },

    // Retorna o valor unitário da entrada ou o preço calculado do produto
    getValorUnitarioEntrada(produto) {
      if (this.valorUnitarioAlteradoManual) {
        const valor = Number(this.valorUnitarioItem || 0);
        return valor > 0 ? valor : this.obterPrecoProduto(produto, this.getQuantidadeEntrada());
      }
      return this.obterPrecoProduto(produto, this.getQuantidadeEntrada());
    },

    // Limpa todos os campos da entrada de produto
    limparEntradaProduto() {
      this.termoBusca = "";
      this.produtoSelecionado = null;
      this.produtosBusca = [];
      this.selectedProductIndex = 0;
      this.quantidadeItem = 1;
      this.valorUnitarioItem = 0;
      this.valorTotalItem = 0;
      this.erroItem = "";
      this.codigoBalancaAtual = null;
      this.valorUnitarioAlteradoManual = false;
      this._cancelarEsperaBalanca();
    },

    _finalizarLeituraCodigoBarras() {
      this.limparEntradaProduto();
      this.limparBuscaProduto();
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
          this.$refs.searchInput.select();
        }
      });
    },

    /**
     * Processa um possível código de balança (13 dígitos).
     * - Extrai o código do produto (pos 0-6) e o peso em gramas (pos 7-11).
     * - Busca o produto pelo código base.
     * - Se o produto for pesado: insere automaticamente no carrinho e retorna true.
     * - Se o produto existir mas não for pesado: exibe aviso, seleciona normalmente e retorna true.
     * - Se não encontrar produto pelo código base: retorna false (cai para busca normal).
     */
    async _processarCodigoBalanca(termo) {
      if (!/^\d{13}$/.test(termo)) return false;

      const base = termo.substring(0, 7);
      const pesoEmGramas = parseInt(termo.substring(7, 12), 10);
      if (isNaN(pesoEmGramas) || pesoEmGramas <= 0) return false;

      const pesoEmKg = pesoEmGramas / 1000;

      // Busca produto pelo código base (cod_barras ou codigo)
      let produto = null;
      try {
        produto =
          (await ProdutoLocalRepository.buscarPorCodigoBarras(base)) ??
          (await ProdutoLocalRepository.buscarExato(base));
      } catch (_) { /* produto não encontrado */ }

      if (!produto) return false;

      const produtoNorm = this._normalizarProduto(produto);

      // Código EAN-13 comum não deve ficar preso na rotina de balança.
      // Se o produto não for pesado, deixa o fluxo seguir para busca exata por código.
      if (!this.produtoEhPesado(produtoNorm)) {
        return false;
      }

      // Determina quantidade conforme unidade de medida
      const unidade =
        produtoNorm?.unidade_medida?.sigla ??
        produtoNorm?.unidade_medida_sigla ??
        produtoNorm?.unidade ??
        produtoNorm?.sigla_unidade;
      const quantidade = unidade === "KG" ? pesoEmKg : 1;
      const valorUnitario = this.obterPrecoProduto(produtoNorm, quantidade);

      // Insere no carrinho com o código da balança
      this.codigoBalancaAtual = termo;
      this._adicionarAoCarrinho(produtoNorm, quantidade, valorUnitario);
      this.codigoBalancaAtual = null;

      // Feedback visual (auto-limpa em 3 s)
      const qtdStr = quantidade.toLocaleString("pt-BR", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      });
      this.feedbackItem = `Adicionado: ${produtoNorm.descricao} – ${qtdStr} KG`;
      clearTimeout(this._feedbackTimer);
      this._feedbackTimer = setTimeout(() => { this.feedbackItem = ""; }, 3000);

      // Limpa campos e refoca no campo de busca
      this._finalizarLeituraCodigoBarras();

      return true;
    },

    // Busca produto pelo código base de um código de balança — usado apenas como helper interno.
    async _tentarProdutoBalanca(termo) {
      if (!/^\d{13}$/.test(termo)) return null;
      const base = termo.substring(0, 7);
      try {
        const porBarras = await ProdutoLocalRepository.buscarPorCodigoBarras(base);
        if (porBarras) return porBarras;
        const porExato = await ProdutoLocalRepository.buscarExato(base);
        return porExato ?? null;
      } catch (_) {
        return null;
      }
    },

    _normalizarProduto(produto) {
      const id = produto.id_produto || produto.id || null;
      const precoBase = Number(produto.valor_venda || produto.preco || produto.valor || 0);
      // Usa preco_lista se o cliente tiver lista e o produto estiver nela
      const precoLista = id ? (this.listaPrecoMap[id] ?? null) : null;
      return {
        ...produto,
        id_produto: id,
        preco: precoLista !== null ? precoLista : precoBase,
        preco_base: precoBase,
        preco_lista: precoLista,
        descricao: produto.descricao || "",
        codigo: produto.codigo || "",
        cod_barras: produto.cod_barras || "",
      };
    },

    // Retorna true e sinaliza erro quando o produto não pode ser lançado por
    // ter todo o estoque comprometido em reserva condicional (disponível <= 0).
    _bloqueadoPorReservaCondicional(produto) {
      const reservada =
        parseFloat(produto?.quantidade_reservada_condicional) || 0;
      const quantidade = parseFloat(produto?.quantidade) || 0;
      const disponivel = quantidade - reservada;

      if (reservada > 0 && disponivel <= 0) {
        this.erroItem =
          "Este produto possui quantidades reservadas em condicional e não pode ser lançado na venda.";
        return true;
      }

      return false;
    },

    // Retorna true quando o produto é vendido por peso (campo pesado)
    produtoEhPesado(produto) {
      const v = produto?.pesado;
      return v === true || v === 1 || v === "1" || v === "true";
    },

    // ── Balança de checkout ───────────────────────────────────────────
    async _inicializarBalancaCheckout() {
      try {
        this.balancaConfig = await BalancaCheckoutService.carregarConfiguracao();
        if (!this.balancaConfig?.habilitada || !this.balancaConfig?.portaSerial) return;

        this._removerListenerBalanca = BalancaCheckoutService.onEvento(
          (payload) => this._onEventoBalanca(payload)
        );
        await BalancaCheckoutService.conectar(this.balancaConfig);
      } catch (e) {
        this.balancaConectada = false;
        this.balancaErro = e?.message || "Balança indisponível.";
      }
    },

    _produtoUsaBalancaCheckout(produto) {
      return Boolean(
        this.balancaConfig?.habilitada &&
        this.balancaConfig?.portaSerial &&
        this.produtoEhPesado(produto)
      );
    },

    _onEventoBalanca(payload = {}) {
      if (payload.tipo === "conexao") {
        this.balancaConectada = !!payload.conectado;
        if (payload.conectado) this.balancaErro = "";
        return;
      }

      if (payload.tipo === "estado") {
        this.balancaConectada = !!payload.conectado;
        this.balancaEstado = String(payload.estado || "idle").toLowerCase();
        if (this.balancaEstado === "idle") {
          this.balancaPesoTempoReal = 0;
          this.balancaPesoProcessado = false;
        }
        return;
      }

      if (payload.tipo === "peso_tempo_real") {
        this.balancaConectada = true;
        this.balancaEstado = String(payload.estado || "pesando").toLowerCase();
        if (Number.isFinite(Number(payload.peso))) {
          this.balancaPesoTempoReal = Number(payload.peso);
        }
        return;
      }

      if (payload.tipo === "peso_estavel") {
        this.balancaConectada = true;
        this.balancaEstado = "estavel";
        this.balancaPesoTempoReal = Number(payload.peso) || 0;
        this._aplicarPesoBalanca(payload.peso);
        return;
      }

      if (payload.tipo === "erro") {
        this.balancaErro = payload.mensagem || "Falha de comunicação com a balança.";
      }
    },

    _aguardarPesoBalanca() {
      const esperaId = ++this.balancaEsperaId;
      this.aguardandoPesoBalanca = true;
      this.balancaPesoProcessado = false;
      this.balancaPesoTempoReal = 0;
      this.balancaEstado = "idle";
      this.balancaErro = "";
      this.quantidadeItem = 0;
      this.codigoBalancaAtual = null;
      this._recalcularTotalItem();

      // Se o produto já estiver sobre a balança, a leitura serial permite
      // capturar o valor atual sem exigir que ele seja retirado e recolocado.
      BalancaCheckoutService.lerPeso(this.balancaConfig)
        .then((amostra) => {
          if (amostra?.status === "estavel" && Number(amostra.peso) > 0) {
            this._aplicarPesoBalanca(amostra.peso, esperaId);
          }
        })
        .catch((e) => {
          if (esperaId !== this.balancaEsperaId) return;
          this.balancaErro = e?.message || "Balança não encontrada.";
          this.balancaConectada = false;
        });
    },

    _cancelarEsperaBalanca() {
      this.balancaEsperaId += 1;
      this.aguardandoPesoBalanca = false;
      this.balancaPesoProcessado = false;
      this.balancaAplicandoPeso = false;
      this.balancaPesoTempoReal = 0;
      this.balancaEstado = "idle";
      this.balancaErro = "";
    },

    async _aplicarPesoBalanca(peso, esperaId = null) {
      const quantidade = Number(peso);
      if (
        (esperaId !== null && esperaId !== this.balancaEsperaId) ||
        !this.aguardandoPesoBalanca ||
        !this.produtoSelecionado ||
        this.balancaPesoProcessado ||
        this.balancaAplicandoPeso ||
        !Number.isFinite(quantidade) ||
        quantidade <= 0
      ) return false;

      const produto = this.produtoSelecionado;
      const descricao = produto.descricao || "Produto";
      this.balancaPesoProcessado = true;
      this.balancaAplicandoPeso = true;
      this.aguardandoPesoBalanca = false;
      this.quantidadeItem = parseFloat(quantidade.toFixed(4));
      this.valorUnitarioItem = this.obterPrecoProduto(produto, this.quantidadeItem);
      this._recalcularTotalItem();

      try {
        await this.$nextTick();
        await this.inserirNoCarrinho();
        const qtd = quantidade.toLocaleString("pt-BR", {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        });
        this.feedbackItem = `Peso capturado: ${qtd} kg • ${descricao}`;
        clearTimeout(this._feedbackTimer);
        this._feedbackTimer = setTimeout(() => { this.feedbackItem = ""; }, 4500);
        return true;
      } finally {
        this.balancaAplicandoPeso = false;
      }
    },

    async capturarPesoBalancaManual() {
      if (!this.aguardandoPesoBalanca || this.balancaCapturando) return;
      this.balancaCapturando = true;
      this.balancaErro = "";
      try {
        const amostra = await BalancaCheckoutService.lerPeso(this.balancaConfig);
        if (!amostra || amostra.status !== "estavel" || Number(amostra.peso) <= 0) {
          throw new Error("Peso instável ou zerado. Aguarde a balança estabilizar.");
        }
        await this._aplicarPesoBalanca(amostra.peso);
      } catch (e) {
        this.balancaErro = e?.message || "Não foi possível ler o peso.";
      } finally {
        this.balancaCapturando = false;
      }
    },

    // Extrai quantidade em KG a partir de um código de barras de balança (13 dígitos).
    // Posições 7-11 (substring 7,12) contêm o peso em gramas.
    // Retorna 1 quando o produto não é pesado, o código não tem 13 dígitos ou a UM não é KG.
    extrairPesoCodigoBalanca(codigoBarras, produto) {
      if (!this.produtoEhPesado(produto)) return 1;

      if (!codigoBarras || String(codigoBarras).length !== 13) return 1;

      const unidade =
        produto?.unidade_medida?.sigla ??
        produto?.unidade_medida_sigla ??
        produto?.unidade ??
        produto?.sigla_unidade;

      if (unidade !== "KG") return 1;

      const pesoEmGramas = parseInt(String(codigoBarras).substring(7, 12), 10);
      if (!pesoEmGramas || isNaN(pesoEmGramas)) return 1;

      return pesoEmGramas / 1000;
    },

    // Preenche os campos de entrada com os dados do produto selecionado
    selecionarProduto(produtoBruto) {
      const produto = this._normalizarProduto(produtoBruto);

      if (this._bloqueadoPorReservaCondicional(produto)) return;

      // Captura o código bipado ANTES de sobrescrever termoBusca
      const codigoBipado = (this.termoBusca || "").trim();
      const quantidade = this.extrairPesoCodigoBalanca(codigoBipado, produto);

      this.valorUnitarioAlteradoManual = false;
      this.produtoSelecionado = produto;
      this.codigoBalancaAtual = this.produtoEhPesado(produto) ? codigoBipado : null;
      this.termoBusca = produto.descricao;
      this.produtosBusca = [];
      this.selectedProductIndex = 0;
      this.quantidadeItem = quantidade;
      this.valorUnitarioItem = this.obterPrecoProduto(produto, quantidade);
      this._recalcularTotalItem();
      this.erroItem = "";
      if (this._produtoUsaBalancaCheckout(produto)) {
        this._aguardarPesoBalanca(produto);
        return;
      }
      this._cancelarEsperaBalanca();
      this.$nextTick(() => {
        this.$refs.qtyInput && this.$refs.qtyInput.focus();
        this.$refs.qtyInput && this.$refs.qtyInput.select();
      });
    },

    // Recalcula total: atualiza preço automaticamente se qty mudou e não há edição manual
    _onQtyInput() {
      if (this.produtoSelecionado && !this.valorUnitarioAlteradoManual) {
        this.valorUnitarioItem = this.obterPrecoProduto(
          this.produtoSelecionado,
          Number(this.quantidadeItem) || 1
        );
      }
      this._recalcularTotalItem();
    },

    // Marca que o operador editou manualmente o preço unitário
    _onValorUnitarioManual() {
      this.valorUnitarioAlteradoManual = true;
      this._recalcularTotalItem();
    },

    // Recalcula o total do item de entrada
    _recalcularTotalItem() {
      const qty = Number(this.quantidadeItem) || 0;
      const unit = Number(this.valorUnitarioItem) || 0;
      this.valorTotalItem = parseFloat((qty * unit).toFixed(2));
    },

    // Limpa os campos de entrada de produto
    _limparCamposItem() {
      this.produtoSelecionado = null;
      this.quantidadeItem = 1;
      this.valorUnitarioItem = 0;
      this.valorTotalItem = 0;
      this.erroItem = "";
      this.codigoBalancaAtual = null;
      this.valorUnitarioAlteradoManual = false;
      this._cancelarEsperaBalanca();
    },

    // Valida e insere o produto selecionado no carrinho
    async inserirNoCarrinho() {
      if (this.aguardandoPesoBalanca && !this.balancaAplicandoPeso) {
        this.erroItem = "Aguarde o peso estabilizar na balança.";
        return false;
      }
      if (!this.produtoSelecionado) {
        this.erroItem = "Selecione um produto antes de inserir.";
        this.$refs.searchInput && this.$refs.searchInput.focus();
        return;
      }
      const qty = Number(this.quantidadeItem);
      const unit = Number(this.valorUnitarioItem);
      if (qty <= 0) {
        this.erroItem = "Informe uma quantidade válida.";
        this.$refs.qtyInput && this.$refs.qtyInput.focus();
        return;
      }
      if (unit < 0) {
        this.erroItem = "Informe um valor unitário válido.";
        this.$refs.precoInput && this.$refs.precoInput.focus();
        return;
      }
      this.erroItem = "";

      const produto = this.produtoSelecionado;

      // Produto com controle de combinação?
      if (await this._produtoControlaCombinacoesAsync(produto)) {
        let combinacoes = [];
        try {
          combinacoes = (await ProdutoCombinacaoLocalRepository.buscarCombinacoesPorProduto(this.getIdProduto(produto))) || [];
        } catch (e) {
          console.error("[NovaVenda] Erro ao buscar combinações:", e);
        }
        if (combinacoes.length === 0) {
          this.erroItem = "Nenhuma combinação cadastrada para este produto.";
          return;
        }
        this.modalCombinacao.produto     = produto;
        this.modalCombinacao.combinacoes = combinacoes;
        this.modalCombinacao.combinacaoConfirmada = null;
        this.modalCombinacao.visivel     = true;
        return;
      }

      // Produto com controle de lote?
      if (this._produtoControlaLote(produto)) {
        let lotes = [];
        try {
          lotes = (await ProdutoLoteLocalRepository.buscarLotesDisponiveisPorProduto(produto.id_produto)) || [];
        } catch (e) {
          console.error("[NovaVenda] Erro ao buscar lotes:", e);
        }

        if (lotes.length === 0) {
          this.erroItem = "Nenhum lote disponível em estoque para este produto.";
          return;
        }

        if (lotes.length === 1) {
          // Auto-seleciona lote único
          await this._adicionarAoCarrinhoComLote(produto, qty, unit, lotes[0], qty);
          this._limparCamposItem();
          this.limparBuscaProduto();
          this.$nextTick(() => { this.$refs.searchInput && this.$refs.searchInput.focus(); });
          return;
        }

        // Múltiplos lotes — abre modal
        this.modalLote.produto = produto;
        this.modalLote.lotes   = lotes;
        this.modalLote.visivel = true;
        return;
      }

      // Produto sem lote — comportamento original
      this._adicionarAoCarrinho(produto, qty, unit);
      this._limparCamposItem();
      this.limparBuscaProduto();
      this.$nextTick(() => {
        this.$refs.searchInput && this.$refs.searchInput.focus();
      });
    },

    // Insere produto no carrinho com quantidade e valor unitário informados.
    // Agrupamento: mesmo produto + mesmo lote + mesma combinação + mesmo preço → soma quantidade.
    // Preço diferente, lote diferente ou combinação diferente → nova linha no carrinho.
    _adicionarAoCarrinho(produto, qty, unit, loteData = null, combinacaoData = null) {
      const produtoId = produto.id_produto;
      const qtdNum = Number(qty) || 1;
      const unitNum = Number(unit) || 0;
      const idLote = loteData ? loteData.id_produto_lote : null;
      const idCombinacao = combinacaoData ? combinacaoData.id_produto_combinacao : null;

      // Determina se o preço aplicado é de atacado
      const precoAtacadoConfig = Number(produto.preco_atacado || 0);
      const precoNormalConfig  = Number(produto.preco || 0);
      const tipoPrecoAplicado  =
        precoAtacadoConfig > 0 && Math.abs(unitNum - precoAtacadoConfig) < 0.001
          ? "atacado"
          : "normal";

      const itemExistente = this.carrinho.find((item) => {
        const itemId = item.id_produto || item.id;
        const itemLoteId = item.id_produto_lote ?? null;
        const itemCombId = item.id_produto_combinacao ?? null;
        return (
          produtoId &&
          Number(itemId) === Number(produtoId) &&
          itemLoteId === idLote &&
          itemCombId === idCombinacao &&
          parseFloat(Number(item.valor_unitario).toFixed(4)) === parseFloat(Number(unitNum).toFixed(4))
        );
      });

      if (itemExistente) {
        itemExistente.quantidade = parseFloat(
          (Number(itemExistente.quantidade) + qtdNum).toFixed(4)
        );
        this._recalcularTotaisItem(itemExistente);
      } else {
        const totalBrutoNovo = parseFloat((qtdNum * unitNum).toFixed(2));
        this.carrinho.push({
          id_produto:              produtoId,
          codigo:                  produto.codigo,
          descricao:               produto.descricao,
          cod_barras:              produto.cod_barras,
          imagem:                  produto.imagem ?? null,
          quantidade:              qtdNum,
          valor_unitario:          unitNum,
          // ── Ajustes por item (desconto/acréscimo) ───────────────────────
          ajuste_tipo:             null,
          ajuste_forma:            null,
          desconto:                0,
          acrescimo:               0,
          desconto_valor:          0,
          desconto_percentual:     0,
          acrescimo_valor:         0,
          acrescimo_percentual:    0,
          total_bruto:             totalBrutoNovo,
          total_liquido:           totalBrutoNovo,
          total:                   totalBrutoNovo,
          produto_original:        produto,
          // campos de lote (null para produtos sem lote)
          id_produto_lote:         idLote,
          numero_lote:             loteData ? loteData.numero_lote     ?? null : null,
          data_validade:           loteData ? loteData.data_validade   ?? null : null,
          data_fabricacao:         loteData ? loteData.data_fabricacao ?? null : null,
          lote_original:           loteData ?? null,
          // campos de combinação (null para produtos sem combinação)
          id_produto_combinacao:   idCombinacao,
          combinacao:              combinacaoData ? combinacaoData.combinacao ?? null : null,
          descricao_combinacao:    combinacaoData ? combinacaoData.descricao_combinacao ?? null : null,
          combinacao_original:     combinacaoData ?? null,
          // campos de produto pesado / balança
          pesado:                  this.produtoEhPesado(produto),
          codigo_balanca:          this.codigoBalancaAtual ?? null,
          peso_liquido:            this.produtoEhPesado(produto) ? qtdNum : null,
          // preço de atacado
          tipo_preco_aplicado:     tipoPrecoAplicado,
          preco_normal:            precoNormalConfig,
          preco_atacado_config:    precoAtacadoConfig,
          quantidade_atacado_config: Number(produto.quantidade_atacado || 0),
          // payload_json inclui produto + lote + combinação para rastreabilidade no XML
          payload_json:            JSON.stringify({ produto, lote: loteData ?? null, combinacao: combinacaoData ?? null }),
        });
        if (loteData) {
          console.log('[CARRINHO] item com lote:', this.carrinho[this.carrinho.length - 1]);
        }
        if (combinacaoData) {
          console.log('[CARRINHO] item com combinação:', this.carrinho[this.carrinho.length - 1]);
        }
      }
    },

    // Adiciona ao carrinho com dados de lote e deduz estoque local
    async _adicionarAoCarrinhoComLote(produto, qty, unit, lote, qtdBaixar, combinacaoData = null) {
      this._adicionarAoCarrinho(produto, qty, unit, lote, combinacaoData);
      try {
        await ProdutoLoteLocalRepository.baixarQuantidadeLocal(lote.id_produto_lote, qtdBaixar);
      } catch (e) {
        console.error("[NovaVenda] Erro ao baixar quantidade do lote:", e);
      }
    },

    // Confirma seleção de lote no modal
    async onConfirmarLote(lote, qtd) {
      this.modalLote.visivel = false;
      const produto = this.modalLote.produto;
      if (!produto) return;

      const unit = this.valorUnitarioAlteradoManual
        ? Number(this.valorUnitarioItem)
        : this.obterPrecoProduto(produto, Number(qtd) || 1);
      const combinacaoData = this.modalCombinacao.combinacaoConfirmada ?? null;

      console.log('[LOTE] lote selecionado:', lote);
      console.log('[LOTE] quantidade selecionada:', qtd);

      // qtd é a quantidade confirmada no modal — usada tanto no carrinho como na baixa do estoque
      await this._adicionarAoCarrinhoComLote(produto, qtd, unit, lote, qtd, combinacaoData);
      this.modalCombinacao.combinacaoConfirmada = null;
      this._limparCamposItem();
      this.limparBuscaProduto();
      this.$nextTick(() => { this.$refs.searchInput && this.$refs.searchInput.focus(); });
    },

    onFecharModalLote() {
      this.modalLote.visivel = false;
      this.modalLote.produto = null;
      this.modalLote.lotes   = [];
    },

    // Retorna true se o produto controla combinações (verificação assíncrona no banco local)
    // Retorna o id canônico do produto (id_produto tem prioridade)
    getIdProduto(produto) {
      return produto?.id_produto ?? produto?.id ?? null;
    },

    async _produtoControlaCombinacoesAsync(produto) {
      if (!produto) return false;
      const idProduto = this.getIdProduto(produto);
      if (!idProduto) return false;

      console.log("[PDV_COMBINACAO] produto:", idProduto, produto.descricao);

      try {
        // 1. Verificação rápida no banco local
        const possuiLocal = await ProdutoCombinacaoLocalRepository.produtoPossuiCombinacoes(idProduto);
        if (possuiLocal) return true;

        // 2. Se o produto está marcado como variável mas não há combinações locais,
        //    tenta recuperar do payload embutido no próprio objeto (produto_original ou payload_json)
        const ehVariavel =
          produto.variavel === true ||
          produto.variavel === 1 ||
          produto.variavel === "1";

        if (ehVariavel) {
          // Tenta extrair combinações de diferentes fontes
          let combinacoesRecuperadas =
            (Array.isArray(produto.combinacoes)         ? produto.combinacoes         : null) ??
            (Array.isArray(produto.produto_combinacoes) ? produto.produto_combinacoes : null) ??
            (Array.isArray(produto.variacoes)           ? produto.variacoes           : null) ??
            [];

          if (!combinacoesRecuperadas.length) {
            // Tenta no payload_json salvo localmente
            try {
              const payloadStr = produto.payload_json ?? produto.produto_original?.payload_json ?? null;
              if (payloadStr) {
                const payloadObj = typeof payloadStr === "string" ? JSON.parse(payloadStr) : payloadStr;
                combinacoesRecuperadas =
                  (Array.isArray(payloadObj.combinacoes)         ? payloadObj.combinacoes         : null) ??
                  (Array.isArray(payloadObj.produto_combinacoes) ? payloadObj.produto_combinacoes : null) ??
                  (Array.isArray(payloadObj.variacoes)           ? payloadObj.variacoes           : null) ??
                  [];
              }
            } catch (_) { /**/ }
          }

          if (combinacoesRecuperadas.length > 0) {
            const api = getElectronAPI ? getElectronAPI() : null;
            const id_empresa = api ? (await api.config.get("id_empresa")) : null;
            const normalizadas = combinacoesRecuperadas.map((c) => ({
              ...c,
              id_produto: c.id_produto ?? idProduto,
              id_empresa: c.id_empresa ?? (id_empresa ? parseInt(id_empresa, 10) : null),
            }));
            console.log("[PDV_COMBINACAO] recuperando combinações do payload:", normalizadas.length);
            await ProdutoCombinacaoLocalRepository.salvarCombinacoes(normalizadas);
            return true;
          }

          // Produto variável mas sem dados locais nem no payload — reporta como controlável
          // para que o fluxo mostre o erro adequado ao operador
          console.warn("[PDV_COMBINACAO] produto variável sem combinações locais:", idProduto);
          return true;
        }

        const combinacoesLocais = await ProdutoCombinacaoLocalRepository.buscarCombinacoesPorProduto(idProduto);
        console.log("[PDV_COMBINACAO] combinacoes locais:", combinacoesLocais.length);
        return combinacoesLocais.length > 0;
      } catch (e) {
        console.error("[NovaVenda] Erro ao verificar combinações:", e);
        return false;
      }
    },

    // Confirma seleção de combinação no modal
    async onConfirmarCombinacao({ combinacao, quantidade } = {}) {
      this.modalCombinacao.visivel = false;
      this.modalCombinacao.combinacaoConfirmada = combinacao;

      const produto = this.modalCombinacao.produto;
      if (!produto) return;

      const qty  = Number(quantidade) || Number(this.quantidadeItem) || 1;
      const unit = this.valorUnitarioAlteradoManual
        ? (Number(this.valorUnitarioItem) || 0)
        : this.obterPrecoProduto(produto, qty);

      // Após combinação, verificar lote
      if (this._produtoControlaLote(produto)) {
        let lotes = [];
        try {
          lotes = (await ProdutoLoteLocalRepository.buscarLotesDisponiveisPorProduto(produto.id_produto)) || [];
        } catch (e) {
          console.error("[NovaVenda] Erro ao buscar lotes:", e);
        }

        if (lotes.length === 0) {
          this.erroItem = "Nenhum lote disponível em estoque para este produto.";
          this.modalCombinacao.combinacaoConfirmada = null;
          return;
        }

        if (lotes.length === 1) {
          await this._adicionarAoCarrinhoComLote(produto, qty, unit, lotes[0], qty, combinacao);
          this.modalCombinacao.combinacaoConfirmada = null;
          this._limparCamposItem();
          this.limparBuscaProduto();
          this.$nextTick(() => { this.$refs.searchInput && this.$refs.searchInput.focus(); });
          return;
        }

        // Múltiplos lotes — abre modal de lote (combinação já está em combinacaoConfirmada)
        this.modalLote.produto = produto;
        this.modalLote.lotes   = lotes;
        this.modalLote.visivel = true;
        return;
      }

      // Sem lote — adiciona direto com combinação
      this._adicionarAoCarrinho(produto, qty, unit, null, combinacao);
      this.modalCombinacao.combinacaoConfirmada = null;
      this._limparCamposItem();
      this.limparBuscaProduto();
      this.$nextTick(() => { this.$refs.searchInput && this.$refs.searchInput.focus(); });
    },

    onFecharModalCombinacao() {
      this.modalCombinacao.visivel = false;
      this.modalCombinacao.produto = null;
      this.modalCombinacao.combinacoes = [];
      this.modalCombinacao.combinacaoConfirmada = null;
    },

    // Retorna true se o produto controla lote
    _produtoControlaLote(produto) {
      const v = produto.lote ?? produto.controla_lote ?? null;
      return v === true || v === 1 || v === "1" || v === "true";
    },

    recalcularItem(idx) {
      const item = this.carrinho[idx];
      if (!item) return;

      const qtdAnterior = item._qtd_antes_recalculo ?? item.quantidade;
      const qtdNova = Number(item.quantidade) || 0;

      // Reavalia preço de atacado se a quantidade mudar e o produto tiver regra de atacado
      if (item.preco_atacado_config > 0 && item.quantidade_atacado_config > 0) {
        const novoPreco = this.obterPrecoProduto(
          {
            preco:              item.preco_normal,
            preco_atacado:      item.preco_atacado_config,
            quantidade_atacado: item.quantidade_atacado_config,
          },
          qtdNova
        );
        if (Math.abs(novoPreco - item.valor_unitario) > 0.001) {
          item.valor_unitario      = novoPreco;
          item.tipo_preco_aplicado = novoPreco === item.preco_atacado_config ? "atacado" : "normal";
        }
      }

      this._recalcularTotaisItem(item);

      // Ajusta estoque do lote pela diferença
      if (item.id_produto_lote) {
        const diff = qtdNova - Number(qtdAnterior);
        if (diff > 0) {
          ProdutoLoteLocalRepository.baixarQuantidadeLocal(item.id_produto_lote, diff)
            .catch((e) => console.error("[NovaVenda] Erro ao baixar lote (recalcular):", e));
        } else if (diff < 0) {
          ProdutoLoteLocalRepository.devolverQuantidadeLocal(item.id_produto_lote, Math.abs(diff))
            .catch((e) => console.error("[NovaVenda] Erro ao devolver lote (recalcular):", e));
        }
      }

      delete item._qtd_antes_recalculo;
    },

    /**
     * Calcula total_bruto/desconto_valor/acrescimo_valor/total_liquido do item
     * a partir de quantidade, valor_unitario e ajuste (tipo + forma + percentual/valor).
     * Quando o ajuste é percentual, recomputa o valor com base no total bruto atual.
     * Mantém os campos legados (desconto, acrescimo, total) em sincronia.
     */
    _recalcularTotaisItem(item) {
      if (!item) return;
      const qtd  = Number(item.quantidade)     || 0;
      const unit = Number(item.valor_unitario) || 0;
      const round = (n) => Math.round(Number(n || 0) * 100) / 100;

      const totalBruto = round(qtd * unit);
      item.total_bruto = totalBruto;

      let descontoValor  = 0;
      let acrescimoValor = 0;

      if (item.ajuste_tipo === "desconto") {
        if (item.ajuste_forma === "percentual") {
          const pct = Number(item.desconto_percentual) || 0;
          descontoValor = round((totalBruto * pct) / 100);
        } else {
          descontoValor = round(Number(item.desconto_valor) || 0);
        }
        // Trava: desconto não pode exceder o total bruto
        if (descontoValor > totalBruto) descontoValor = totalBruto;
      } else if (item.ajuste_tipo === "acrescimo") {
        if (item.ajuste_forma === "percentual") {
          const pct = Number(item.acrescimo_percentual) || 0;
          acrescimoValor = round((totalBruto * pct) / 100);
        } else {
          acrescimoValor = round(Number(item.acrescimo_valor) || 0);
        }
        if (acrescimoValor < 0) acrescimoValor = 0;
      }

      item.desconto_valor  = descontoValor;
      item.acrescimo_valor = acrescimoValor;
      // Recalcula percentuais para refletir a forma autoritativa
      item.desconto_percentual = item.ajuste_tipo === "desconto" && item.ajuste_forma === "percentual"
        ? Number(item.desconto_percentual) || 0
        : (totalBruto > 0 ? round((descontoValor / totalBruto) * 100) : 0);
      item.acrescimo_percentual = item.ajuste_tipo === "acrescimo" && item.ajuste_forma === "percentual"
        ? Number(item.acrescimo_percentual) || 0
        : (totalBruto > 0 ? round((acrescimoValor / totalBruto) * 100) : 0);

      const totalLiquido = round(totalBruto - descontoValor + acrescimoValor);
      item.total_liquido = totalLiquido;

      // Campos legados consumidos por serviços/XML/repositories
      item.desconto  = descontoValor;
      item.acrescimo = acrescimoValor;
      item.total     = totalLiquido;
    },

    incrementarQtd(idx) {
      const item = this.carrinho[idx];
      item._qtd_antes_recalculo = Number(item.quantidade);
      item.quantidade = Number(item.quantidade) + 1;
      this.recalcularItem(idx);
    },

    decrementarQtd(idx) {
      const item = this.carrinho[idx];
      const qty = Number(item.quantidade);
      if (qty <= 1) {
        this.removerItem(idx);
      } else {
        item._qtd_antes_recalculo = qty;
        item.quantidade = qty - 1;
        this.recalcularItem(idx);
      }
    },

    removerItem(idx) {
      const item = this.carrinho[idx];
      if (item && item.id_produto_lote) {
        ProdutoLoteLocalRepository.devolverQuantidadeLocal(item.id_produto_lote, item.quantidade)
          .catch((e) => console.error("[NovaVenda] Erro ao devolver lote:", e));
      }
      this.carrinho.splice(idx, 1);
      if (this.itemSelecionadoIdx === idx) {
        this.itemSelecionadoIdx = -1;
      } else if (this.itemSelecionadoIdx > idx) {
        this.itemSelecionadoIdx -= 1;
      }
    },

    selecionarItemCarrinho(idx) {
      if (idx < 0 || idx >= this.carrinho.length) return;
      this.itemSelecionadoIdx = this.itemSelecionadoIdx === idx ? -1 : idx;
    },

    // ── Desconto / Acréscimo por item ─────────────────────────────
    abrirDescontoAcrescimoItem(idx) {
      if (idx < 0 || idx >= this.carrinho.length) return;
      this.itemSelecionadoIdx = idx;
      this.modalItemDescAcres = {
        visivel: true,
        item:    this.carrinho[idx],
        itemIdx: idx,
      };
    },

    _atalhoDescontoItem() {
      if (!this.carrinho.length) {
        this.erroItem = "Selecione um item para aplicar desconto ou acréscimo.";
        return;
      }
      let idx = this.itemSelecionadoIdx;
      if (idx < 0 || idx >= this.carrinho.length) {
        // Só 1 item no carrinho: assume seleção automática
        if (this.carrinho.length === 1) {
          idx = 0;
          this.itemSelecionadoIdx = 0;
        } else {
          this.erroItem = "Selecione um item para aplicar desconto ou acréscimo.";
          clearTimeout(this._feedbackTimer);
          this._feedbackTimer = setTimeout(() => { this.erroItem = ""; }, 3500);
          return;
        }
      }
      this.abrirDescontoAcrescimoItem(idx);
    },

    onAplicarDescontoAcrescimoItem(dados) {
      const idx  = this.modalItemDescAcres.itemIdx;
      const item = this.carrinho[idx];
      if (!item) {
        this.modalItemDescAcres.visivel = false;
        return;
      }

      item.ajuste_tipo          = dados.ajuste_tipo;
      item.ajuste_forma         = dados.ajuste_forma;
      item.desconto_valor       = Number(dados.desconto_valor)       || 0;
      item.desconto_percentual  = Number(dados.desconto_percentual)  || 0;
      item.acrescimo_valor      = Number(dados.acrescimo_valor)      || 0;
      item.acrescimo_percentual = Number(dados.acrescimo_percentual) || 0;

      this._recalcularTotaisItem(item);

      // Atualiza o valor do pagamento corrente para refletir o novo total
      this.valorPagamentoAtual = this.valorRestante;

      this.modalItemDescAcres.visivel = false;
    },

    _itemTemDesconto(item) {
      return Number(item && (item.desconto_valor ?? item.desconto) || 0) > 0;
    },

    _itemTemAcrescimo(item) {
      return Number(item && (item.acrescimo_valor ?? item.acrescimo) || 0) > 0;
    },

    _totalBrutoItem(item) {
      const tb = Number(item && item.total_bruto) || 0;
      if (tb > 0) return tb;
      const q = Number(item && item.quantidade)     || 0;
      const u = Number(item && item.valor_unitario) || 0;
      return Math.round(q * u * 100) / 100;
    },

    _badgeAjuste(item, tipo) {
      if (!item) return "";
      const forma = item.ajuste_forma;
      if (tipo === "desconto") {
        if (forma === "percentual" && Number(item.desconto_percentual) > 0) {
          return `${this._fmtNum(item.desconto_percentual)}%`;
        }
        return this.formatBRL(item.desconto_valor ?? item.desconto ?? 0);
      }
      if (forma === "percentual" && Number(item.acrescimo_percentual) > 0) {
        return `${this._fmtNum(item.acrescimo_percentual)}%`;
      }
      return this.formatBRL(item.acrescimo_valor ?? item.acrescimo ?? 0);
    },

    _fmtNum(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },

    limparCarrinho() {
      // Devolve quantidades de lotes dos itens no carrinho
      for (const item of this.carrinho) {
        if (item.id_produto_lote) {
          ProdutoLoteLocalRepository.devolverQuantidadeLocal(item.id_produto_lote, item.quantidade)
            .catch((e) => console.error("[NovaVenda] Erro ao devolver lote (limpar):", e));
        }
      }
      this.carrinho = [];
      this._limparCamposItem();
      this.limparBuscaProduto();
      this.listaPagamentos = [];
      this.pagamentoRapidoAtivo = null;
      this.valorPagamentoAtual = 0;
      this.avisoClienteObrigatorio = "";
      this.itemSelecionadoIdx = -1;
    },

    limparBuscaProduto() {
      this.termoBusca = "";
      this.produtosBusca = [];
      this.selectedProductIndex = 0;
    },

    // ── Cliente ─────────────────────────────────────────────────────────

    async _buscarCliente() {
      const termo = (this.termoCliente || "").trim();
      if (termo.length < 2) {
        this.clientesBusca = [];
        return;
      }

      try {
        this.clientesBusca = (await ClienteLocalRepository.search(termo)) || [];
      } catch (e) {
        console.error("[NovaVenda] Erro na busca de cliente:", e);
        this.clientesBusca = [];
      }
    },

    async selecionarCliente(cliente) {
      this.modoCliente = "cadastrado";
      this.clienteSelecionado = cliente;
      this.termoCliente = this.nomeCliente(cliente);
      this.clientesBusca = [];
      this._verificarClienteObrigatorio();

      // Se o cliente tem lista associada, auto-seleciona (sem sobrescrever seleção manual)
      const idLista = cliente.id_lista_preco ?? null;
      this.idListaPrecoCliente = idLista;

      if (idLista && !this.listaPrecoSelecionada) {
        const lista = this.listasDisponiveis.find((l) => l.id_lista_preco === idLista);
        if (lista) {
          this._aplicarLista(lista);
          this.listaPrecoDeCliente = true;
        }
      }
    },

    removerCliente() {
      this.clienteSelecionado = null;
      this.idListaPrecoCliente = null;
      if (this.modoCliente === "cadastrado") {
        this.termoCliente = "";
      }
      this.clientesBusca = [];
      // Remove lista apenas se foi carregada automaticamente pelo cliente
      if (this.listaPrecoDeCliente) {
        this.removerListaPreco();
      }
      this._verificarClienteObrigatorio();
    },

    limparBuscaCliente() {
      if (this.modoCliente === "manual") return;
      this.termoCliente = "";
      this.clientesBusca = [];
    },

    // ── Lista de Preço ────────────────────────────────────────────────────

    async carregarListasPreco() {
      try {
        this.listasDisponiveis = (await ListaPrecoLocalRepository.listar()) || [];
      } catch (e) {
        console.warn("[NovaVenda] Erro ao carregar listas de preço:", e);
      }
    },

    onSelecionarListaPreco(event) {
      const id = parseInt(event.target.value, 10);
      const lista = this.listasDisponiveis.find((l) => l.id_lista_preco === id);
      if (lista) {
        this._aplicarLista(lista);
        this.listaPrecoDeCliente = false;
      }
    },

    _aplicarLista(lista) {
      this.listaPrecoSelecionada = lista;
      const map = {};
      for (const p of (lista.produtos || [])) {
        if (p.id_produto != null) {
          map[p.id_produto] = Number(p.preco_lista);
        }
      }
      this.listaPrecoMap = map;
    },

    removerListaPreco() {
      this.listaPrecoSelecionada = null;
      this.listaPrecoDeCliente = false;
      this.listaPrecoMap = {};
    },

    // ── Pagamento ────────────────────────────────────────────────────────

    async carregarFormasPagamento() {
      try {
        const formas = (await FormaPagamentoLocalRepository.listar()) || [];
        this.formasPagamento = formas;
      } catch (e) {
        console.error("[NovaVenda] Erro ao carregar formas de pagamento:", e);
      }
    },

    async carregarVendedores() {
      try {
        this.vendedores = (await VendedorLocalRepository.listar()) || [];
      } catch (e) {
        console.warn("[NovaVenda] Erro ao carregar vendedores:", e);
      }
    },

    async carregarGrupos() {
      try {
        this.grupos = (await GrupoLocalRepository.listar()) || [];
      } catch (e) {
        console.warn("[NovaVenda] Erro ao carregar grupos:", e);
      }
    },

    preencherValorTotal() {
      this.valorPagamentoAtual = this.totalComDescontoAcrescimo;
    },

    adicionarPagamento() {
      if (!this.formaPagamentoSelecionada || this.valorPagamentoAtual <= 0) return;

      const range      = this.parcelasRange;
      const hoje       = new Date().toISOString().split("T")[0];
      const isDinheiro = /\bvista\b|\bdinheiro\b|\besp[eé]cie\b/i.test(
        this.formaPagamentoSelecionada.descricao
      );

      // Para formas não-dinheiro, limitar ao valor restante
      let valorRecebido = Number(this.valorPagamentoAtual);
      if (!isDinheiro && valorRecebido > this.valorRestante && this.valorRestante > 0) {
        valorRecebido = this.valorRestante;
      }

      // Troco: novo total pago (existente + este pagamento) vs total da nota
      const novoTotalPago = this.totalPago + valorRecebido;
      const troco = Math.round(Math.max(0, novoTotalPago - this.totalComDescontoAcrescimo) * 100) / 100;

      const item = {
        id_forma_pagamento: this.formaPagamentoSelecionada.id ?? null,
        id_meio_pagamento:
          this.formaPagamentoSelecionada.id_meio_pagamento ??
          this.formaPagamentoSelecionada.meio_pagamento?.id ??
          null,
        descricao: this.formaPagamentoSelecionada.descricao,
        informar_cliente: this.isTrue(this.formaPagamentoSelecionada.informar_cliente),
        value: valorRecebido,
        vezes: range ? range.length : 1,
        range: range
          ? range.map(({ date, value }) => ({ date, value }))
          : [{ date: hoje, value: valorRecebido }],
        tpag:
          this.formaPagamentoSelecionada.tpag ??
          this.formaPagamentoSelecionada.tPag ??
          this.formaPagamentoSelecionada.codigo_pagamento_nfce ??
          null,
      };

      if (troco > 0) item.valor_troco = troco;

      this.listaPagamentos.push(item);
      const novoRestante = Math.max(0, this.totalComDescontoAcrescimo - novoTotalPago);
      this.valorPagamentoAtual = novoRestante;
      this._verificarClienteObrigatorio();
      if (novoRestante > 0) {
        this.focarValorPagamento();
      }
    },

    removerPagamento(idx) {
      this.listaPagamentos.splice(idx, 1);
      this.pagamentoRapidoAtivo = null;
      this._verificarClienteObrigatorio();
      this.valorPagamentoAtual = this.valorRestante;
    },

    // ── Seleção de forma de pagamento ──────────────────────────────────

    selecionarFormaPagamento(tipo) {
      if (!this.subtotal) return;
      let forma = null;
      if (tipo === "avista") {
        forma = this.formasPagamento.find((f) =>
          /\bvista\b|\bdinheiro\b|\besp[eé]cie\b/i.test(f.descricao)
        ) || null;
      } else if (tipo === "pix") {
        forma = this.formasPagamento.find((f) => /pix/i.test(f.descricao)) || null;
      } else if (tipo === "credito1x") {
        forma = this.formasPagamento.find((f) => /cr[eé]dito/i.test(f.descricao)) || null;
      } else if (tipo === "debito") {
        forma = this.formasPagamento.find((f) => /d[eé]bito/i.test(f.descricao)) || null;
      }
      if (!forma) return;
      this.formaPagamentoSelecionada = forma;
      this.pagamentoRapidoAtivo = tipo;
    },

    /**
     * Foca o input de "Valor recebido" (forma selecionada).
     * Usado pelos atalhos F1-F4 e ao adicionar pagamento.
     */
    focarValorPagamento() {
      this.$nextTick(() => {
        const inp = this.$refs.valorPagamentoInput;
        if (inp && typeof inp.focus === "function") {
          inp.focus();
          try { inp.select(); } catch (_) { /* noop */ }
        }
      });
    },

    /**
     * Atalhos F1/F2/F3: pagamento rápido para cartão crédito, débito e PIX.
     * - Seleciona a forma correspondente
     * - Preenche valorPagamentoAtual com o valor restante da venda
     * - Adiciona o pagamento automaticamente
     *
     * Para Dinheiro use selecionarPagamentoDinheiro() (permite informar valor
     * recebido para calcular troco).
     *
     * @param {"cartao_credito"|"cartao_debito"|"pix"} tipo
     */
    adicionarPagamentoRapido(tipo) {
      if (this.finalizando) return;
      if (!this.carrinho.length) return;

      const mapa = {
        cartao_credito: { interno: "credito1x", computed: this._formaCredito },
        cartao_debito:  { interno: "debito",    computed: this._formaDebito  },
        pix:            { interno: "pix",       computed: this._formaPix     },
      };
      const cfg = mapa[tipo];
      if (!cfg) return;

      if (!cfg.computed) {
        this.mensagemErro = "Forma de pagamento não configurada para este atalho.";
        return;
      }

      const restante = Number(this.valorRestante || 0);
      if (restante <= 0) {
        this.mensagemErro = "A venda já está quitada.";
        return;
      }

      this.formaPagamentoSelecionada = cfg.computed;
      this.pagamentoRapidoAtivo      = cfg.interno;
      // O watcher de formaPagamentoSelecionada já preenche valorPagamentoAtual
      // com o valorRestante; garantimos aqui para evitar race.
      this.valorPagamentoAtual       = restante;

      this.$nextTick(() => this.adicionarPagamento());
    },

    /**
     * Atalho F4: seleciona Dinheiro e foca o campo "Valor recebido"
     * (sem adicionar automaticamente, para permitir informar valor maior
     * que o total e calcular troco).
     */
    selecionarPagamentoDinheiro() {
      if (this.finalizando) return;
      if (!this.carrinho.length) return;

      if (!this._formaAvista) {
        this.mensagemErro = "Forma de pagamento Dinheiro não configurada.";
        return;
      }

      const restante = Number(this.valorRestante || 0);
      if (restante <= 0) {
        this.mensagemErro = "A venda já está quitada.";
        return;
      }

      this.formaPagamentoSelecionada = this._formaAvista;
      this.pagamentoRapidoAtivo      = "avista";
      this.valorPagamentoAtual       = restante;
      this.focarValorPagamento();
    },

    // ── Pagamento rápido ────────────────────────────────────────────────

    selecionarPagamentoRapido(tipo) {
      if (!this.subtotal) return;

      let forma = null;
      let tpag  = null;

      if (tipo === "avista") {
        forma =
          this.formasPagamento.find((f) =>
            /\bvista\b|\bdinheiro\b|\besp[eé]cie\b/i.test(f.descricao)
          ) || this.formasPagamento[0] || null;
        tpag = "01";
      } else if (tipo === "pix") {
        forma =
          this.formasPagamento.find((f) => /pix/i.test(f.descricao)) || null;
        tpag = "20";
      } else if (tipo === "credito1x") {
        forma =
          this.formasPagamento.find((f) => /cr[eé]dito/i.test(f.descricao)) || null;
        tpag = "03";
      } else if (tipo === "debito") {
        forma =
          this.formasPagamento.find((f) => /d[eé]bito/i.test(f.descricao)) || null;
        tpag = "04";
      } else if (tipo === "outros") {
        forma =
          this.formasPagamento.find((f) =>
            !/\bvista\b|\bdinheiro\b|\besp[eé]cie\b|pix|cr[eé]dito|d[eé]bito/i.test(f.descricao)
          ) || this.formasPagamento[0] || null;
        tpag = "99";
      }

      if (!forma) {
        this.pagamentoRapidoAtivo = null;
        return;
      }

      const hoje = new Date().toISOString().split("T")[0];
      this.pagamentoRapidoAtivo = tipo;
      this.listaPagamentos = [
        {
          id_forma_pagamento: forma.id ?? null,
          id_meio_pagamento: forma.id_meio_pagamento ?? forma.meio_pagamento?.id ?? null,
          descricao: forma.descricao,
          informar_cliente: this.isTrue(forma.informar_cliente),
          value: this.totalComDescontoAcrescimo,
          vezes: 1,
          range: [{ date: hoje, value: this.totalComDescontoAcrescimo }],
          tpag,
        },
      ];
      this._verificarClienteObrigatorio();
    },

    abrirMenuOutros() {
      if (!this.subtotal) return;
      if (this.formasOutros.length === 1) {
        this.selecionarFormaOutrosPagamento(this.formasOutros[0]);
        return;
      }
      this.outrosMenuAberto = !this.outrosMenuAberto;
    },

    selecionarFormaOutros(forma) {
      this.outrosMenuAberto = false;
      const hoje = new Date().toISOString().split("T")[0];
      this.pagamentoRapidoAtivo = "outros";
      this.listaPagamentos = [
        {
          id_forma_pagamento: forma.id ?? null,
          id_meio_pagamento: forma.id_meio_pagamento ?? forma.meio_pagamento?.id ?? null,
          descricao: forma.descricao,
          informar_cliente: this.isTrue(forma.informar_cliente),
          value: this.totalComDescontoAcrescimo,
          vezes: 1,
          range: [{ date: hoje, value: this.totalComDescontoAcrescimo }],
          tpag: "99",
        },
      ];
      this._verificarClienteObrigatorio();
    },

    selecionarFormaOutrosPagamento(forma) {
      if (!forma) return;
      this.outrosMenuAberto = false;
      this.formaPagamentoSelecionada = forma;
      this.pagamentoRapidoAtivo = "outros";
    },

    // ── Finalização rápida por forma ────────────────────────────────────

    finalizarComPagamento(tipo) {
      this.selecionarFormaPagamento(tipo);
    },

    _formaTemParcelas(forma) {
      const tipo = Number(forma.tipo_repeticao ?? 0);
      if (tipo === 0) return false;
      if (tipo === 9) {
        const iv = forma.intervalo_dias;
        return iv && Array.isArray(iv.intervalo) && iv.intervalo.length > 0;
      }
      return !!(forma.intervalo_dias && forma.intervalo_dias.intervalo);
    },

    async finalizarComForma(forma) {
      this.selecionarFormaOutrosPagamento(forma);
    },

    // ── Atalhos de teclado ──────────────────────────────────────────────

    _registrarAtalhos() {
      this._atalhoHandler = (e) => this.handleGlobalShortcuts(e);
      window.addEventListener("keydown", this._atalhoHandler);
    },

    _removerAtalhos() {
      if (this._atalhoHandler) {
        window.removeEventListener("keydown", this._atalhoHandler);
        this._atalhoHandler = null;
      }
    },

    /**
     * Indica se existe algum modal/dropdown aberto na tela.
     * Quando true, atalhos como F4 / Ctrl+S não devem disparar
     * (ESC continua valendo para fechar modal — tratado no handler).
     */
    _algumModalAberto() {
      return !!(
        this.modalDanfe.visivel
        || this.modalCombinacao.visivel
        || this.modalLote.visivel
        || this.modalCorrecaoFiscal.visivel
        || this.modalItemDescAcres.visivel
        || this.modalAbrirCaixa.visivel
        || this.modalFecharCaixa.visivel
        || this.modalMovimento.visivel
        || this.modalProdutoPesquisa.visivel
        || this.modalClientePesquisa.visivel
        || this.outrosMenuAberto
      );
    },

    _isCampoEditavel(target) {
      if (!target) return false;
      const tag = (target.tagName || "").toUpperCase();
      if (tag === "TEXTAREA") return true;
      if (target.isContentEditable) return true;
      return false;
    },

    /**
     * Tratador global de atalhos do PDV Desktop.
     * Regras:
     *   - F1: pagamento rápido Cartão de Crédito
     *   - F2: pagamento rápido Cartão de Débito
     *   - F3: pagamento rápido PIX
     *   - F4: seleciona Dinheiro e foca "Valor recebido" (permite troco)
     *   - F5: finaliza a venda (Finalizar NFC-e)
     *   - F6: abre pesquisa avançada de Produtos
     *   - F7: abre pesquisa avançada de Clientes
     *   - F8: finaliza a venda como Pré-venda
     *   - F9: abre listagem de Vendas
     *   - ESC: fecha modal aberto; sem modal, sai da tela do PDV
     *   - Ctrl+S: cancela a venda atual (não é "salvar" no PDV Desktop)
     *   - Alt+1/2/3: formas de pagamento rápidas (legado)
     *   - Ctrl+D: desconto/acréscimo do item selecionado
     */
    handleGlobalShortcuts(e) {
      // ── ESC ────────────────────────────────────────────────────────
      if (e.key === "Escape" || e.key === "Esc") {
        // Se há modal aberto, deixa o próprio modal cuidar do ESC (não sai do PDV)
        if (this._algumModalAberto()) return;
        // Se está finalizando, ignora
        if (this.finalizando) return;
        // Se já existe uma confirmação aberta, evita duplicar
        if (this.saindoDoPdv) return;
        e.preventDefault();
        this.sairDoPdv();
        return;
      }

      // Bloqueia demais atalhos enquanto finaliza ou enquanto há modal aberto
      const finalizando = this.finalizando;
      const modalAberto = this._algumModalAberto();
      // Bloqueia F1-F9 quando foco está em campo "sensível" (textarea / contenteditable).
      // Inputs comuns do PDV (produto, qtd, valor un., valor recebido) seguem aceitando.
      const campoSensivel = this._isCampoEditavel(e.target);

      // ── F1: Cartão de Crédito ─────────────────────────────────────
      if (e.key === "F1") {
        if (modalAberto || finalizando || campoSensivel) return;
        e.preventDefault();
        this.adicionarPagamentoRapido("cartao_credito");
        return;
      }

      // ── F2: Cartão de Débito ──────────────────────────────────────
      if (e.key === "F2") {
        if (modalAberto || finalizando || campoSensivel) return;
        e.preventDefault();
        this.adicionarPagamentoRapido("cartao_debito");
        return;
      }

      // ── F3: PIX ───────────────────────────────────────────────────
      if (e.key === "F3") {
        if (modalAberto || finalizando || campoSensivel) return;
        e.preventDefault();
        this.adicionarPagamentoRapido("pix");
        return;
      }

      // ── F4: Dinheiro (seleciona e foca "Valor recebido") ──────────
      if (e.key === "F4") {
        if (modalAberto || finalizando || campoSensivel) return;
        e.preventDefault();
        this.selecionarPagamentoDinheiro();
        return;
      }

      // ── F5: Finalizar NFC-e ───────────────────────────────────────
      if (e.key === "F5") {
        if (modalAberto || finalizando || campoSensivel) return;
        e.preventDefault();
        this.finalizarVendaPorAtalho();
        return;
      }

      // ── F6: Pesquisa avançada de Produtos ─────────────────────────
      if (e.key === "F6") {
        if (modalAberto || finalizando || campoSensivel) return;
        e.preventDefault();
        this.abrirPesquisaProdutos();
        return;
      }

      // ── F7: Pesquisa avançada de Clientes ─────────────────────────
      if (e.key === "F7") {
        if (modalAberto || finalizando || campoSensivel) return;
        e.preventDefault();
        this.abrirPesquisaClientes();
        return;
      }

      // ── F8: Finalizar Pré-venda ───────────────────────────────────
      if (e.key === "F8") {
        if (modalAberto || finalizando || campoSensivel) return;
        e.preventDefault();
        this.finalizarPreVenda();
        return;
      }

      // ── F9: Listagem de Vendas ────────────────────────────────────
      if (e.key === "F9") {
        if (modalAberto || finalizando || campoSensivel) return;
        e.preventDefault();
        this.abrirVendasOffline();
        return;
      }

      // ── Ctrl+S: Cancelar venda atual ──────────────────────────────
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        // Sempre previne o "Salvar" do navegador, mesmo quando bloqueado
        e.preventDefault();
        if (modalAberto || finalizando) return;
        if (this.confirmandoCancelamento) return;
        this.cancelarVendaAtual();
        return;
      }

      // ── Alt+1: À vista ────────────────────────────────────────────
      if (e.altKey && e.key === "1") {
        if (modalAberto || finalizando) return;
        e.preventDefault();
        this.selecionarFormaPagamento("avista");
        return;
      }
      // ── Alt+2: Pix ────────────────────────────────────────────────
      if (e.altKey && e.key === "2") {
        if (modalAberto || finalizando) return;
        e.preventDefault();
        this.selecionarFormaPagamento("pix");
        return;
      }
      // ── Alt+3: Cartão Crédito 1x ──────────────────────────────────
      if (e.altKey && e.key === "3") {
        if (modalAberto || finalizando) return;
        e.preventDefault();
        this.selecionarFormaPagamento("credito1x");
        return;
      }
      // ── Ctrl+D: Desconto/Acréscimo do item selecionado ────────────
      if ((e.ctrlKey || e.metaKey) && (e.key === "d" || e.key === "D")) {
        if (modalAberto || finalizando) return;
        e.preventDefault();
        this._atalhoDescontoItem();
        return;
      }
    },

    // ── Ações operacionais (atalhos / botões) ──────────────────────────

    /**
     * Indica se existe venda em andamento (itens, pagamentos, cliente,
     * desconto/acréscimo ou produto sendo editado).
     */
    existeVendaEmAndamento() {
      if (this.carrinho && this.carrinho.length > 0) return true;
      if (this.listaPagamentos && this.listaPagamentos.length > 0) return true;
      if (this.clienteSelecionado) return true;
      if ((this.clienteManualNome || "").trim()) return true;
      if (this._normalizarDocumento(this.clienteManualDocumento)) return true;
      if (Number(this.descontoVendaReais) > 0) return true;
      if (Number(this.acrescimoVendaReais) > 0) return true;
      if (this.produtoSelecionado) return true;
      return false;
    },

    /** Foca o campo de busca de produto. */
    focarBuscaProduto() {
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
          try { this.$refs.searchInput.select(); } catch (_) { /* noop */ }
        }
      });
    },

    /**
     * Disparado pelo botão "Finalizar NFC-e" e pelo atalho F4.
     * Apenas delega para finalizarVenda — não duplica lógica.
     */
    finalizarVendaPorAtalho() {
      if (this.finalizando) return;
      this.finalizarVenda();
    },

    _montarPayloadFinalizacao({ preVenda = false } = {}) {
      return {
        buyer: this._montarBuyer(),
        price: this.totalComDescontoAcrescimo,
        desconto: Number(this.descontoVendaReais) || 0,
        acrescimo: Number(this.acrescimoVendaReais) || 0,
        observation: this.observacaoVenda || "",
        isNFce: !preVenda,
        pre_venda: preVenda,
        modelo: 65,
        finalidade: 1,
        tpNF: 1,
        id_vendedor: this.vendedorSelecionado ? (this.vendedorSelecionado.id_vendedor ?? null) : null,
        paymentModeCollection: this.listaPagamentos,
        productOfTradeCollection: this.carrinho.map((item) => ({
          ...item,
          active: true,
        })),
      };
    },

    async finalizarPreVenda() {
      if (!this.podeFinalizar || this.finalizando) return;
      if (!this.caixaAberto) {
        this.mensagemErro = "Abra o caixa antes de finalizar a pré-venda.";
        this.modalAbrirCaixa = { visivel: true, bloqueante: true };
        return;
      }
      if (this._vendaExigeCliente && !this._clienteInformado) {
        this.mensagemErro = "Informe o cliente para utilizar esta forma de pagamento.";
        this._focarCampoCliente();
        return;
      }

      this.finalizando = true;
      this.operacaoFinalizacao = "pre_venda";
      this.mensagemSucesso = "";
      this.mensagemErro = "";
      this.erroFiscalCorrigido = false;

      try {
        const retorno = await NfcePdvDesktopService.finalizarPreVenda(
          this._montarPayloadFinalizacao({ preVenda: true })
        );
        const uuidFinalizada = retorno.uuid_venda_terminal ?? null;

        try {
          await CaixaPdvDesktopService.registrarVenda(
            { uuid_venda_terminal: uuidFinalizada },
            this.listaPagamentos
          );
          await this.carregarCaixaAberto();
          this._sincronizarCaixaBackground();
        } catch (errCaixa) {
          console.warn("[NovaVenda] Falha ao registrar pré-venda no caixa:", errCaixa);
        }

        // A pré-venda segue o mesmo comportamento da NFC-e: envia o documento
        // diretamente para a impressora padrão e libera imediatamente a tela.
        this.imprimirComprovantePreVenda(uuidFinalizada, { silenciosa: true });
        this.resetarVenda({ devolverLotes: false });
        this.iniciarNovaVenda();
        window.dispatchEvent(new CustomEvent("pdv-dashboard-refresh"));
        this.$toast && this.$toast.success(retorno.message);
      } catch (e) {
        if (this._tratarErroBloqueioEmpresa(e)) return;
        if (this._tratarErroConfiguracaoNfce(e)) return;
        console.error("[NovaVenda] Erro ao finalizar pré-venda:", e);
        this.mensagemErro = `Erro ao finalizar pré-venda: ${e.message}`;
      } finally {
        this.finalizando = false;
        this.operacaoFinalizacao = null;
      }
    },

    /**
     * Atalho ESC: sai da tela do PDV.
     * - Se há venda em andamento, pede confirmação.
     * - Não cancela a venda; apenas sai da tela.
     */
    async sairDoPdv() {
      const navegar = () => {
        this.$router.push({ name: "pdvDesktopDashboard" }).catch(() => {});
      };

      if (!this.existeVendaEmAndamento()) {
        navegar();
        return;
      }

      this.saindoDoPdv = true;
      try {
        const result = await this.$swal.fire({
          title: "Sair do PDV?",
          text: "Existe uma venda em andamento. Deseja sair do PDV mesmo assim?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, sair",
          cancelButtonText: "Continuar vendendo",
          reverseButtons: true,
          focusCancel: true,
        });
        if (result && result.isConfirmed) {
          navegar();
        }
      } finally {
        this.saindoDoPdv = false;
      }
    },

    /**
     * Atalho Ctrl+S e botão "Cancelar venda":
     * cancela toda a venda atual (itens, pagamentos, cliente, etc.)
     * e abre uma nova venda limpa.
     */
    async cancelarVendaAtual() {
      if (this.finalizando) return;

      // Se não há venda em andamento, apenas reseta e foca
      if (!this.existeVendaEmAndamento()) {
        this.resetarVenda();
        this.focarBuscaProduto();
        return;
      }

      this.confirmandoCancelamento = true;
      try {
        const result = await this.$swal.fire({
          title: "Cancelar venda atual?",
          text: "Deseja cancelar a venda atual? Todos os itens e pagamentos serão removidos.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, cancelar venda",
          cancelButtonText: "Voltar",
          reverseButtons: true,
          focusCancel: true,
          confirmButtonColor: "#c53030",
        });
        if (!result || !result.isConfirmed) return;

        // Devolve quantidades de lotes (mesma lógica de limparCarrinho/resetarVenda)
        this.resetarVenda();
        this.iniciarNovaVenda();
        this.focarBuscaProduto();
      } finally {
        this.confirmandoCancelamento = false;
      }
    },

    /** Abre o modal de pesquisa avançada de produtos. */
    abrirPesquisaProdutos() {
      if (this.finalizando) return;
      if (this.mensagemSucesso || this.mensagemContingencia) return;
      this.modalProdutoPesquisa = { visivel: true };
    },

    /** Abre o modal de pesquisa avançada de clientes. */
    abrirPesquisaClientes() {
      if (this.finalizando) return;
      if (this.mensagemSucesso || this.mensagemContingencia) return;
      this.modalClientePesquisa = { visivel: true };
    },

    /** Abre a listagem de vendas do PDV Desktop. */
    abrirVendasOffline() {
      if (this.finalizando) return;
      if (this.mensagemSucesso || this.mensagemContingencia) return;
      this.$router.push({ name: "pdvDesktopVendasOffline" });
    },

    /**
     * Recebe o produto selecionado no modal de pesquisa avançada
     * e prepara a entrada de produto (sem inserir automaticamente,
     * para preservar regras de lote/combinação/pesado já tratadas
     * em inserirNoCarrinho).
     */
    onSelecionarProdutoPesquisaAvancada(produto) {
      this.modalProdutoPesquisa.visivel = false;
      if (!produto) return;
      // Reutiliza o fluxo padrão: seleciona o produto na entrada principal
      this.selecionarProduto(produto);
      this.quantidadeItem = 1;
      // selecionarProduto já preenche valorUnitarioItem com o preço
      this.$nextTick(() => {
        if (this.$refs.qtyInput) {
          this.$refs.qtyInput.focus();
          try { this.$refs.qtyInput.select(); } catch (_) { /* noop */ }
        }
      });
    },

    /** Recebe o cliente selecionado no modal de pesquisa avançada. */
    onSelecionarClientePesquisaAvancada(cliente) {
      this.modalClientePesquisa.visivel = false;
      if (!cliente) return;
      this.selecionarCliente(cliente);
      this.focarBuscaProduto();
    },

    // ── Venda ─────────────────────────────────────────────────────────

    verDetalheRejeicao() {
      this.$router.push({ name: "pdvDesktopVendasOffline" });
    },

    _tratarErroBloqueioEmpresa(err) {
      if (!TerminalConfigService.isCodigoBloqueioEmpresa(err?.code)) return false;

      this.terminalBloqueado = true;
      this.motivoBloqueio = err?.message ||
        "Empresa bloqueada para operacoes no PDV Desktop.";
      this.$toast && this.$toast.error(this.motivoBloqueio);
      this.$router.push({ name: "pdvDesktopBloqueio" }).catch(() => {});
      return true;
    },

    _tratarErroConfiguracaoNfce(err) {
      if (!TerminalConfigService.isCodigoConfiguracaoNfceAusente(err?.code)) return false;

      this.terminalBloqueado = true;
      this.configuracaoNfceAusente = true;
      this.motivoBloqueio = TerminalConfigService.mensagemConfiguracaoNfceAusente();
      this.mensagemErro = this.motivoBloqueio;
      this.$toast && this.$toast.error(this.motivoBloqueio);
      return true;
    },

    async sincronizarDadosFiscais() {
      this.sincronizandoFiscal = true;
      this.mensagemFiscal = "";
      this.mensagemFiscalErro = false;
      try {
        await FiscalContextPdvDesktopService.sincronizarContextoFiscal();
        this.mensagemFiscal = "Dados fiscais sincronizados!";
        setTimeout(() => { this.mensagemFiscal = ""; }, 4000);
      } catch (e) {
        if (this._tratarErroBloqueioEmpresa(e)) return;
        if (this._tratarErroConfiguracaoNfce(e)) return;
        this.mensagemFiscal = `Erro: ${e.message}`;
        this.mensagemFiscalErro = true;
        setTimeout(() => { this.mensagemFiscal = ""; this.mensagemFiscalErro = false; }, 6000);
      } finally {
        this.sincronizandoFiscal = false;
      }
    },

    async finalizarVenda() {
      if (!this.podeFinalizar || this.finalizando) return;

      // Bloqueia venda se não houver caixa aberto
      if (!this.caixaAberto) {
        this.mensagemErro = "Abra o caixa antes de finalizar a venda.";
        this.modalAbrirCaixa = { visivel: true, bloqueante: true };
        return;
      }

      // Bloqueia finalização se alguma forma exige cliente e ele não foi informado
      if (this._vendaExigeCliente && !this._clienteInformado) {
        this.mensagemErro = "Informe o cliente para utilizar esta forma de pagamento.";
        this._focarCampoCliente();
        return;
      }

      this.finalizando = true;
      this.operacaoFinalizacao = "nfce";
      this.mensagemSucesso = "";
      this.mensagemErro = "";
      this.erroFiscalCorrigido = false;

      const payload = this._montarPayloadFinalizacao();

      try {
        const retorno = await NfcePdvDesktopService.finalizarNfce(payload);
        const uuidFinalizada = retorno.uuid_venda_terminal ?? null;

        if (retorno.autorizada || retorno.contingencia) {
          // Registra movimentação de caixa por forma de pagamento
          try {
            await CaixaPdvDesktopService.registrarVenda(
              {
                uuid_venda_terminal: uuidFinalizada,
                chave:               retorno.chave      ?? null,
                protocolo:           retorno.protocolo  ?? null,
                id_nfe:              retorno.id_nfe     ?? null,
              },
              this.listaPagamentos
            );
            await this.carregarCaixaAberto();
            this._sincronizarCaixaBackground();
          } catch (errCaixa) {
            console.warn("[NovaVenda] Falha ao registrar venda no caixa:", errCaixa);
          }

          // Sucesso (online): abre PDF do backend no padrão web
          if (retorno.autorizada && retorno.danfe_url) {
            // DANFE local é a fonte única; em autorização, usa o mesmo HTML salvo
            await this.imprimirNfce(uuidFinalizada);
          } else {
            // Contingência ou sem URL: usa fluxo local (modal / impressora)
            await this.imprimirNfce(uuidFinalizada);
          }
          this.resetarVenda({ devolverLotes: false });
          this.iniciarNovaVenda();
          window.dispatchEvent(new CustomEvent("pdv-dashboard-refresh"));
        } else if (retorno.rejeitada) {
          // Rejeitada: mostra erro, mantém carrinho para correção
          this.mensagemErro = retorno.message;
          this.uuidRejeicao = retorno.uuid_venda_terminal;
          this.mensagemSucesso = "";
          this.mensagemContingencia = "";
        } else if (retorno.erro_transmissao) {
          // XML gerado mas não transmitido — disponível em Vendas Offline
          this.mensagemErro = retorno.message;
          this.uuidRejeicao = retorno.uuid_venda_terminal;
          this.mensagemSucesso = "";
          this.mensagemContingencia = "";
        }
      } catch (e) {
        if (this._tratarErroBloqueioEmpresa(e)) return;
        if (this._tratarErroConfiguracaoNfce(e)) return;
        console.error("[NovaVenda] Erro ao finalizar venda:", e);
        this.mensagemErro = `Erro ao finalizar venda: ${e.message}`;
        this.erroFiscalCorrigido = false;

        // Tenta identificar erro fiscal e abre modal de correção automático
        const erroFiscal = this.parseErroFiscalProduto(e.message);
        if (erroFiscal) {
          const idx = this._localizarItemCarrinhoPorNome(erroFiscal.produtoNome);
          if (idx >= 0) {
            this.modalCorrecaoFiscal = {
              visivel:  true,
              item:     this.carrinho[idx],
              itemIdx:  idx,
              erroInfo: erroFiscal,
            };
          }
        }
      } finally {
        this.finalizando = false;
        this.operacaoFinalizacao = null;
      }
    },

    /**
     * Dispara impressão do DANFE após finalização com sucesso.
     * - Se imprimir_automatico + printer_name configurados:
     *     abrir_preview=true  → abre modal DanfeNfce
     *     abrir_preview=false → impressão silenciosa em background (fire-and-forget)
     * - Sem impressora configurada → abre modal DanfeNfce para o operador imprimir
     * Erros não bloqueiam o reset/nova venda.
     */
    // ---------------------------------------------------------------
    // Correção fiscal de item do carrinho
    // ---------------------------------------------------------------

    /** Retorna true se o item do carrinho é o produto com erro fiscal ativo. */
    _itemTemErroFiscal(item) {
      if (!this.mensagemErro || this.erroFiscalCorrigido) return false;
      const erro = this.parseErroFiscalProduto(this.mensagemErro);
      if (!erro) return false;
      return !!(
        item.descricao &&
        item.descricao.toLowerCase().includes(erro.produtoNome.toLowerCase())
      );
    },

    /**
     * Analisa a mensagem de erro de finalização e detecta se é um erro
     * de dado fiscal faltante em produto específico.
     * Retorna { isErroFiscalProduto, produtoNome, campo, mensagem } ou null.
     */
    parseErroFiscalProduto(msg) {
      if (!msg) return null;
      const m = msg.match(/"([^"]+)":\s*(NCM|CFOP|CSOSN|CST\s+ICMS|CST|CEST)/i);
      if (!m) return null;
      return {
        isErroFiscalProduto: true,
        produtoNome: m[1],
        campo: m[2].toUpperCase().replace(/\s+/, " "),
        mensagem: msg,
      };
    },

    /** Localiza o primeiro item do carrinho cujo nome contém `nome`. */
    _localizarItemCarrinhoPorNome(nome) {
      if (!nome || !this.carrinho.length) return -1;
      const n = nome.toLowerCase();
      return this.carrinho.findIndex(
        (i) => i.descricao && i.descricao.toLowerCase().includes(n)
      );
    },

    /** Abre o modal de correção fiscal manualmente (botão ⚙ no item). */
    abrirCorrecaoFiscalItem(idx) {
      if (idx < 0 || idx >= this.carrinho.length) return;
      this.modalCorrecaoFiscal = {
        visivel:  true,
        item:     this.carrinho[idx],
        itemIdx:  idx,
        erroInfo: null,
      };
    },

    /**
     * Recebe os dados corrigidos do modal e:
     * 1. Patcha o item in-memory do carrinho (campos flat + produto_original + payload_json)
     * 2. Atualiza produtos_local no SQLite
     * 3. Enfileira na fila_sync para sincronização posterior
     * 4. Exibe mensagem e habilita o botão de retry
     */
    async onSalvarCorrecaoFiscal(dadosCorrigidos) {
      const idx = this.modalCorrecaoFiscal.itemIdx;
      if (idx < 0 || idx >= this.carrinho.length) return;

      const fiscal = {
        ncm_codigo:                    dadosCorrigidos.ncm      || null,
        cfop_codigo:                   dadosCorrigidos.cfop     || null,
        cst_csosn_codigo:              dadosCorrigidos.csosn    || null,
        cst_csosn_contribuinte_codigo: dadosCorrigidos.cst_icms || null,
        cst_pis_codigo:                dadosCorrigidos.cst_pis  || null,
        cst_cofins_codigo:             dadosCorrigidos.cst_cofins || null,
      };

      // 1. Patch item in-memory e disparar reatividade Vue 2
      const item = { ...this.carrinho[idx], ...fiscal };
      if (item.produto_original) {
        item.produto_original = { ...item.produto_original, ...fiscal };
      }

      // Reconstruir payload_json para que NfcePdvDesktopService leia os dados corretos
      let payloadObj = { produto: {}, lote: null };
      try { payloadObj = JSON.parse(item.payload_json || "{}"); } catch (_) { /* noop */ }
      item.payload_json = JSON.stringify({
        produto: {
          ...(payloadObj.produto || {}),
          ...fiscal,
          ncm:       dadosCorrigidos.ncm       || null,
          cfop:      dadosCorrigidos.cfop      || null,
          cfop_nfce: dadosCorrigidos.cfop      || null,
          csosn:     dadosCorrigidos.csosn     || null,
          cst:       dadosCorrigidos.cst_icms  || null,
          orig:      dadosCorrigidos.origem    || "0",
          cst_pis:   dadosCorrigidos.cst_pis   || "07",
          cst_cofins: dadosCorrigidos.cst_cofins || "07",
        },
        lote: payloadObj.lote ?? null,
      });

      this.$set(this.carrinho, idx, item);

      // 2. Persistir em produtos_local (SQLite)
      const id_produto = item.id_produto;
      if (id_produto) {
        try {
          const produtoDB = await ProdutoLocalRepository.getById(id_produto);
          if (produtoDB) {
            let payloadProduto = {};
            try { payloadProduto = JSON.parse(produtoDB.payload_json || "{}"); } catch (_) { /* noop */ }
            const payloadPatched = {
              ...payloadProduto,
              ...fiscal,
              ncm:       dadosCorrigidos.ncm       || null,
              cfop:      dadosCorrigidos.cfop      || null,
              cfop_nfce: dadosCorrigidos.cfop      || null,
              csosn:     dadosCorrigidos.csosn     || null,
              cst:       dadosCorrigidos.cst_icms  || null,
              orig:      dadosCorrigidos.origem    || "0",
              cst_pis:   dadosCorrigidos.cst_pis   || "07",
              cst_cofins: dadosCorrigidos.cst_cofins || "07",
            };
            await ProdutoLocalRepository.atualizarFiscal(id_produto, {
              ...fiscal,
              payload_json: JSON.stringify(payloadPatched),
            });
          }
        } catch (e) {
          console.error("[NovaVenda] Erro ao atualizar produto_local:", e);
        }
      }

      // 3. Enfileirar na fila_sync para sincronização com servidor
      try {
        const api = getElectronAPI();
        if (api && api.sync) {
          await api.sync.enqueue({
            tipo: "produto_correcao_fiscal",
            uuid_referencia:
              item.produto_original?.uuid ??
              item.produto_original?.uuid_sync ??
              null,
            payload: {
              id_produto,
              descricao:   item.descricao,
              ...fiscal,
              ncm:         dadosCorrigidos.ncm        || null,
              cfop:        dadosCorrigidos.cfop       || null,
              orig:        dadosCorrigidos.origem     || "0",
              csosn:       dadosCorrigidos.csosn      || null,
              cst:         dadosCorrigidos.cst_icms   || null,
              cst_pis:     dadosCorrigidos.cst_pis    || "07",
              cst_cofins:  dadosCorrigidos.cst_cofins || "07",
            },
          });
        }
      } catch (e) {
        console.warn("[NovaVenda] Erro ao enfileirar sync:", e);
      }

      // 4. Fechar modal, mostrar feedback e habilitar retry
      this.modalCorrecaoFiscal = { visivel: false, item: null, itemIdx: -1, erroInfo: null };
      this.erroFiscalCorrigido = true;
      this.mensagemErro = `Dados fiscais de "${item.descricao}" atualizados. Tente finalizar novamente.`;
    },

    // ---------------------------------------------------------------

    async imprimirNfce(uuid_venda_terminal) {
      if (!uuid_venda_terminal) return;
      try {
        const api = getElectronAPI();
        if (!api) return;

        const config = await api.impressoraConfig.buscar();

        if (config && config.imprimir_automatico && config.printer_name) {
          if (config.abrir_preview) {
            // Abre modal de visualização antes de imprimir
            this.modalDanfe = { visivel: true, uuid: uuid_venda_terminal };
          } else {
            // Impressão silenciosa — não aguarda para não travar nova venda
            api.printer
              .imprimirDanfeNfce({
                uuid_venda_terminal,
                printerName: config.printer_name,
                silent: true,
                copies: config.numero_vias ?? 1,
              })
              .catch((e) =>
                console.warn("[NovaVenda] Impressão silenciosa falhou:", e?.message ?? e)
              );
          }
        } else {
          // Sem impressora automática: abre modal para o operador imprimir/baixar
          this.modalDanfe = { visivel: true, uuid: uuid_venda_terminal };
        }
      } catch (e) {
        console.warn("[NovaVenda] Falha ao iniciar impressão:", e?.message ?? e);
        // Fallback: abre modal mesmo assim
        this.modalDanfe = { visivel: true, uuid: uuid_venda_terminal };
      }
    },

    async imprimirComprovantePreVenda(uuid_venda_terminal, { silenciosa = false } = {}) {
      if (!uuid_venda_terminal) return;
      try {
        const api = getElectronAPI();
        if (!api?.printer?.imprimirComprovantePreVenda) {
          throw new Error("Impressão de comprovante de pré-venda não disponível.");
        }

        const config = await api.impressoraConfig.buscar();

        await api.printer.imprimirComprovantePreVenda({
          uuid_venda_terminal,
          printerName: config?.printer_name ?? null,
          copies: config?.numero_vias ?? 1,
        });
      } catch (e) {
        if (silenciosa) {
          console.warn("[NovaVenda] Impressão da pré-venda falhou:", e?.message ?? e);
          this.$toast && this.$toast.warning(`Pré-venda concluída, mas não foi possível imprimir: ${e?.message ?? e}`);
          return;
        }
        this.mensagemErro = `Erro ao imprimir comprovante: ${e?.message ?? e}`;
      }
    },

    /**
     * Limpa todo o estado da venda atual.
     * NÃO limpa modalDanfe — ele pode estar exibindo o DANFE recém-finalizado.
     */
    resetarVenda({ devolverLotes = true } = {}) {
      // Ao cancelar/abandonar uma venda, libera as quantidades reservadas dos
      // lotes. Após uma venda concluída, a baixa deve permanecer no saldo local.
      if (devolverLotes) {
        for (const item of this.carrinho) {
          if (item.id_produto_lote) {
            ProdutoLoteLocalRepository.devolverQuantidadeLocal(item.id_produto_lote, item.quantidade)
              .catch((e) => console.error("[NovaVenda] Erro ao devolver lote (resetar):", e));
          }
        }
      }

      // Busca produto
      this.termoBusca = "";
      this.produtosBusca = [];
      this.buscando = false;
      this.selectedProductIndex = 0;

      // Campos de entrada de produto
      this.produtoSelecionado = null;
      this.quantidadeItem = 1;
      this.valorUnitarioItem = 0;
      this.valorTotalItem = 0;
      this.erroItem = "";
      this._cancelarEsperaBalanca();

      // Cliente
      this.termoCliente = "";
      this.clientesBusca = [];
      this.clienteSelecionado = null;
      this.modoCliente = "cadastrado";
      this.clienteManualNome = "";
      this.clienteManualDocumento = "";
      this.clienteManualExpandido = false;
      this.clienteManualNomeDraft = "";
      this.clienteManualDocumentoDraft = "";

      // Vendedor
      this.vendedorSelecionado = null;

      // Lista de preço
      this.listaPrecoSelecionada = null;
      this.listaPrecoDeCliente = false;
      this.listaPrecoAberta = false;
      this.idListaPrecoCliente = null;
      this.listaPrecoMap = {};

      // Carrinho
      this.carrinho = [];

      // Pagamentos
      this.listaPagamentos = [];
      this.valorPagamentoAtual = 0;
      this.pagamentoRapidoAtivo = null;
      if (this.formasPagamento.length) {
        this.formaPagamentoSelecionada = this.formasPagamento[0];
      }

      // Desconto / Acréscimo
      this.descontoVendaReais = 0;
      this.descontoVendaPct = 0;
      this.acrescimoVendaReais = 0;
      this.acrescimoVendaPct = 0;

      // Complementares
      this.observacaoVenda = "";
      this.complementaresAberto = false;

      // Mensagens e flags
      this.mensagemSucesso = "";
      this.mensagemErro = "";
      this.mensagemContingencia = "";
      this.avisoClienteObrigatorio = "";
      this.uuidRejeicao = null;
      this.uuidUltimaVenda = null;
      this.finalizando = false;
      this.operacaoFinalizacao = null;
      this.erroFiscalCorrigido = false;
      this.modalCorrecaoFiscal = { visivel: false, item: null, itemIdx: -1, erroInfo: null };

      // Parcelas
      this.parcelasVencimento = [];
      this.diasParcelas = [];
    },

    /**
     * Inicia o estado visual de uma nova venda:
     * garante formas de pagamento carregadas e foca o campo de busca.
     */
    iniciarNovaVenda() {
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },

    // Chamado pelo botão "Nova Venda" exibido em caso de erro/rejeição
    novaVenda() {
      this.resetarVenda();
      this.iniciarNovaVenda();
    },

    // ── Caixa Local PDV Desktop ─────────────────────────────────────────

    async carregarCaixaAberto() {
      try {
        this.caixaAberto = await CaixaPdvDesktopService.obterCaixaAberto();
        if (this.caixaAberto) {
          this.resumoCaixa = await CaixaPdvDesktopService.calcularResumoCaixa(this.caixaAberto.uuid_caixa);
        } else {
          this.resumoCaixa = null;
        }
        await this._atualizarPendentesSync();
      } catch (e) {
        console.warn("[NovaVenda] Erro ao carregar caixa:", e);
        this.caixaAberto = null;
        this.resumoCaixa = null;
      }
    },

    async _atualizarPendentesSync() {
      try {
        const pendentes = await CaixaLocalRepository.buscarPendentesSync();
        this.pendentesSync = Array.isArray(pendentes) ? pendentes.length : 0;
      } catch (e) {
        this.pendentesSync = 0;
      }
    },

    async _carregarContextoHeader() {
      try {
        const api = getElectronAPI();
        if (!api) return;
        const [info, nomeUsuario, idUsuario] = await Promise.all([
          api.terminal && api.terminal.getInfo ? api.terminal.getInfo().catch(() => null) : Promise.resolve(null),
          api.config.get("nome_usuario").catch(() => null),
          api.config.get("id_usuario").catch(() => null),
        ]);
        this.terminalNome = (info && (info.nome || info.uuid_terminal)) || "PDV";
        this.terminalSerie = (info && (info.serie_nfe || info.serie)) || "";
        this.terminalUuid = (info && info.uuid_terminal) || "";
        this.operadorNome = nomeUsuario || (idUsuario ? `Usuário #${idUsuario}` : "");
      } catch (e) {
        console.warn("[NovaVenda] Erro ao carregar contexto do header:", e);
      }
    },

    _onConexaoChange() {
      this.online = typeof navigator !== "undefined" ? navigator.onLine : true;
      if (this.online) {
        this._sincronizarCaixaBackground();
      }
    },

    abrirConfiguracoesTopo() {
      this.$router.push({ name: "pdvDesktopConfiguracaoImpressao" }).catch(() => {});
    },

    async sairDaSessaoPdv() {
      try {
        await PdvDesktopAuthService.logout();
      } catch (_) { /* ignore */ }
      this.$router.push({ name: "pdvDesktopLogin" }).catch(() => {});
    },

    async sincronizarCaixaManual() {
      if (this.sincronizandoCaixa) return;
      this.sincronizandoCaixa = true;
      try {
        // Sincroniza caixa pendente + dados mestre (produtos, combinações, lotes, etc.)
        await CaixaSyncService.sincronizarCaixasPendentes();
        await SyncPdvDesktopService.sincronizarCompleto();
      } catch (e) {
        if (this._tratarErroBloqueioEmpresa(e)) return;
        console.warn("[NovaVenda] Sincronização manual falhou:", e.message);
      } finally {
        this.sincronizandoCaixa = false;
        await this._atualizarPendentesSync();
      }
    },

    async recarregarCombinacoes() {
      if (this.sincronizandoCaixa) return;
      this.sincronizandoCaixa = true;
      try {
        await SyncPdvDesktopService.recarregarCombinacoes();
        console.log("[NovaVenda] Combinações recarregadas com sucesso.");
      } catch (e) {
        if (this._tratarErroBloqueioEmpresa(e)) return;
        console.warn("[NovaVenda] Recarga de combinações falhou:", e.message);
      } finally {
        this.sincronizandoCaixa = false;
      }
    },

    async _sincronizarCaixaBackground() {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      try {
        await CaixaSyncService.sincronizarCaixasPendentes();
      } catch (e) {
        if (this._tratarErroBloqueioEmpresa(e)) return;
        console.warn("[NovaVenda] Sync caixa em background falhou:", e.message);
      }
    },

    /**
     * Valida o vínculo do terminal com o back ao iniciar a tela.
     * Se estiver offline, permite operar normalmente.
     * Se o terminal tiver sido desvinculado, bloqueia operações e redireciona.
     */
    async _validarVinculoTerminalInicio() {
      try {
        await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });
        await TerminalConfigService.garantirConfiguracaoNfceLocal();
      } catch (e) {
        if (this._tratarErroConfiguracaoNfce(e)) return;

        this.terminalBloqueado = true;
        this.motivoBloqueio    = e.message;
        this.$toast && this.$toast.error(e.message);
        // Aguarda 3s para o usuário ver a mensagem e redireciona para reconfigurar
        setTimeout(() => {
          this.$router.push({
            name: TerminalConfigService.isCodigoBloqueioEmpresa(e.code)
              ? "pdvDesktopBloqueio"
              : "pdvDesktopConfiguracaoTerminal",
          }).catch(() => {});
        }, 3000);
      }
    },

    async onCaixaAberto(caixa) {
      this.caixaAberto = caixa;
      this.modalAbrirCaixa = { visivel: false, bloqueante: false };
      this.mensagemErro = "";
      await this.carregarCaixaAberto();
      this._sincronizarCaixaBackground();
    },

    async onCaixaFechado() {
      this.modalFecharCaixa = { visivel: false };
      await this.carregarCaixaAberto();
      this._sincronizarCaixaBackground();
      // Sem caixa aberto → bloqueia novas vendas
      if (!this.caixaAberto) {
        this.modalAbrirCaixa = { visivel: true, bloqueante: true };
      }
    },

    async onMovimentoRegistrado() {
      this.modalMovimento = { visivel: false, tipo: this.modalMovimento.tipo };
      await this.carregarCaixaAberto();
      this._sincronizarCaixaBackground();
    },

    abrirSangria() {
      if (!this.caixaAberto) return;
      this.modalMovimento = { visivel: true, tipo: "sangria" };
    },

    abrirSuprimento() {
      if (!this.caixaAberto) return;
      this.modalMovimento = { visivel: true, tipo: "suprimento" };
    },

    abrirFecharCaixa() {
      if (!this.caixaAberto) return;
      this.modalFecharCaixa = { visivel: true };
    },

    abrirResumoCaixa() {
      if (this.$route.name === "pdvDesktopCaixaResumo") return;
      this.$router.push({ name: "pdvDesktopCaixaResumo" });
    },
  },
};
</script>

<style scoped>
/* ── Terminal desvinculado banner ───────────────────────────────────── */
.pdv-terminal-bloqueado-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #c53030;
  color: #fff;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  z-index: 9999;
}
.pdv-terminal-bloqueado-banner i {
  font-size: 18px;
  flex-shrink: 0;
}
.pdv-terminal-bloqueado-redirect {
  margin-left: auto;
  font-size: 12px;
  opacity: 0.85;
  font-weight: 400;
}

/* ── PDV Desktop Page layout (wrapper com header operacional) ──────── */
.pdv-desktop-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #f5f7fa;
}

/* ── PDV Desktop Screen layout ─────────────────────────────────────── */
.pdv-desktop-screen {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  gap: 12px;
  flex: 1;
  min-height: 0;
  padding: 8px;
  overflow: hidden;
  background: #f5f7fa;
  box-sizing: border-box;
}

.pdv-left-panel,
.pdv-right-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

/* ── Seções ─────────────────────────────────────────────────────────── */
.nova-venda__section {
  background: #fff;
  border-radius: 10px;
  padding: 1.1rem 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.nova-venda__section-title {
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #4a5568;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nova-venda__optional {
  font-size: 0.7rem;
  font-weight: 400;
  text-transform: none;
  color: #a0aec0;
  background: #edf2f7;
  border-radius: 10px;
  padding: 1px 7px;
  letter-spacing: 0;
}

.pdv-badge-obrigatorio {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: none;
  color: #c53030;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 10px;
  padding: 1px 7px;
  letter-spacing: 0;
}

.pdv-aviso-cliente {
  margin: 0 0 6px;
  padding: 6px 12px;
  background: #fff5f5;
  border-left: 3px solid #fc8181;
  border-radius: 4px;
  font-size: 0.78rem;
  color: #c53030;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pdv-badge-cliente {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #c53030;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 0px 5px;
  margin-left: 5px;
  vertical-align: middle;
  white-space: nowrap;
}

.pdv-outros-exige-cliente {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #c53030;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 1px 6px;
  margin-left: auto;
  white-space: nowrap;
}

.nova-venda__cart-count {
  font-size: 0.72rem;
  font-weight: 400;
  text-transform: none;
  color: #718096;
  letter-spacing: 0;
}

/* ── Campo de busca ─────────────────────────────────────────────────── */
.nova-venda__search-wrap {
  position: relative;
}

.nova-venda__search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.82rem;
  pointer-events: none;
}

.nova-venda__search-input {
  width: 100%;
  padding: 8px 10px 8px 34px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  font-size: 0.88rem;
  outline: none;
  transition: border-color 0.15s;
  background: #f7fafc;
  box-sizing: border-box;
}

.nova-venda__search-input:focus {
  border-color: #4299e1;
  background: #fff;
}

/* ── Lista de resultados ─────────────────────────────────────────────── */
.nova-venda__results {
  margin-top: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  overflow: hidden;
  max-height: 220px;
  overflow-y: auto;
}

.nova-venda__result-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f4f8;
  transition: background 0.1s;
}

.nova-venda__result-item:last-child {
  border-bottom: none;
}

.nova-venda__result-item:hover {
  background: #ebf8ff;
}

.nova-venda__result-item--active {
  background: #ffe8e1;
  border-left: 4px solid #ff6b4a;
  font-weight: 600;
}

.nova-venda__result-item--active .nova-venda__result-nome {
  color: #c43d1b;
}

.nova-venda__result-item--active .nova-venda__result-preco {
  color: #c43d1b;
}

.nova-venda__result-nome {
  font-size: 0.88rem;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 6px;
}

.nova-venda__result-cod-inline {
  font-size: 0.75rem;
  font-weight: 700;
  color: #fff;
  background: #718096;
  border-radius: 4px;
  padding: 1px 6px;
  white-space: nowrap;
  flex-shrink: 0;
}

.nova-venda__result-info {
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
}

.nova-venda__result-cod {
  font-size: 0.75rem;
  color: #718096;
}

.nova-venda__result-preco {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--primary-color, #ee7361);
  display: flex;
  align-items: center;
  gap: 5px;
}

.nova-venda__result-preco--lista-wrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #ebf8f0;
  border: 1px solid #68d391;
  border-radius: 6px;
  padding: 1px 6px 1px 4px;
}

.nova-venda__result-preco--lista-badge {
  font-size: 0.62rem;
  font-weight: 800;
  color: #fff;
  background: #38a169;
  border-radius: 3px;
  padding: 0px 4px;
  letter-spacing: 0.03em;
  line-height: 1.5;
}

.nova-venda__result-preco--lista {
  font-size: 0.9rem;
  font-weight: 800;
  color: #1a202c;
}

.nova-venda__result-preco--original {
  font-size: 0.74rem;
  font-weight: 500;
  color: #1a202c;
  text-decoration: line-through;
  text-decoration-color: #fc8181;
}

.nova-venda__result-item--active .nova-venda__result-preco--lista-wrap {
  background: #d4edda;
  border-color: #38a169;
}

.nova-venda__result-item--active .nova-venda__result-preco--lista-badge {
  background: rgba(255,255,255,0.9);
  color: #276749;
}

.nova-venda__result-item--active .nova-venda__result-preco--lista {
  color: #1a202c;
}

.nova-venda__result-item--active .nova-venda__result-preco--original {
  color: #1a202c;
}

.nova-venda__no-results {
  margin-top: 6px;
  font-size: 0.82rem;
  color: #a0aec0;
  text-align: center;
  padding: 8px 0;
}

/* ── Cliente selecionado ─────────────────────────────────────────────── */
.nova-venda__selected-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ebfaf1;
  border: 1px solid #9ae6b4;
  border-radius: 7px;
  padding: 8px 12px;
  font-size: 0.88rem;
  color: #276749;
  font-weight: 600;
}

.nova-venda__remove-badge {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 1.1rem;
  line-height: 1;
  color: #276749;
  cursor: pointer;
  padding: 0 2px;
  opacity: 0.7;
}

.nova-venda__remove-badge:hover {
  opacity: 1;
}

.nova-venda__cliente-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.nova-venda__cliente-toolbar-search {
  flex: 1;
}

.nova-venda__cliente-toolbar-btn {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 7px;
  padding: 6px 8px;
  min-height: 34px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.nova-venda__cliente-toolbar-btn:hover {
  border-color: rgba(238, 115, 97, 0.45);
  color: var(--primary-color, #ee7361);
  background: #fff;
}

.nova-venda__cliente-toolbar-btn--manual {
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.nova-venda__cliente-manual-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}

.nova-venda__cliente-manual-panel {
  margin-top: 6px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 8px;
}

.nova-venda__cliente-manual-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
}

.nova-venda__cliente-manual-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.25px;
  border-radius: 7px;
  padding: 5px 8px;
  cursor: pointer;
}

.nova-venda__cliente-manual-btn:hover {
  border-color: #94a3b8;
}

.nova-venda__cliente-manual-btn--apply {
  background: var(--primary-color, #ee7361);
  border-color: var(--primary-color, #ee7361);
  color: #fff;
}

.nova-venda__cliente-manual-btn--apply:hover {
  background: #e05a47;
  border-color: #e05a47;
}

.nova-venda__cliente-manual-hint {
  display: block;
  margin-top: 5px;
  font-size: 0.72rem;
  color: #94a3b8;
}

.nova-venda__selected-badge--manual {
  margin-top: 6px;
  background: #fff4ec;
  border-color: rgba(238, 115, 97, 0.35);
  color: #9a3412;
}

.nova-venda__select--inline {
  width: 100%;
  flex: unset;
}

/* ── Lista de preço (chip discreto) ─────────────────────────────────── */
.nova-venda__lista-preco-bar {
  margin-top: 6px;
  min-height: 22px;
  display: flex;
  align-items: center;
}

.nova-venda__lista-preco-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.75rem;
  color: #a0aec0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s;
}

.nova-venda__lista-preco-btn:hover {
  color: #4a5568;
}

.nova-venda__lista-preco-btn i {
  font-size: 0.7rem;
}

.nova-venda__lista-preco-select {
  font-size: 0.78rem;
  padding: 2px 6px;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  background: #f7fafc;
  color: #4a5568;
  outline: none;
  width: 100%;
}

.nova-venda__lista-preco-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  color: #276749;
  background: #f0fff4;
  border: 1px solid #9ae6b4;
  border-radius: 20px;
  padding: 2px 8px 2px 7px;
}

.nova-venda__lista-preco-chip i {
  font-size: 0.65rem;
}

.nova-venda__lista-preco-chip__remove {
  background: none;
  border: none;
  padding: 0 0 0 2px;
  font-size: 0.9rem;
  line-height: 1;
  color: #276749;
  cursor: pointer;
  opacity: 0.6;
}

.nova-venda__lista-preco-chip__remove:hover {
  opacity: 1;
}

/* ── Pagamento ──────────────────────────────────────────────────────── */
.nova-venda__payment-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.nova-venda__select {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  font-size: 0.85rem;
  background: #f7fafc;
  outline: none;
}

.nova-venda__select:focus {
  border-color: #4299e1;
  background: #fff;
}

.nova-venda__input-valor {
  width: 110px;
  padding: 7px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  font-size: 0.85rem;
  text-align: right;
  background: #f7fafc;
  outline: none;
}

.nova-venda__input-valor:focus {
  border-color: #4299e1;
  background: #fff;
}

.nova-venda__add-payment-btn {
  padding: 7px 14px;
  background: var(--primary-color, #ee7361);
  color: #fff;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.15s;
}

.nova-venda__add-payment-btn:hover:not(:disabled) {
  background: #e05a47;
}

.nova-venda__add-payment-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.nova-venda__fill-total-btn {
  margin-top: 6px;
  width: 100%;
  padding: 5px;
  background: none;
  border: 1px dashed rgba(238, 115, 97, 0.45);
  border-radius: 6px;
  color: var(--primary-color, #ee7361);
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.15s;
}

.nova-venda__fill-total-btn:hover {
  background: #fde8e5;
}

/* ── Preview de parcelas a prazo ─────────────────────────────────────── */
.nova-venda__parcelas-preview {
  margin-top: 8px;
  background: #fffbeb;
  border: 1px solid #f6e05e;
  border-radius: 7px;
  padding: 8px 10px;
  font-size: 0.8rem;
}

.nova-venda__parcelas-preview__title {
  font-weight: 700;
  color: #744210;
  margin-bottom: 5px;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.nova-venda__parcela-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  color: #2d3748;
  border-bottom: 1px dashed #fef08a;
}

.nova-venda__parcela-item:last-child {
  border-bottom: none;
}

.nova-venda__parcela-num {
  font-weight: 700;
  color: #92400e;
  min-width: 24px;
}

.nova-venda__parcela-val {
  font-weight: 700;
  flex: 1;
}

.nova-venda__parcela-date-input {
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.75rem;
  color: #4a5568;
  background: #fff;
  outline: none;
  cursor: pointer;
}

.nova-venda__parcela-date-input:focus {
  border-color: #f6ad55;
  box-shadow: 0 0 0 2px rgba(246,173,85,.25);
}

/* ── Pagamento rápido ────────────────────────────────────────────────── */
.pdv-payment-cards {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  margin-bottom: 6px;
}

.pdv-payment-outros-wrapper {
  position: relative;
  height: 100%;
}

.pdv-payment-card--outros {
  width: 100%;
  height: 100%;
}

.pdv-payment-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px 3px;
  background: #f7fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 7px;
  font-size: 0.66rem;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  min-width: 0;
  text-align: center;
}

.pdv-payment-card i {
  font-size: 0.85rem;
  line-height: 1;
}

.pdv-payment-card:hover {
  background: #fde8e5;
  border-color: rgba(238, 115, 97, 0.5);
  color: #e05a47;
}

.pdv-payment-card--active {
  background: #fde8e5;
  border-color: var(--primary-color, #ee7361);
  color: #e05a47;
  box-shadow: 0 0 0 2px rgba(238, 115, 97, 0.25);
}

.pdv-payment-card--active i {
  color: var(--primary-color, #ee7361);
}

.pdv-payment-card__chevron {
  margin-left: auto;
  font-size: 0.7rem;
  opacity: 0.55;
}

.pdv-payment-card__atalho {
  display: inline-block;
  margin-top: 2px;
  padding: 1px 5px;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.3px;
  border-radius: 4px;
  background: #edf2f7;
  color: #4a5568;
}

.pdv-payment-card--active .pdv-payment-card__atalho {
  background: var(--primary-color, #ee7361);
  color: #fff;
}

.pdv-outros-overlay {
  position: fixed;
  inset: 0;
  z-index: 99;
}

.pdv-outros-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  left: auto;
  z-index: 100;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
}

.pdv-outros-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 14px;
  background: none;
  border: none;
  text-align: left;
  font-size: 0.85rem;
  color: #2d3748;
  cursor: pointer;
  white-space: nowrap;
}

.pdv-outros-menu__item:hover {
  background: #fde8e5;
  color: var(--primary-color, #ee7361);
}

.pdv-outros-menu__empty {
  padding: 8px 14px;
  color: #a0aec0;
  font-size: 0.8rem;
}

.nova-venda__payments-list {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nova-venda__payment-item {
  display: flex;
  align-items: center;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.85rem;
}

.nova-venda__payment-desc {
  flex: 1;
  color: #2d3748;
}

.nova-venda__payment-val {
  font-weight: 700;
  color: #276749;
  margin-right: 8px;
}

/* ── Product entry row ─────────────────────────────────────────────── */
.product-entry-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-bottom: 1px solid #f0f4f8;
  flex-shrink: 0;
}

.product-entry-search {
  flex: 1;
}

.pdv-insert-btn {
  padding: 7px 14px;
  background: var(--primary-color, #ee7361);
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  flex-shrink: 0;
}

.pdv-insert-btn:hover {
  background: #e05a47;
}

.pdv-insert-btn:disabled {
  opacity: .55;
  cursor: not-allowed;
}

/* ── Product entry area ──────────────────────────────────────────────── */
.product-entry-area {
  flex-shrink: 0;
}

.pdv-balanca-status {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  margin: 4px 10px 9px;
  padding: 10px 13px;
  border: 1px solid #bfdbfe;
  border-radius: 9px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: .94rem;
  box-shadow: 0 2px 8px rgba(29, 78, 216, .08);
}

.pdv-balanca-status > i {
  flex: 0 0 auto;
  font-size: 1.2rem;
}

.pdv-balanca-status > span {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.pdv-balanca-status strong {
  font-size: .95rem;
  line-height: 1.25;
}

.pdv-balanca-status small {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .78);
  font-size: .9rem;
  font-weight: 800;
  color: inherit;
  white-space: nowrap;
}

.pdv-balanca-status button {
  border: 1px solid currentColor;
  border-radius: 7px;
  padding: 7px 10px;
  background: #fff;
  color: inherit;
  font-size: .8rem;
  font-weight: 600;
  white-space: nowrap;
}

.pdv-balanca-status--erro {
  background: #fff1f2;
  border-color: #fecdd3;
  color: #be123c;
}

.pdv-entry-input {
  padding: 5px 7px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.82rem;
  background: #f7fafc;
  outline: none;
  min-width: 0;
  transition: border-color 0.15s;
  appearance: textfield;
  -moz-appearance: textfield;
}

.pdv-entry-input::-webkit-outer-spin-button,
.pdv-entry-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pdv-entry-input:focus {
  border-color: #4299e1;
  background: #fff;
}

.pdv-entry-input:disabled {
  background: #edf2f7;
  color: #a0aec0;
  cursor: not-allowed;
}

.pdv-entry-input--qty {
  width: 62px;
  text-align: center;
}

.pdv-entry-input--preco {
  width: 88px;
  text-align: right;
}

.pdv-entry-total {
  width: 88px;
  text-align: right;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--primary-color, #ee7361);
  white-space: nowrap;
  padding: 0 4px;
  flex-shrink: 0;
}

.pdv-entry-total--vazio {
  color: #cbd5e0;
}

.pdv-entry-error {
  font-size: 0.75rem;
  color: #c53030;
  padding: 2px 10px 3px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.pdv-entry-feedback {
  min-height: 44px;
  margin: 4px 10px 9px;
  padding: 9px 12px;
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 9px;
  font-size: 0.92rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 9px;
  box-shadow: 0 2px 8px rgba(22, 101, 52, .08);
}

.pdv-entry-feedback i {
  flex: 0 0 auto;
  font-size: 1.15rem;
}

.nova-venda__search-input--selecionado {
  border-color: #68d391;
  background: #f0fff4;
  color: #276749;
  font-weight: 600;
}

.pdv-results-dropdown {
  margin: 0 12px 4px;
}

.pdv-no-results {
  margin: 4px 12px 0;
}

/* ── Cart table wrapper ─────────────────────────────────────────────── */
.cart-table-wrapper {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 12px;
}

.cart-table-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a0aec0;
  border-bottom: 1px solid #f0f4f8;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}

.cart-col-produto { flex: 1; }
.cart-col-qty     { width: 132px; text-align: center; }
.cart-col-unit    { width: 80px; text-align: right; }
.cart-col-total   { width: 80px; text-align: right; }
.cart-col-action  { width: 24px; }

/* ── Cart summary footer ─────────────────────────────────────────────── */
.cart-summary-footer {
  flex-shrink: 0;
  display: flex;
  gap: 6px;
  padding: 6px 8px;
  border-top: 1px solid #f0f4f8;
  background: #f8fafc;
}

.cart-summary-footer--stacked {
  flex-direction: column;
  gap: 6px;
}

.pdv-sale-footer-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 2px 2px 0;
}

.pdv-sale-footer-summary-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  gap: 1px;
}

.pdv-sale-footer-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--pdv-muted, #64748b);
  white-space: nowrap;
  line-height: 1.1;
}

.pdv-sale-footer-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--pdv-text, #334155);
  line-height: 1.1;
  white-space: nowrap;
}

.pdv-sale-footer-value.discount {
  color: var(--bs-danger, #dc3545);
}

.pdv-sale-footer-value.addition {
  color: var(--bs-success, #198754);
}

.pdv-sale-total-final {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--primary-color, #ee7361) 68%, #ffffff 32%);
  background: var(--primary-color, #ee7361);
  color: #fff;
  text-align: center;
}

.pdv-sale-total-final-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.1;
}

.pdv-sale-total-final-value {
  font-size: 34px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}

/* ── Right panel ─────────────────────────────────────────────────────── */
.checkout-header {
  padding: 6px 12px;
  border-bottom: 1px solid #f0f4f8;
  flex-shrink: 0;
}

.checkout-header__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.checkout-header__title i {
  color: #38a169;
}

.pdv-alert-inline {
  margin: 4px 12px 0;
  flex-shrink: 0;
}

.pdv-right-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 10px 4px;
}

.pdv-section-label {
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #4a5568;
  margin: 6px 0 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ── Customer section ────────────────────────────────────────────────── */
.customer-section {
  padding-bottom: 4px;
  border-bottom: 1px solid #f0f4f8;
}

/* ── Extra info section ──────────────────────────────────────────────── */
.extra-info-section {
  padding-bottom: 4px;
  border-bottom: 1px solid #f0f4f8;
}

/* ── Checkout values section ─────────────────────────────────────────── */
.checkout-values-section {
  padding-bottom: 3px;
  border-bottom: 1px solid #f0f4f8;
}

.checkout-total-a-pagar {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 7px;
  background: #fde8e5;
  border: 1px solid rgba(238, 115, 97, 0.3);
  border-radius: 10px;
  padding: 7px 10px;
  margin-top: 5px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary-color, #ee7361);
}

.checkout-total-a-pagar__label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(36, 42, 48, 0.8);
}

.checkout-total-a-pagar__forma {
  font-size: 11px;
  font-weight: 600;
  color: #4a5568;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-transform: none;
  letter-spacing: 0;
}

.checkout-total-a-pagar__restante {
  color: #718096;
  font-weight: 600;
}

.checkout-total-a-pagar__edit {
  display: flex;
  align-items: center;
  gap: 6px;
}

.checkout-total-a-pagar__valor {
  font-size: 24px;
  font-weight: 800;
  line-height: 1.05;
  color: var(--primary-color, #ee7361);
  white-space: nowrap;
}

/* ── Payment section ─────────────────────────────────────────────────── */
.payment-section {
  padding-bottom: 4px;
  border-bottom: 1px solid #f0f4f8;
}

/* ── Payment summary section ─────────────────────────────────────────── */
.payment-summary-section {
  position: sticky;
  bottom: 0;
  z-index: 3;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.94), #ffffff 24%);
  border-top: 1px solid #eef2f7;
  margin-top: 4px;
  padding-top: 2px;
  padding-bottom: 4px;
}

.pdv-payment-summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.83rem;
  color: #4a5568;
  padding: 2px 0;
}

.pdv-payment-summary-row__value {
  font-weight: 700;
  color: #2d3748;
}

.pdv-payment-summary-row__value--restante {
  color: #c53030;
}

/* ── Checkout actions footer ─────────────────────────────────────────── */
.checkout-actions-footer {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
  padding: 8px 10px;
  border-top: 1px solid #f0f4f8;
  background: #f7fafc;
}

.checkout-action-btn {
  /* Default span: 2 colunas (3 botões por linha em grade de 6) */
  grid-column: span 2;
  min-width: 0;
  padding: 8px 6px;
  border: none;
  border-radius: 7px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
}

.checkout-action-btn > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.checkout-action-btn--finalizar {
  min-height: 38px;
}

.checkout-action-btn__conteudo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.checkout-action-btn__conteudo > i {
  flex: 0 0 auto;
}

.checkout-action-btn__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.checkout-action-btn--primary {
  background: #38a169;
  color: #fff;
  grid-column: span 3;
}

.checkout-action-btn--primary:hover:not(:disabled) {
  background: #2f855a;
}

.checkout-action-btn--primary:disabled {
  background: #a0aec0;
  cursor: default;
}

.checkout-action-btn--pre-venda {
  background: #f6c343;
  color: #2d3748;
}

.checkout-action-btn--pre-venda:hover:not(:disabled) {
  background: #e5b22f;
}

.checkout-action-btn--pre-venda:disabled {
  background: #edf2f7;
  color: #718096;
  cursor: default;
}

.checkout-action-btn--success {
  background: #48bb78;
  color: #fff;
  grid-column: span 3;
}

.checkout-action-btn--success:hover {
  background: #38a169;
}

.checkout-action-btn--secondary {
  background: #edf2f7;
  color: #4a5568;
}

.checkout-action-btn--secondary:hover {
  background: #e2e8f0;
}

.checkout-action-btn--danger {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  color: #c53030;
  grid-column: span 3;
}

.checkout-action-btn--danger:hover {
  background: #fed7d7;
}

.checkout-action-btn.checkout-action-btn--linha-final {
  grid-column: span 2;
}

.checkout-action-btn__atalho {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  padding: 1px 6px;
  font-size: 0.68rem;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.12);
  color: inherit;
  border-radius: 4px;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.checkout-action-btn--primary .checkout-action-btn__atalho {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.checkout-action-btn--danger .checkout-action-btn__atalho {
  background: #fed7d7;
  color: #9b2c2c;
}

.nova-venda__cart-empty {
  text-align: center;
  padding: 2rem 1rem;
  color: #a0aec0;
}

.nova-venda__cart-empty i {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  display: block;
}

.nova-venda__cart-empty p {
  font-size: 0.9rem;
  margin: 0 0 4px 0;
}

.nova-venda__cart-empty small {
  font-size: 0.75rem;
}

.nova-venda__cart-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
}

/* ── Item do carrinho ────────────────────────────────────────────────── */
.nova-venda__cart-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  font-size: 0.82rem;
}

.nova-venda__cart-item-info {
  flex: 1;
  min-width: 0;
}

.nova-venda__cart-item-nome {
  display: block;
  font-weight: 600;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nova-venda__cart-item-codbarras {
  display: block;
  font-size: 0.7rem;
  color: #a0aec0;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nova-venda__cart-item-unit {
  font-size: 0.72rem;
  color: #718096;
}

.nova-venda__cart-item-lote {
  font-size: 0.72rem;
  color: #0f766e;
  font-weight: 500;
}

.nova-venda__cart-item-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 132px;
  flex-shrink: 0;
}

.nova-venda__qty-btn {
  width: 24px;
  height: 24px;
  background: #edf2f7;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a5568;
  transition: background 0.1s;
}

.nova-venda__qty-btn:hover {
  background: #e2e8f0;
}

.nova-venda__qty-input {
  width: 70px;
  text-align: center;
  padding: 3px 4px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  outline: none;
}

.nova-venda__cart-item-unit-col {
  width: 80px;
  flex-shrink: 0;
  text-align: right;
  font-size: 0.82rem;
  color: #4a5568;
  white-space: nowrap;
}

.nova-venda__cart-item-total {
  font-weight: 700;
  color: var(--primary-color, #ee7361);
  white-space: nowrap;
  width: 80px;
  flex-shrink: 0;
  text-align: right;
  font-size: 0.85rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.nova-venda__cart-item-total-bruto {
  font-size: 0.7rem;
  font-weight: 500;
  color: #a0aec0;
  text-decoration: line-through;
  line-height: 1;
}

/* Badges de ajustes (desconto/acréscimo) na linha do item */
.nova-venda__cart-item-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;
}

.nova-venda__cart-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.66rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  line-height: 1.3;
}

.nova-venda__cart-badge--desc {
  background: #fed7d7;
  color: #9b2c2c;
}

.nova-venda__cart-badge--acres {
  background: #c6f6d5;
  color: #22543d;
}

.nova-venda__cart-badge--atacado {
  background: #fde8e5;
  color: var(--primary-color, #ee7361);
  font-weight: 600;
}

/* Botão de desconto/acréscimo por item */
.nova-venda__desc-btn {
  background: none;
  border: none;
  color: var(--primary-color, #ee7361);
  cursor: pointer;
  font-size: 0.72rem;
  padding: 1px 4px;
  border-radius: 3px;
  opacity: 0.9;
  flex-shrink: 0;
  transition: opacity 0.15s, color 0.15s;
}

.nova-venda__desc-btn:hover {
  opacity: 1;
  color: #e05a47;
}

/* Estado: item selecionado no carrinho (para atalho Ctrl+D) */
.nova-venda__cart-item--selecionado {
  background: #fde8e5 !important;
  box-shadow: inset 3px 0 0 var(--primary-color, #ee7361);
}

/* ── Botão remover ───────────────────────────────────────────────────── */
.nova-venda__remove-btn {
  background: none;
  border: none;
  color: #fc8181;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0 2px;
  transition: color 0.1s;
}

.nova-venda__remove-btn:hover {
  color: #c53030;
}

.nova-venda__corrigir-btn {
  background: none;
  border: none;
  color: #e67e22;
  cursor: pointer;
  font-size: 0.7rem;
  padding: 1px 3px;
  border-radius: 3px;
  opacity: 0.9;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.nova-venda__corrigir-btn:hover {
  opacity: 1;
  color: #d35400;
}

/* ── Totais ─────────────────────────────────────────────────────────── */
.nova-venda__totals {
  border-top: 1px solid #e2e8f0;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
}

/* Total principal em destaque */
.nova-venda__total-principal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #1a365d;
  color: #fff;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 10px;
}

.nova-venda__total-principal__label {
  font-size: 0.82rem;
  font-weight: 600;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.nova-venda__total-principal__valor {
  font-size: 1.9rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1;
}

.nova-venda__total-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #4a5568;
  padding: 3px 0;
}

.nova-venda__total-row span:last-child {
  font-weight: 700;
  color: #2d3748;
}

.nova-venda__ajustes-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 8px;
}

/* Linha de desconto/acréscimo com inputs inline */
.nova-venda__total-desconto-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.82rem;
  color: #4a5568;
  padding: 4px 6px;
  gap: 6px;
  border: 1px solid #edf2f7;
  border-radius: 7px;
  background: #f8fafc;
  min-width: 0;
}

.nova-venda__total-desconto-label {
  min-width: 56px;
  font-weight: 600;
  flex-shrink: 0;
}

.nova-venda__total-inputs {
  display: flex;
  gap: 4px;
  min-width: 0;
}

.nova-venda__total-input-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  overflow: hidden;
  background: #f7fafc;
}

.nova-venda__total-input {
  width: 52px;
  border: none;
  padding: 3px 4px;
  font-size: 0.76rem;
  text-align: right;
  background: transparent;
  outline: none;
}

.nova-venda__total-input-suffix {
  font-size: 0.68rem;
  color: #718096;
  padding: 0 4px 0 1px;
  white-space: nowrap;
}

/* Troco em destaque */
.nova-venda__troco-destaque {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fffbeb;
  border: 2px solid #f6e05e;
  border-radius: 9px;
  padding: 10px 14px;
  margin-top: 8px;
  font-size: 0.88rem;
  font-weight: 700;
  color: #744210;
}

.nova-venda__troco-destaque i {
  font-size: 1rem;
  color: #d69e2e;
}

.nova-venda__troco-destaque__valor {
  margin-left: auto;
  font-size: 1.35rem;
  font-weight: 800;
  color: #b7791f;
}

/* ── Informações complementares ─────────────────────────────────────── */
.nova-venda__complementares {
  margin-top: 0.6rem;
  border-top: 1px solid #f0f4f8;
  padding-top: 0.5rem;
}

.nova-venda__complementares-toggle {
  background: none;
  border: none;
  font-size: 0.75rem;
  color: #a0aec0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  transition: color 0.15s;
}

.nova-venda__complementares-toggle:hover {
  color: #4a5568;
}

.nova-venda__complementares-body {
  margin-top: 8px;
}

.nova-venda__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #718096;
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.nova-venda__textarea {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  font-size: 0.85rem;
  resize: vertical;
  outline: none;
  background: #f7fafc;
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.nova-venda__textarea:focus {
  border-color: #4299e1;
  background: #fff;
}

/* ── Alertas ─────────────────────────────────────────────────────────── */
.nova-venda__alert {
  border-radius: 7px;
  padding: 10px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 0.75rem;
  display: flex;
  align-items: flex-start;
}

.nova-venda__alert--success {
  background: #f0fff4;
  border: 1px solid #9ae6b4;
  color: #276749;
}

.nova-venda__alert--error {
  background: #fff5f5;
  border: 1px solid #feb2b2;
  color: #c53030;
}

.nova-venda__alert--warning {
  background: #fffbeb;
  border: 1px solid #f6ad55;
  color: #744210;
}

.nova-venda__link-btn {
  background: none;
  border: none;
  color: #c53030;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: 0.85rem;
}

/* ── Botões de ação ─────────────────────────────────────────────────── */
.nova-venda__cart-actions {
  margin-top: 0.75rem;
  display: flex;
  gap: 8px;
  flex-direction: column;
}

.nova-venda__finalize-btn {
  width: 100%;
  padding: 12px;
  background: #38a169;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.nova-venda__finalize-btn:hover:not(:disabled) {
  background: #2f855a;
}

.nova-venda__finalize-btn:disabled {
  background: #a0aec0;
  cursor: default;
}

.nova-venda__new-sale-btn {
  width: 100%;
  padding: 12px;
  background: var(--primary-color, #ee7361);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.nova-venda__new-sale-btn:hover {
  background: #e05a47;
}

.nova-venda__danfe-btn {
  width: 100%;
  padding: 10px;
  background: #38a169;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  margin-top: 6px;
}

.nova-venda__danfe-btn:hover {
  background: #276749;
}

.nova-venda__clear-btn {
  width: 100%;
  padding: 8px;
  background: none;
  border: 1px solid #fed7d7;
  color: #c53030;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s;
}

.nova-venda__clear-btn:hover {
  background: #fff5f5;
}

.nova-venda__fiscal-btn {
  width: 100%;
  padding: 8px;
  background: none;
  border: 1px solid rgba(238, 115, 97, 0.3);
  color: var(--primary-color, #ee7361);
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s;
  margin-top: 6px;
}
.nova-venda__fiscal-btn:hover { background: #fde8e5; }
.nova-venda__fiscal-btn:disabled { opacity: .55; cursor: not-allowed; }

.nova-venda__fiscal-msg {
  margin-top: 4px;
  font-size: 0.78rem;
  padding: 4px 8px;
  border-radius: 6px;
}
.nova-venda__fiscal-msg--ok  { background: #c6f6d5; color: #22543d; }
.nova-venda__fiscal-msg--erro { background: #fed7d7; color: #822727; }

/* ── Imagem do produto – resultados ─────────────────────────────────── */
.nova-venda__result-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nova-venda__result-thumb {
  width: 38px;
  height: 38px;
  object-fit: cover;
  border-radius: 5px;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
  background: #f7fafc;
}

.nova-venda__result-body {
  flex: 1;
  min-width: 0;
}

/* ── Imagem do produto – carrinho ────────────────────────────────────── */
.nova-venda__cart-item-thumb {
  width: 34px;
  height: 34px;
  object-fit: cover;
  border-radius: 5px;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
  background: #f7fafc;
}

/* ── Ícone na lista de pagamentos ───────────────────────────────────────── */
.nova-venda__payment-icon {
  font-size: 0.9rem;
  color: #718096;
  width: 18px;
  flex-shrink: 0;
  text-align: center;
}

/* ── Resumo de pagamento (cards) ───────────────────────────────────────── */
.pdv-pay-summary-cards {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding-top: 2px;
}

.pdv-pay-summary-card {
  flex: 1;
  min-width: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 8px;
  gap: 2px;
}

.pdv-pay-summary-card__label {
  font-size: 0.67rem;
  font-weight: 600;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.pdv-pay-summary-card__label i {
  font-size: 0.7rem;
}

.pdv-pay-summary-card__value {
  font-size: 0.9rem;
  font-weight: 700;
  color: #2d3748;
}

.pdv-pay-summary-card--restante {
  border-color: #feb2b2;
  background: #fff5f5;
}

.pdv-pay-summary-card__value--restante {
  color: #c53030;
}

.pdv-pay-summary-card--troco {
  background: #fffbeb;
  border: 2px solid #f6e05e;
}

.pdv-pay-summary-card--troco .pdv-pay-summary-card__label {
  color: #92400e;
}

.pdv-pay-summary-card__value--troco {
  font-size: 1.05rem;
  font-weight: 800;
  color: #b7791f;
}

/* ── Responsividade ───────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .pdv-desktop-screen {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;
  }

  .pdv-left-panel {
    height: 60vh;
  }

  .pdv-right-panel {
    height: auto;
    overflow: visible;
  }

  .pdv-right-scroll {
    overflow: visible;
    flex: unset;
  }

  .pdv-payment-cards {
    grid-template-columns: repeat(5, 1fr);
  }
}

/* ── Nova área de pagamento ─────────────────────────────────────── */

.pdv-valor-recebido-wrap {
  margin-top: 10px;
}

.pdv-valor-recebido-forma {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.pdv-restante-hint {
  font-size: 11px;
  color: #888;
  margin-bottom: 6px;
}

.pdv-valor-recebido-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pdv-valor-recebido-input-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  border: 2px solid color-mix(in srgb, var(--primary-color, #ee7361) 40%, #ffffff 60%);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: var(--primary-color, #ee7361);
  }
}

.pdv-valor-recebido-prefix {
  padding: 0 12px;
  font-weight: 700;
  color: var(--primary-color, #ee7361);
  font-size: 20px;
  background: #fff2ef;
  align-self: stretch;
  display: flex;
  align-items: center;
  border-right: 1px solid rgba(238, 115, 97, 0.25);
}

.pdv-valor-recebido-input {
  border: none;
  outline: none;
  padding: 8px 12px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  height: 50px;
  text-align: right;
  width: 100%;
  color: #222;
  background: transparent;
}

.pdv-adicionar-pagamento-btn {
  padding: 10px 16px;
  background: var(--primary-color, #ee7361);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #e05a47;
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
}

/* Lista de pagamentos adicionados */

.pdv-pagamentos-adicionados {
  margin-top: 8px;
}

.pdv-pagamentos-adicionados__titulo {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.pdv-pagamento-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 6px;
  background: #f7f9fb;
  margin-bottom: 3px;
  border: 1px solid #e5eaf0;
}

.pdv-pagamento-item__icone {
  font-size: 16px;
  flex-shrink: 0;
}

.pdv-pagamento-item__info {
  flex: 1;
  min-width: 0;
}

.pdv-pagamento-item__descricao {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pdv-pagamento-item__parcelas {
  font-size: 11px;
  color: #888;
}

.pdv-pagamento-item__valor {
  font-size: 17px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  flex-shrink: 0;
}

.pdv-pagamento-item__remover {
  background: none;
  border: none;
  cursor: pointer;
  color: #c0392b;
  font-size: 16px;
  padding: 0 4px;
  border-radius: 4px;
  line-height: 1;
  transition: background 0.1s;
  flex-shrink: 0;

  &:hover {
    background: #fdecea;
  }
}

/* Resumo Pago / Restante / Troco */

.pdv-resumo-pag {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  border-top: 1px solid #e5eaf0;
  padding-top: 8px;
}

.pdv-resumo-pag__item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 6px 8px;
}

.pdv-resumo-pag__label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #64748b;
}

.pdv-resumo-pag__value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.05;
  color: #334155;
}

.pdv-resumo-pag__item--restante {
  .pdv-resumo-pag__label,
  .pdv-resumo-pag__value--restante {
    color: #e67e22;
    font-weight: 700;
  }
}

.pdv-resumo-pag__value--restante {
  font-size: 20px;
}

.pdv-resumo-pag__item--troco {
  grid-column: 1 / -1;
  background: #ecfdf3;
  border-color: #86efac;

  .pdv-resumo-pag__label,
  .pdv-resumo-pag__value--troco {
    color: #15803d;
    font-weight: 800;
  }
}

.pdv-resumo-pag__value--troco {
  font-size: 24px;
}

.pdv-total-payable {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
}

.pdv-payment-summary-value {
  font-size: 20px;
  font-weight: 700;
}

.pdv-change-value {
  font-size: 24px;
  font-weight: 800;
}

.pdv-payment-value-input {
  font-size: 22px;
  font-weight: 700;
  height: 50px;
  text-align: right;
}

@media (max-width: 1280px) {
  .checkout-total-a-pagar__valor,
  .pdv-total-payable {
    font-size: 22px;
  }

  .pdv-resumo-pag__value,
  .pdv-payment-summary-value {
    font-size: 18px;
  }

  .pdv-resumo-pag__value--troco,
  .pdv-change-value {
    font-size: 22px;
  }

  .pdv-valor-recebido-input,
  .pdv-payment-value-input {
    font-size: 20px;
    height: 46px;
  }
}

@media (max-width: 1024px) {
  .checkout-total-a-pagar__valor,
  .pdv-total-payable {
    font-size: 20px;
  }

  .pdv-resumo-pag__value,
  .pdv-payment-summary-value {
    font-size: 17px;
  }

  .pdv-resumo-pag__value--troco,
  .pdv-change-value {
    font-size: 20px;
  }

  .pdv-valor-recebido-prefix {
    font-size: 16px;
    padding: 0 9px;
  }

  .pdv-valor-recebido-input,
  .pdv-payment-value-input {
    font-size: 18px;
    height: 42px;
  }
}

@media (min-width: 901px) and (max-height: 860px) {
  .checkout-header {
    padding: 4px 10px;
  }

  .checkout-header__title {
    font-size: 0.88rem;
  }

  .pdv-right-scroll {
    padding: 0 8px 2px;
  }

  .pdv-section-label {
    font-size: 0.7rem;
    margin: 4px 0 3px;
  }

  .customer-section,
  .extra-info-section,
  .checkout-values-section,
  .payment-section {
    padding-bottom: 2px;
  }

  .nova-venda__total-desconto-row {
    padding: 2px 5px;
    gap: 5px;
  }

  .nova-venda__total-desconto-label {
    min-width: 52px;
    font-size: 0.76rem;
  }

  .nova-venda__total-input {
    width: 46px;
    font-size: 0.72rem;
    padding: 2px 3px;
  }

  .nova-venda__total-input-suffix {
    font-size: 0.64rem;
  }

  .checkout-total-a-pagar {
    gap: 5px;
    padding: 6px 8px;
    margin-top: 3px;
  }

  .checkout-total-a-pagar__label {
    gap: 1px;
  }

  .checkout-total-a-pagar__forma {
    font-size: 10px;
  }

  .checkout-total-a-pagar__edit {
    gap: 5px;
  }

  .pdv-adicionar-pagamento-btn {
    padding: 9px 12px;
    font-size: 12px;
  }

  .pdv-pagamentos-adicionados {
    margin-top: 6px;
  }

  .pdv-pagamentos-adicionados__titulo {
    margin-bottom: 3px;
  }

  .pdv-pagamento-item {
    padding: 4px 7px;
    margin-bottom: 2px;
  }

  .pdv-resumo-pag {
    margin-top: 5px;
    gap: 5px;
    padding-top: 6px;
  }

  .pdv-resumo-pag__item {
    padding: 5px 7px;
  }

  .checkout-actions-footer {
    gap: 4px;
    padding: 6px 8px;
  }

  .checkout-action-btn {
    padding: 6px 5px;
    font-size: 0.76rem;
  }

  .checkout-action-btn__atalho {
    display: none;
  }
}

@media (max-width: 1200px) {
  .nova-venda__ajustes-grid {
    grid-template-columns: 1fr;
  }
}

/* ── Caixa Toolbar ─────────────────────────────────────────────────── */
.caixa-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 0.85rem;
}
.caixa-toolbar__status {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.caixa-toolbar__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.caixa-toolbar__badge--aberto {
  background: #d1fae5;
  color: #047857;
}
.caixa-toolbar__badge--fechado {
  background: #fee2e2;
  color: #b91c1c;
}
.caixa-toolbar__info {
  color: #4b5563;
  display: inline-flex;
  gap: 10px;
  flex-wrap: wrap;
}
.caixa-toolbar__info strong {
  color: #111827;
}
.caixa-toolbar__actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.caixa-toolbar__btn {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 0.15s;
}
.caixa-toolbar__btn:hover { background: #f3f4f6; }
.caixa-toolbar__btn--success {
  background: #16a34a;
  color: #fff;
  border-color: #16a34a;
}
.caixa-toolbar__btn--success:hover { background: #15803d; }
.caixa-toolbar__btn--danger {
  background: #dc2626;
  color: #fff;
  border-color: #dc2626;
}
.caixa-toolbar__btn--danger:hover { background: #b91c1c; }
</style>
